/**
 * RESTORATIVE ALIGNMENT PILLOW - Enhanced PDP Landing Page
 * 
 * UI Refinements:
 * - Pillow images fill available space
 * - Redesigned pillowcase bonus as adjacent glued box
 * - Emotional testimonials with human touches
 * - New "cut the middlemen" savings section with comparison bar graphs
 */

import { useState, useEffect, useRef, useMemo } from "react";
import { usePricing } from "@/contexts/PricingContext";
import { 
  Star, Check, Moon, Thermometer, Shield, MapPin, ChevronDown, ChevronUp, BadgeCheck,
  Clock, Sparkles, Droplets, Wind, RefreshCw, Layers, Gift, Store, Truck, Tag, X,
  ChevronLeft, ChevronRight, Heart, Bed, Key, Package, Zap, Lock, Cpu, Wifi, Maximize2
} from "lucide-react";
import RyderLogo from "@/components/RyderLogo";
import PressLogos from "@/components/PressLogos";
import ComparisonTable from "@/components/ComparisonTable";
import TapSafeSection from "@/components/TapSafeSection";
// import TapSafeExplainerSection from "@/components/TapSafeExplainerSection"; // Removed - redundant with Recovery Scenarios
import NeverLockedInSection from "@/components/NeverLockedInSection";
import IntroVideoSection from "@/components/IntroVideoSection";
import BuiltForSecuritySection from "@/components/BuiltForSecuritySection";
import OrderPopup from "@/components/OrderPopup";
import { StockInventoryBar } from "@/components/StockInventoryBar";
import { EventClosedPage } from "@/components/EventClosedPage";
import ProfileResultsBanner from '@/components/ProfileResultsBanner';
import { QuizMiniSection } from '@/components/QuizMiniSection';
import { QuizModal } from '@/components/QuizModal';
import { trackViewContent, trackAddToCart, getProductDataForTracking } from "@/lib/tracking";
import { trackViewItem, trackAddToCart as trackGA4AddToCart, createMainProductItem } from "@/lib/ga4-tracking";
import { CustomerCount } from "@/components/CustomerCount";
import { ShippingMessage } from "@/components/ShippingMessage";
import { useGeolocation, isUSVisitor, useNonUsBadgeText, useNonUsAlternativeUsp } from "@/hooks/useGeolocation";
import { AnimatedPlanet } from "@/components/AnimatedPlanet";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useDefaultCta } from "@/hooks/useDefaultCta";
import { trpc } from "@/lib/trpc";
import { BlurImage } from "@/components/BlurImage";
import { ImageCarousel, type CarouselImage } from "@/components/ImageCarousel";
import { LazyVideo } from "@/components/LazyVideo";
import ExpertPillowSlide, { ExpertPillowThumbnail } from "@/components/ExpertPillowSlide";
import CoupleSleepSlide, { CoupleSleepThumbnail } from "@/components/CoupleSleepSlide";
import DeliverySlide, { DeliveryThumbnail } from "@/components/DeliverySlide";
import SleepCompanionSlide, { SleepCompanionThumbnail } from "@/components/SleepCompanionSlide";
import EthicalMakingSlide, { EthicalMakingThumbnail } from "@/components/EthicalMakingSlide";

import { BlackFridayUrgencySection } from "@/components/BlackFridayUrgencySection";
import { BundleShowcase } from "@/components/product/BundleShowcase";
import { BtcBundleShowcase } from "@/components/product/BtcBundleShowcase";
import BtcComparisonTable from "@/components/BtcComparisonTable";
import { BtcOfferSection } from "@/components/BtcOfferSection";
import LovedByThousands from "@/components/LovedByThousands";
// MiniTagsSlide removed - Tags slide no longer in gallery

/**
 * Helper function to convert PNG path to WebP with fallback
 * Returns WebP path if browser supports it, otherwise PNG
 */
const supportsWebP = typeof window !== 'undefined' && 
  document.createElement('canvas').toDataURL('image/webp').indexOf('data:image/webp') === 0;

const toWebP = (pngPath: string): string => {
  if (!supportsWebP) return pngPath;
  // Only convert optimized PNG images
  if (!pngPath.includes('/optimized/') || !pngPath.endsWith('.png')) return pngPath;
  return pngPath.replace('.png', '.webp');
};


/**
 * Comparison Slide Component - Fourth gallery image
 * Recreates the "Ruined Sleep vs VIP Slumber" comparison using page design system
 * Adapted for "Restorative Alignment" narrative targeting 45+ audience
 */
function ComparisonSlide() {
  const negativePoints = [
    "Seed phrases on paper or metal",
    "Complex setup taking hours",
    "No recovery if device is lost",
    "Clunky interfaces and tiny screens",
    "Single point of failure"
  ];
  
  const positivePoints = [
    "Optional seed phrase — TapSafe handles it for you",
    "60-second setup with your phone",
    "TapSafe recovery with NFC tags",
    "1.6\" OLED display with Qi charging",
    "EAL6+ certified security chip"
  ];

  return (
    <div className="w-full h-full flex overflow-hidden relative">
      {/* Left Side - Traditional Wallets - Grey background to stand out */}
      <div className="w-1/2 h-full bg-[#ffffff] flex flex-col relative">
        <div className="p-4 lg:p-6 flex flex-col h-full">
          <h3 className="text-lg lg:text-xl font-semibold text-[#1a1a1a] mb-2">Traditional Wallets</h3>
          <p className="text-xs lg:text-sm text-muted-foreground mb-4 lg:mb-6">The Old Way</p>
          
          {/* Negative Points */}
          <div className="space-y-2.5 lg:space-y-3">
            {negativePoints.map((point, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <X className="w-4 h-4 text-[#6a63f5] flex-shrink-0 mt-0.5" />
                <span className="text-xs lg:text-sm text-[#666]">{point}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* VS Divider */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
        <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-[#f5c542] flex items-center justify-center shadow-lg">
          <span className="text-xs lg:text-sm font-bold text-[#1a1a1a]">VS</span>
        </div>
      </div>
      
      {/* Right Side - Ryder One */}
      <div className="w-1/2 h-full bg-[#171717] flex flex-col relative">
        <div className="p-4 lg:p-6 flex flex-col h-full">
          <h3 className="text-lg lg:text-xl font-semibold text-white mb-2">Ryder One</h3>
          <p className="text-xs lg:text-sm text-white/70 mb-4 lg:mb-6">The Ryder Way</p>
          
          {/* Positive Points */}
          <div className="space-y-2.5 lg:space-y-3">
            {positivePoints.map((point, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <Check className="w-4 h-4 text-[#22c55e] flex-shrink-0 mt-0.5" />
                <span className="text-xs lg:text-sm text-white/90">{point}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Hotel Comparison Slide Component - Compares FluffCo vs luxury hotel brands
 * Shows price, savings, and feature comparison table
 * Logos rotate with fade in/out animation across all columns
 */
function HotelComparisonSlide({ fluffPrice = "$59" }: { fluffPrice?: string }) {
  // All hotel logos for rotation - unified semi-transparent styling
  const hotelLogos = useMemo(() => [
    { src: "/images/four-seasons-logo.svg", alt: "Four Seasons" },
    { src: "/images/ritz-carlton-logo.svg", alt: "Ritz-Carlton" },
    { src: "/images/optimized/marriott-logo-small.webp", alt: "Marriott" },
    { src: "/images/mandarin-oriental.svg", alt: "Mandarin Oriental" },
    { src: "/images/aman-logo.svg", alt: "Aman" },
    { src: "/images/rosewood-logo.svg", alt: "Rosewood" },
    { src: "/images/fairmont-logo.svg", alt: "Fairmont" },
    { src: "/images/hyatt-logo.svg", alt: "Hyatt" },
  ], []);

  // Preload all images before starting animation
  const [imagesLoaded, setImagesLoaded] = useState(false);
  
  useEffect(() => {
    let mounted = true;
    const loadImages = async () => {
      const promises = hotelLogos.map((logo: { src: string; alt: string }) => {
        return new Promise<void>((resolve) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => resolve(); // Still resolve on error to not block
          img.src = logo.src;
        });
      });
      await Promise.all(promises);
      if (mounted) setImagesLoaded(true);
    };
    loadImages();
    return () => { mounted = false; };
  }, [hotelLogos]);

  // State for current logos in each column (indices into hotelLogos array)
  const [columnLogos, setColumnLogos] = useState([0, 1, 2]);
  const [fadeStates, setFadeStates] = useState([1, 1, 1]); // 1 = visible, 0 = hidden
  
  // Rotate logos with random desync timing for each column - only after images loaded
  useEffect(() => {
    if (!imagesLoaded) return;
    
    const visibleTime = 5000; // Time logo stays fully visible (5s - longer)
    const fadeTime = 2000; // Very slow fade transition (2s)
    const totalCycleTime = visibleTime + fadeTime * 2; // Full cycle: fade in + visible + fade out
    
    const rotateColumn = (columnIndex: number) => {
      // Start fade out (very slow)
      setFadeStates(prev => {
        const newStates = [...prev];
        newStates[columnIndex] = 0;
        return newStates;
      });
      
      // After fade out completes, change logo and fade in
      setTimeout(() => {
        setColumnLogos(prev => {
          const newLogos = [...prev];
          // Get next logo that's not currently shown in any column
          let nextIndex = (newLogos[columnIndex] + 3) % hotelLogos.length;
          while (newLogos.includes(nextIndex)) {
            nextIndex = (nextIndex + 1) % hotelLogos.length;
          }
          newLogos[columnIndex] = nextIndex;
          return newLogos;
        });
        
        // Fade in (very slow) - small delay to ensure logo changed
        setTimeout(() => {
          setFadeStates(prev => {
            const newStates = [...prev];
            newStates[columnIndex] = 1;
            return newStates;
          });
        }, 50);
      }, fadeTime);
    };
    
    // Create intervals with random offsets for desync effect
    const createRandomInterval = (colIdx: number) => {
      const randomOffset = Math.random() * 3000 + 1000; // Random 1-4s offset for initial desync
      const randomVariation = Math.random() * 1500 - 750; // +/- 750ms variation
      const interval = totalCycleTime + randomVariation;
      
      // Initial delay with random offset
      const initialTimeout = setTimeout(() => {
        rotateColumn(colIdx);
        // Then set up recurring interval with slight randomness
        const recurringInterval = setInterval(() => {
          rotateColumn(colIdx);
        }, interval + (Math.random() * 1000 - 500));
        intervalRefs.current[colIdx] = recurringInterval;
      }, randomOffset);
      
      return initialTimeout;
    };
    
    const intervalRefs = { current: [null, null, null] as (ReturnType<typeof setInterval> | null)[] };
    const timeouts = [0, 1, 2].map(createRandomInterval);
    
    return () => {
      timeouts.forEach(t => clearTimeout(t));
      intervalRefs.current.forEach(i => i && clearInterval(i));
    };
  }, [imagesLoaded, hotelLogos]);

  const features = [
    { name: "Price", fluff: fluffPrice, hotel: "$130-200" },
    { name: "Savings", fluff: null, hotel: "55-70%" },
    { name: "Free Trial", fluff: true, hotel: false },
    { name: "Sustainable", fluff: true, hotel: false },
    { name: "Cruelty-Free", fluff: true, hotel: false },
  ];

  return (
    <div className="w-full h-full bg-[#171717] flex flex-col p-2 lg:p-5 relative overflow-hidden">
      {/* Moon icon top left */}
      <div className="absolute top-2 left-2 lg:top-3 lg:left-3">
        <div className="relative">
          <Moon className="w-5 h-5 lg:w-6 lg:h-6 text-white/80" />
          <Sparkles className="w-2 h-2 lg:w-2.5 lg:h-2.5 text-[#6a63f5] absolute -top-0.5 -right-0.5" />
        </div>
      </div>
      
      {/* Grid-based comparison table with unified FluffCo column */}
      <div className="flex-1 flex mt-6 lg:mt-8 relative min-w-0">
        {/* Feature labels column */}
        <div className="w-16 lg:w-24 flex flex-col pt-14 lg:pt-20 flex-shrink-0">
          {features.map((feature, idx) => (
            <div 
              key={idx} 
              className="flex-1 flex items-center text-[9px] lg:text-[11px] font-semibold text-white"
              style={{ animation: `fadeSlideIn 0.4s ease-out ${0.5 + idx * 0.1}s both` }}
            >
              {feature.name}
            </div>
          ))}
        </div>
        
        {/* FluffCo Column - Unified white background */}
        <div className="flex-1 flex flex-col bg-white rounded-lg lg:rounded-xl mx-1 lg:mx-2 shadow-xl shadow-white/30 animate-[fadeSlideIn_0.4s_ease-out_0.1s_both] relative z-10 min-w-0">
          <div className="h-14 lg:h-20 flex items-center justify-center bg-white rounded-t-lg lg:rounded-t-xl px-2 lg:px-4">
            <RyderLogo className="h-8 lg:h-10 w-auto" variant="dark" />
          </div>
          {features.map((feature, idx) => (
            <div key={idx} className="flex-1 flex items-center justify-center">
              {feature.fluff === true ? (
                <div className="w-5 h-5 lg:w-6 lg:h-6 rounded-full bg-[#171717] flex items-center justify-center">
                  <Check className="w-3 h-3 lg:w-3.5 lg:h-3.5 text-white" />
                </div>
              ) : feature.fluff === null ? (
                <span className="text-[10px] lg:text-xs text-[#171717]/50">-</span>
              ) : (
                <span className="text-xs lg:text-sm font-bold text-[#171717]">{feature.fluff}</span>
              )}
            </div>
          ))}
        </div>
        
        {/* Hotel Columns with rotating logos */}
        {[0, 1, 2].map((colIdx) => (
          <div key={colIdx} className="flex-1 flex flex-col mx-0.5 animate-[fadeSlideIn_0.4s_ease-out_0.2s_both] min-w-0">
            {/* Logo Header with fade animation - unified small size and semi-transparent */}
            <div className="h-14 lg:h-20 flex items-end justify-center pb-2">
              <img 
                src={hotelLogos[columnLogos[colIdx]].src}
                alt={hotelLogos[columnLogos[colIdx]].alt}
                className="h-4 lg:h-6 w-auto object-contain max-w-[90%] brightness-0 invert transition-opacity duration-[2000ms] ease-in-out"
                style={{ opacity: fadeStates[colIdx] * 0.6 }}
              />
            </div>
            {/* Values */}
            {features.map((feature, idx) => (
              <div key={idx} className="flex-1 flex items-center justify-center">
                {feature.hotel === false ? (
                  <div className="w-4 h-4 lg:w-5 lg:h-5 rounded-full bg-[#6a63f5] flex items-center justify-center">
                    <X className="w-2.5 h-2.5 lg:w-3 lg:h-3 text-white" />
                  </div>
                ) : (
                  <span className="text-[9px] lg:text-[11px] text-white/70">{feature.hotel}</span>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Hotel Comparison Thumbnail - Shows mini table preview
 */
function HotelComparisonThumbnail() {
  return (
    <div className="w-full h-full bg-[#171717] flex flex-col items-center justify-center p-1">
      <div className="bg-white rounded px-1.5 py-0.5 mb-1">
        <span className="text-[6px] font-bold text-[#171717]">FLUFF<span className="text-[#d4a5a5]">Co</span></span>
      </div>
      <div className="flex gap-1">
        <div className="w-2 h-2 rounded-full bg-white/30" />
        <div className="w-2 h-2 rounded-full bg-white/30" />
        <div className="w-2 h-2 rounded-full bg-white/30" />
      </div>
      <div className="text-[5px] text-white/70 mt-1">vs hotels</div>
    </div>
  );
}

/**
 * Comparison Thumbnail - Shows mini version of comparison slide with X vs Check icons
 */
function ComparisonThumbnail() {
  return (
    <div className="w-full h-full flex relative">
      {/* Left side - grey with X */}
      <div className="w-1/2 h-full bg-[#ffffff] flex items-center justify-center">
        <X className="w-4 h-4 text-[#6a63f5]" />
      </div>
      {/* Right side - navy with check */}
      <div className="w-1/2 h-full bg-[#171717] flex items-center justify-center">
        <Check className="w-4 h-4 text-[#22c55e]" />
      </div>
      {/* VS badge in center */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-[#f5c542] flex items-center justify-center shadow-sm">
        <span className="text-[5px] font-bold text-[#1a1a1a]">VS</span>
      </div>
    </div>
  );
}

/**
 * Second Image Thumbnail - Shows preview with subtle overlay indicator
 */
function SecondImageThumbnail() {
  return (
    <div className="w-full h-full relative">
      <img 
        src={toWebP("/images/optimized/hero/lifestyle-sleep.png")} 
        alt="" 
        className="w-full h-full object-cover"
      />
      {/* Subtle corner indicator showing this image has overlays */}
      <div className="absolute bottom-0 right-0 bg-white/90 rounded-tl-lg px-1.5 py-1">
        <span className="text-[7px] font-semibold text-[#171717]">why</span>
      </div>
    </div>
  );
}

/**
 * Third Image Thumbnail - Shows preview with "3 layers" indicator (same style as 2nd)
 */
function ThirdImageThumbnail() {
  return (
    <div className="w-full h-full relative">
      <img 
        src={toWebP("/images/optimized/benefits/engineering-detail-new.png")} 
        alt="" 
        className="w-full h-full object-cover"
      />
      {/* Subtle corner indicator showing this image has overlays - same style as 2nd */}
      <div className="absolute bottom-0 right-0 bg-white/90 rounded-tl-lg px-1.5 py-1">
        <span className="text-[7px] font-semibold text-[#171717]">3 layers</span>
      </div>
    </div>
  );
}

/**
 * First Image Thumbnail - Shows "5-star" indicator
 */
function FirstImageThumbnail() {
  return (
    <div className="w-full h-full relative">
      <img 
        src={toWebP("/images/optimized/cards/pillow-hero.png")} 
        alt="" 
        className="w-full h-full object-cover"
      />
      <div className="absolute bottom-0 right-0 bg-white/90 rounded-tl-lg px-1.5 py-1">
        <span className="text-[7px] font-semibold text-[#171717]">5-star</span>
      </div>
    </div>
  );
}

/**
 * Fifth Image Thumbnail - Shows "3 care" indicator (washing machine)
 */
function FifthImageThumbnail() {
  return (
    <div className="w-full h-full relative">
      <img 
        src={toWebP("/images/optimized/benefits/washing-machine.png")} 
        alt="" 
        className="w-full h-full object-cover"
      />
      <div className="absolute bottom-0 right-0 bg-white/90 rounded-tl-lg px-1.5 py-1">
        <span className="text-[7px] font-semibold text-[#171717]">care</span>
      </div>
    </div>
  );
}

/**
 * Sixth Image Thumbnail - Shows "hotel" indicator (packaging)
 */
function SixthImageThumbnail() {
  return (
    <div className="w-full h-full relative">
      <img 
        src={toWebP("/images/optimized/benefits/hotel-comfort-packaging.png")} 
        alt="" 
        className="w-full h-full object-cover"
      />
      <div className="absolute bottom-0 right-0 bg-white/90 rounded-tl-lg px-1.5 py-1">
        <span className="text-[7px] font-semibold text-[#171717]">hotel</span>
      </div>
    </div>
  );
}

import type { LandingPageConfig } from '@/config/types';
import { useLandingPageConfig } from '@/contexts/LandingPageContext';
import { baseConfig } from '@/config/base.config';

interface HomeProps {
  configOverride?: LandingPageConfig;
  // Allow any additional props from wouter routing
  [key: string]: any;
}

export default function Home(props: HomeProps) {
  const { configOverride } = props || {};
  
  // Get config from context or use override/default
  const contextConfig = useLandingPageConfig();
  const config = configOverride || contextConfig || baseConfig;
  
  // Pricing context (supports A/B test scenarios)
  const pricing = usePricing();
  
  // DEBUG: Log config values
  console.log('[DEBUG] Hero headline:', config.hero.headline);
  console.log('[DEBUG] Benefits title:', config.benefits.sectionTitle);
  console.log('[DEBUG] Pricing scenario:', pricing.scenarioId, '- sale price:', pricing.salePrice);
  
  // Get site settings from database
  const { settings: siteSettings, getAngleCTA } = useSiteSettings();
  
  // Get default CTA button from database
  const { cta: defaultCta } = useDefaultCta();
  
  // DISABLED: Database queries - using static config only
  // const { data: landingPageSettings } = trpc.landingPages.get.useQuery({ pageId: config.id });
  const landingPageSettings: { ctaButtonId?: number } | null = null;
  console.log('[DEBUG] landingPageSettings for', config.id, ':', JSON.stringify(landingPageSettings, null, 2));
  
  // DISABLED: Database queries - using static config only
  // const { data: allCtaButtons } = trpc.ctaButtons.getAll.useQuery();
  const allCtaButtons: Array<{ id: number; text: string; secondaryText?: string }> | null = null;
  
  // Helper function to get CTA button group with priority logic:
  // Returns object with primary and secondary text
  function getPageCTAGroup(): { primary: string; secondary: string } {
    console.log('[DEBUG getPageCTAGroup] landingPageSettings FULL:', JSON.stringify(landingPageSettings, null, 2));
    console.log('[DEBUG getPageCTAGroup] landingPageSettings.ctaButtonId:', landingPageSettings?.ctaButtonId);
    console.log('[DEBUG getPageCTAGroup] allCtaButtons:', allCtaButtons);
    // Priority 1: Selected CTA button for this page
    if (landingPageSettings?.ctaButtonId && allCtaButtons) {
      console.log('[DEBUG] Looking for button with ID:', landingPageSettings.ctaButtonId);
      console.log('[DEBUG] Available button IDs:', allCtaButtons.map((btn: any) => btn.id));
      const selectedButton = allCtaButtons.find((btn: any) => btn.id === landingPageSettings.ctaButtonId);
      console.log('[DEBUG] selectedButton found:', selectedButton);
      if (selectedButton) {
        return {
          primary: selectedButton.text || "Order Now",
          secondary: selectedButton.secondaryText || "& Save {discount}%"
        };
      }
    }
    
    // Priority 2: Default CTA button
    if (defaultCta.text) {
      return {
        primary: defaultCta.text,
        secondary: (defaultCta as any).secondaryText || "& Save {discount}%"
      };
    }
    
    // Priority 3: Hardcoded fallback
    if (isBtcOffer) {
      return {
        primary: `Get Ryder One — $${pricing.salePrice}`,
        secondary: "+ $200 BTC Cashback"
      };
    }
    return {
      primary: `Order Now - ${pricing.discountPercent}% Off`,
      secondary: "& Save {discount}%"
    };
  };
  
  // Helper to get simple primary CTA text (backward compatibility)
  const getPageCTA = (isMobile: boolean = false): string => {
    const primaryText = getPageCTAGroup().primary;
    
    // Use shorter text on mobile to prevent overflow
    if (isMobile && primaryText === "End Your Pillow Battle") {
      return "Claim Deal";
    }
    
    return primaryText;
  };
  
  // Helper to get CTA with discount (uses secondary text template)
  const getPageCTAWithDiscount = (discount: number): string => {
    const group = getPageCTAGroup();
    
    // If primary text already contains a percentage or "Off", it's a complete CTA - don't append secondary
    if (group.primary.includes('%') || group.primary.toLowerCase().includes('off')) {
      return group.primary;
    }
    
    // For Ryder One, show device discount
    if (discount > 0) {
      return `${group.primary} - ${discount}% Off`;
    }
    
    // If secondary text has {discount} placeholder, replace it
    if (group.secondary.includes('{discount}')) {
      return `${group.primary} ${group.secondary.replace('{discount}', discount.toString())}`;
    }
    // Otherwise just append the secondary text
    return `${group.primary} ${group.secondary}`;
  };
  
  const [selectedSize, setSelectedSize] = useState<"Standard" | "King">("Standard");
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [hoveredQuantity, setHoveredQuantity] = useState<number | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [expandedAccordion, setExpandedAccordion] = useState<string | null>(null);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [viewerCount, setViewerCount] = useState(47);
  const [displayedViewerCount, setDisplayedViewerCount] = useState(47);
  const [isViewerCountAnimating, setIsViewerCountAnimating] = useState(false);
  const [showAllTestimonials, setShowAllTestimonials] = useState(false);
  const [showAllBenefitCards, setShowAllBenefitCards] = useState(false);
  const [pricingSectionSize, setPricingSectionSize] = useState<"Standard" | "King">("Standard");
  const [savingsQuantity, setSavingsQuantity] = useState<1 | 2 | 4>(4);
  const [isPricingSectionAnimating, setIsPricingSectionAnimating] = useState(false);
  const [prevPricingSectionSize, setPrevPricingSectionSize] = useState<"Standard" | "King">("Standard");
  const [expertGalleryIndex, setExpertGalleryIndex] = useState(0);
  const [expandedExpertAccordion, setExpandedExpertAccordion] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(true); // Track if current gallery image is loaded
  const [preloadedImages, setPreloadedImages] = useState<Set<string>>(new Set());
  
  // Haptic feedback utility for mobile devices
  const triggerHapticFeedback = () => {
    if ('vibrate' in navigator) {
      // Light tap feedback (10ms)
      navigator.vibrate(10);
    }
  };
  
  // Gallery carousel ref for programmatic scrolling
  const galleryScrollRef = useRef<HTMLDivElement | null>(null);
  const [awardIndex, setAwardIndex] = useState(0); // Award carousel index
  const [currentToast, setCurrentToast] = useState<number | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [showHeader, setShowHeader] = useState(false);
  const [isQuizModalOpen, setIsQuizModalOpen] = useState(false);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [showWhatsIncluded, setShowWhatsIncluded] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [stickyBarExpanded, setStickyBarExpanded] = useState(false);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [purchasedToday, setPurchasedToday] = useState(0);
  const [displayedPurchasedToday, setDisplayedPurchasedToday] = useState(0);
  const [isPurchasedAnimating, setIsPurchasedAnimating] = useState(false);
  const [isHeroPriceAnimating, setIsHeroPriceAnimating] = useState(false);
  const [prevPrice, setPrevPrice] = useState<number | null>(null);
  const [stockLevel, setStockLevel] = useState(13); // Starts at 13%, decreases 1% per minute, min 7%
  const [showOrderPopup, setShowOrderPopup] = useState(false);
  const [oekoTexExpanded, setOekoTexExpanded] = useState(false);
  const oekoTexRef = useRef<HTMLButtonElement>(null);
  
  // Geo-detection for conditional shipping display
  const { countryCode } = useGeolocation();
  const isUS = countryCode ? isUSVisitor(countryCode) : true; // Default to US if unknown
  const nonUsBadgeText = useNonUsBadgeText(); // Configurable badge for non-US visitors
  const nonUsAlternativeUsp = useNonUsAlternativeUsp(); // Replacement for "Made in USA" for non-US visitors
  
  // FAQ micro-modal state
  const [showFaqMicroModal, setShowFaqMicroModal] = useState(false);
  const [faqMicroModalDismissed, setFaqMicroModalDismissed] = useState(false);
  
  // Checkout exit recovery modal state
  const [showExitRecoveryModal, setShowExitRecoveryModal] = useState(false);
  const [exitRecoveryShownThisSession, setExitRecoveryShownThisSession] = useState(false);
  const [exitRecoveryEmail, setExitRecoveryEmail] = useState('');
  const [exitRecoverySubmitted, setExitRecoverySubmitted] = useState(false);
  
  // Trust badges gradient visibility state
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(true); // Default to true as design element
  const [showTokenList, setShowTokenList] = useState(false);
  const trustBadgesRef = useRef<HTMLDivElement | null>(null);
  
  // Contextual carousel state for quiz results
  const [contextualCarouselIndex, setContextualCarouselIndex] = useState(0);
  const [contextualCardsCount, setContextualCardsCount] = useState(0);
  
  // Trust badges scroll detection for gradient visibility
  useEffect(() => {
    const container = trustBadgesRef.current;
    if (!container) return;
    
    const handleScroll = () => {
      const scrollLeft = container.scrollLeft;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;
      const isMobile = window.innerWidth < 768;
      
      // Check if content actually overflows
      const hasOverflow = scrollWidth > clientWidth;
      
      // Show left gradient if scrolled away from start (only when there's overflow)
      setShowLeftGradient(hasOverflow && scrollLeft > 5);
      
      // Show right gradient:
      // - On mobile: always show as design element (visual scroll indicator)
      // - On desktop: only show if there's overflow and not scrolled to end
      if (isMobile) {
        // On mobile, always show right gradient unless scrolled to end
        setShowRightGradient(hasOverflow ? scrollLeft < scrollWidth - clientWidth - 5 : true);
      } else {
        // On desktop, only show if there's actual overflow
        setShowRightGradient(hasOverflow && scrollLeft < scrollWidth - clientWidth - 5);
      }
    };
    
    // Initial check
    handleScroll();
    
    // Add scroll listener
    container.addEventListener('scroll', handleScroll);
    
    // Add resize listener to re-check on viewport changes
    window.addEventListener('resize', handleScroll);
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);
  
  // Gallery pills gradient visibility - initial check on mount
  useEffect(() => {
    const checkGalleryGradients = () => {
      const container = document.querySelector('.flex.gap-2.overflow-x-auto.pb-2.scrollbar-hide');
      const leftGradient = document.getElementById('thumb-gradient-left');
      const rightGradient = document.getElementById('thumb-gradient-right');
      
      if (!container || !leftGradient || !rightGradient) return;
      
      const hasOverflow = container.scrollWidth > container.clientWidth;
      
      if (!hasOverflow) {
        // No overflow - hide both gradients
        leftGradient.style.opacity = '0';
        rightGradient.style.opacity = '0';
      } else {
        // Has overflow - show right gradient initially (at scroll start)
        leftGradient.style.opacity = '0';
        rightGradient.style.opacity = '1';
      }
    };
    
    // Check after a short delay to ensure DOM is ready
    setTimeout(checkGalleryGradients, 100);
    
    // Also check on window resize
    window.addEventListener('resize', checkGalleryGradients);
    
    return () => {
      window.removeEventListener('resize', checkGalleryGradients);
    };
  }, []);
  
  // Swipe/drag state for carousel
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragCurrentX, setDragCurrentX] = useState(0);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  const wheelAccumulatorRef = useRef(0);
  const lastWheelTimeRef = useRef(0);
  const swipeCooldownRef = useRef(false);
  

  
  // Scroll to top when arriving from quiz (ensures page starts at top)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const fromQuiz = urlParams.get('from') === 'quiz';
    if (fromQuiz) {
      // Force scroll to top immediately
      window.scrollTo(0, 0);
      // Also set after a small delay to ensure it happens after any browser restoration
      setTimeout(() => window.scrollTo(0, 0), 0);
    }
  }, []);
  
  // Auto-switch contextual carousel every 6 seconds (one card at a time)
  useEffect(() => {
    if (contextualCardsCount <= 3) return; // Only auto-switch if more than 3 cards
    const interval = setInterval(() => {
      setContextualCarouselIndex((prev) => (prev + 1) % contextualCardsCount);
    }, 10000); // 10 seconds - much slower auto-switch
    return () => clearInterval(interval);
  }, [contextualCardsCount]);
  
  // Dynamic title and meta description
  useEffect(() => {
    const pageTitle = config.name || 'Ryder One - Hardware Crypto Wallet | Self-Custody Made Simple';
    const pageDescription = 'After years in crypto, we kept hearing the same thing: too complicated, too confusing, too intimidating. Ryder makes crypto simple, secure, and accessible for everyone.';
    
    document.title = pageTitle;
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', pageDescription);
    } else {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      metaDescription.setAttribute('content', pageDescription);
      document.head.appendChild(metaDescription);
    }
  }, [config]);
  

  
  // Click outside to close OEKO-TEX badge
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (oekoTexRef.current && !oekoTexRef.current.contains(event.target as Node)) {
        setOekoTexExpanded(false);
      }
    };
    if (oekoTexExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [oekoTexExpanded]);
  
  // BTC Offer page detection
  const isBtcOffer = config.id === 'btc-offer';
  
  // Event page IDs that have date-based availability
  const eventPageIds = ['valentine-gift', 'mothers-day', 'fathers-day', 'black-friday'];
  const isEventPage = eventPageIds.includes(config.id);
  
  // Event names for display
  const eventNames: Record<string, string> = {
    'valentine-gift': "Valentine's Day",
    'mothers-day': "Mother's Day",
    'fathers-day': "Father's Day",
    'black-friday': "Black Friday"
  };
  
  // Get selected marketing angle from localStorage (synced from LP Manager)
  const getSelectedAngle = (): string => {
    if (!isEventPage) return 'gift-focused';
    try {
      const stored = localStorage.getItem('skyvane_event_angles');
      if (stored) {
        const angles = JSON.parse(stored);
        return angles[config.id] || 'gift-focused';
      }
    } catch (e) {
      console.error('Error reading angle:', e);
    }
    // Default angles for event pages
    if (config.id === 'mothers-day') return 'neck-pain';
    if (config.id === 'black-friday') return 'black-friday';
    return 'gift-focused';
  };
  
  const selectedAngle = getSelectedAngle();
  
  // Angle-based content modifiers for event pages
  const angleModifiers: Record<string, {
    heroEmphasis?: string;
    benefitHighlight?: string;
    ctaText?: string;
    trustBadge?: string;
  }> = {
    '5-star-hotel': {
      heroEmphasis: 'Same pillows used in luxury hotels like Four Seasons and Ritz-Carlton.',
      benefitHighlight: 'hotel-quality',
      ctaText: 'Get Hotel-Quality Sleep',
      trustBadge: 'As seen in 5-star hotels'
    },
    'neck-pain': {
      heroEmphasis: 'Designed for proper neck alignment to help reduce morning stiffness and discomfort.',
      benefitHighlight: 'alignment',
      ctaText: 'Get Relief Tonight',
      trustBadge: 'Recommended for neck support'
    },
    'gift-focused': {
      heroEmphasis: 'The perfect gift that shows you care about their comfort and well-being.',
      benefitHighlight: 'gifting',
      ctaText: 'Give the Gift of Sleep',
      trustBadge: 'Premium gift packaging'
    },
    'restorative': {
      heroEmphasis: 'Wake up feeling truly refreshed with sleep that actually restores your body.',
      benefitHighlight: 'restoration',
      ctaText: 'Start Sleeping Better',
      trustBadge: 'For restorative rest'
    },
    'black-friday': {
      heroEmphasis: 'Limited time Black Friday pricing on hotel-quality pillows that hold their shape for years.',
      benefitHighlight: 'sale',
      ctaText: 'Claim Black Friday Deal',
      trustBadge: 'Up to 66% off'
    }
  };
  
  const currentAngleModifier = angleModifiers[selectedAngle] || angleModifiers['gift-focused'];
  
  // Event-specific content for the gift section
  const eventContent: Record<string, {
    headline: string;
    subheadline: string;
    description: string;
    image: string;
    features: { icon: 'gift' | 'check' | 'heart'; text: string }[];
    cards: { icon: 'moon' | 'gift' | 'sparkles'; title: string; description: string; image: string }[];
  }> = {
    'valentine-gift': {
      headline: "Better Than Flowers.",
      subheadline: "Softer Than Roses.",
      description: "Because rest is one of the most personal ways to show you care. Give a gift they'll enjoy every night, not just once.",
      image: '/images/optimized/events/valentine-unboxing.png',
      features: [
        { icon: 'gift', text: 'Premium Packaging' },
        { icon: 'check', text: 'No Wrapping Needed' },
        { icon: 'heart', text: '100-Night Guarantee' }
      ],
      cards: [
        { icon: 'moon', title: 'Enjoyed Every Night', description: "Unlike flowers that wilt or chocolates that disappear, this gift becomes part of their nightly routine.", image: '/images/optimized/events/valentine-couple-bed.png' },
        { icon: 'gift', title: 'Arrives Ready to Gift', description: "Premium packaging means no wrapping required. Just add a card and watch them unwrap something special.", image: '/images/optimized/events/valentine-unboxing.png' },
        { icon: 'sparkles', title: 'For Someone Special', description: "The perfect choice for the person who's hard to shop for. Everyone sleeps, and everyone deserves better sleep.", image: '/images/optimized/events/valentine-hero-pillow.png' }
      ]
    },
    'mothers-day': {
      headline: "More Than a Gift.",
      subheadline: "A Thank You She'll Feel Every Night.",
      description: "Mom spends every day taking care of others. This Mother's Day, give her the rest she truly deserves with hotel-quality comfort.",
      image: '/images/optimized/events/mothers-day-unboxing.png',
      features: [
        { icon: 'gift', text: 'Gift-Ready Packaging' },
        { icon: 'check', text: 'Neck & Back Support' },
        { icon: 'heart', text: '30-Day Money Back' }
      ],
      cards: [
        { icon: 'moon', title: 'Rest She Deserves', description: "After years of early mornings and late nights caring for everyone else, give mom the gift of truly restorative sleep.", image: '/images/optimized/events/mothers-day-comfort.png' },
        { icon: 'gift', title: 'Thoughtful & Practical', description: "Not another gift that sits in a drawer. This is something she'll use and appreciate every single night.", image: '/images/optimized/events/mothers-day-unboxing.png' },
        { icon: 'sparkles', title: 'Supports Her Comfort', description: "Designed for proper neck alignment, especially helpful for the aches and tension that come with years of caregiving.", image: '/images/optimized/events/mothers-day-hero.png' }
      ]
    },
    'fathers-day': {
      headline: "More Than a Gift.",
      subheadline: "Relief He'll Feel Every Morning.",
      description: "Dad never complains, but you know he wakes up stiff. This Father's Day, give him proper neck alignment and the rest he's earned.",
      image: '/images/optimized/events/fathers-day-unboxing-new.png',
      features: [
        { icon: 'gift', text: 'Gift-Ready Packaging' },
        { icon: 'check', text: 'Neck Pain Relief' },
        { icon: 'heart', text: '30-Day Money Back' }
      ],
      cards: [
        { icon: 'moon', title: 'Relief He Deserves', description: "After years of hard work and poor pillows, give dad the gift of waking up without the morning stiffness.", image: '/images/optimized/events/fathers-day-sleep-comfort.png' },
        { icon: 'gift', title: 'Practical & Appreciated', description: "Not another tie or gadget. This is something he'll use and appreciate every single night.", image: '/images/optimized/events/fathers-day-unboxing-new.png' },
        { icon: 'sparkles', title: 'Supports His Comfort', description: "Designed for proper neck alignment, especially helpful for the aches that come with years of hard work.", image: '/images/optimized/events/fathers-day-hero-pillow.png' }
      ]
    },
    'black-friday': {
      headline: "Black Friday Sale.",
      subheadline: "Premium Comfort at Unbeatable Prices.",
      description: "This Black Friday, experience hotel-quality sleep at prices that won't come around again. Limited time, limited stock.",
      image: '/images/optimized/events/black-friday-lifestyle-sleep-couple.png',
      features: [
        { icon: 'gift', text: 'Up to 66% Off' },
        { icon: 'check', text: 'Free Shipping' },
        { icon: 'heart', text: '30-Day Money Back' }
      ],
      cards: [
        { icon: 'moon', title: 'Hotel-Quality Sleep', description: "The same premium pillows used in 5-star hotels, now at Black Friday prices you won't see again.", image: '/images/optimized/events/black-friday-unboxing.png' },
        { icon: 'gift', title: 'Limited Time Only', description: "Once Black Friday ends, these prices disappear. Stock up now and upgrade every pillow in your home.", image: '/images/optimized/events/black-friday-packaging.png' },
        { icon: 'sparkles', title: 'Award-Winning Quality', description: "Industry-recognized security standards. Premium quality at sale prices.", image: '/images/optimized/events/black-friday-hero-pillow.png' }
      ]
    }
  };
  
  // California timezone (PST/PDT) utility
  const CALIFORNIA_TIMEZONE = 'America/Los_Angeles';
  
  // Get current time in California timezone
  const getCaliforniaTime = () => {
    return new Date(new Date().toLocaleString('en-US', { timeZone: CALIFORNIA_TIMEZONE }));
  };
  
  // Parse a date string and interpret it as California time
  const parseAsCaliforniaTime = (dateStr: string, endOfDay: boolean = false) => {
    const date = new Date(dateStr);
    // Create a date string in California timezone format
    const caDateStr = date.toLocaleDateString('en-US', { timeZone: CALIFORNIA_TIMEZONE });
    const caDate = new Date(caDateStr);
    if (endOfDay) {
      caDate.setHours(23, 59, 59, 999);
    } else {
      caDate.setHours(0, 0, 0, 0);
    }
    return caDate;
  };

  // Get event dates from localStorage (synced from LP Manager)
  // All dates are interpreted in California timezone
  const getEventDates = () => {
    if (!isEventPage) return null;
    try {
      const storedDates = localStorage.getItem('skyvane_event_dates');
      if (storedDates) {
        const dates = JSON.parse(storedDates);
        if (dates[config.id]) {
          const startDate = dates[config.id].startDate ? parseAsCaliforniaTime(dates[config.id].startDate, false) : null;
          const endDate = dates[config.id].endDate ? parseAsCaliforniaTime(dates[config.id].endDate, true) : null;
          return { startDate, endDate };
        }
      }
    } catch (e) {
      console.error('Error parsing event dates:', e);
    }
    // Default dates based on event type (California timezone)
    // For testing: all event pages accessible Jan 1 to May 15
    // In production, LP Manager will set actual dates
    const year = new Date().getFullYear();
    return {
      startDate: new Date(year, 0, 1, 0, 0, 0, 0), // Jan 1 (for testing)
      endDate: new Date(year, 4, 15, 23, 59, 59, 999) // May 15 (for testing)
    };
  };

  // Check event status (using California timezone)
  const getEventStatus = () => {
    const dates = getEventDates();
    if (!dates) return { status: 'active' as const, startDate: null, endDate: null };
    
    const now = getCaliforniaTime();
    const { startDate, endDate } = dates;
    
    if (startDate && now < startDate) {
      return { status: 'not-started' as const, startDate, endDate };
    }
    if (endDate && now > endDate) {
      return { status: 'ended' as const, startDate, endDate };
    }
    return { status: 'active' as const, startDate, endDate };
  };

  const eventStatus = getEventStatus();
  
  // Check for LP Manager auth (logged in users can access all event pages)
  const bypassActive = typeof window !== 'undefined' && sessionStorage.getItem('skyvane_lp_auth') === 'authenticated';

  // DISABLED: Database queries - using static config only
  // const { data: backendColorSchemes } = trpc.colorSchemes.getAll.useQuery();
  const backendColorSchemes = undefined as Array<{ slug: string; primaryColor: string; secondaryColor: string; accentColor: string; accentDarkColor: string; backgroundColor: string; secondaryLightColor: string; isDark: number }> | undefined;
  
  // Hardcoded fallback color schemes (always available immediately)
  const hardcodedColorSchemes: Record<string, { primary: string; secondary: string; accent: string; accentDark: string; background: string; textOnLight?: string; secondaryLight: string; isDark?: boolean }> = {
    'valentine-gift': {
      primary: '#6a63f5',
      secondary: '#5952e6',
      accent: '#fff5f5',
      accentDark: '#ffe8e8',
      background: '#fff8f8',
      secondaryLight: '#fce8eb'
    },
    'mothers-day': {
      primary: '#6a63f5',
      secondary: '#5952e6',
      accent: '#fff9f9',
      accentDark: '#f5e6e6',
      background: '#fffafa',
      textOnLight: '#5952e6',
      secondaryLight: '#f5eaea'
    },
    'fathers-day': {
      primary: '#6a63f5',
      secondary: '#5952e6',
      accent: '#f0f5ff',
      accentDark: '#e2e8f0',
      background: '#f8fafc',
      secondaryLight: '#e8eef8'
    },
    'black-friday': {
      primary: '#6a63f5',
      secondary: '#5952e6',
      accent: '#fff9f0',
      accentDark: '#ffe8cc',
      background: '#fffbf5',
      secondaryLight: '#fff3e0',
      isDark: false
    }
  };
  
  // Build event color schemes from backend data, with hardcoded fallback per event
  type ColorScheme = { primary: string; secondary: string; accent: string; accentDark: string; background: string; textOnLight?: string; secondaryLight: string; isDark?: boolean };
  const eventColorSchemes: Record<string, ColorScheme> = 
    backendColorSchemes?.reduce<Record<string, ColorScheme>>((acc, scheme) => {
      acc[scheme.slug] = {
        primary: scheme.primaryColor,
        secondary: scheme.secondaryColor,
        accent: scheme.accentColor,
        accentDark: scheme.accentDarkColor,
        background: scheme.backgroundColor,
        secondaryLight: scheme.secondaryLightColor,
        // textOnLight is optional, use secondaryColor as fallback
        textOnLight: scheme.secondaryColor,
        isDark: scheme.isDark === 1
      };
      return acc;
    }, { ...hardcodedColorSchemes } as Record<string, { primary: string; secondary: string; accent: string; accentDark: string; background: string; textOnLight?: string; secondaryLight: string; isDark?: boolean }>) || hardcodedColorSchemes;
  
  // Get colors for current event with proper fallback chain:
  // 1. Try backend color for this specific event
  // 2. Fall back to hardcoded color for this specific event
  // 3. Fall back to Valentine's as last resort
  const eventColors = isEventPage ? (eventColorSchemes[config.id] || hardcodedColorSchemes[config.id] || hardcodedColorSchemes['valentine-gift']) : null;
  
  // Determine if current color scheme is dark (for automatic text/border adaptation)
  const isDarkScheme = eventColors?.isDark === true;
  
  // Color helpers for dark/light scheme adaptation
  const accentColor = isDarkScheme && eventColors ? eventColors.secondary : (eventColors?.primary || '#6a63f5');
  const accentBg = isDarkScheme && eventColors ? eventColors.secondaryLight : (eventColors ? `${eventColors.primary}08` : 'rgba(230, 57, 70, 0.05)');
  // For dark schemes (Black Friday), use gold accent colors for borders
  // For light schemes (Valentine's, etc.), use primary red colors
  const borderColorSelected = isDarkScheme && eventColors ? '#FFD700' : (eventColors?.primary || '#6a63f5');
  const borderColorUnselected = isDarkScheme && eventColors ? 'rgba(255,215,0,0.3)' : (eventColors ? `${eventColors.primary}40` : 'var(--border)');
  
  // Helper function for closed page
  const getEventColorScheme = () => eventColors || eventColorSchemes['valentine-gift'];

  // If event is not active, show closed page
  if (isEventPage && eventStatus.status !== 'active' && !bypassActive) {
    return (
      <EventClosedPage
        eventName={eventNames[config.id] || 'Holiday'}
        eventId={config.id}
        status={eventStatus.status}
        startDate={eventStatus.startDate || undefined}
        colorScheme={getEventColorScheme()}
      />
    );
  }

  // Legacy function for timer compatibility
  const getEventEndDate = () => {
    const dates = getEventDates();
    return dates?.endDate || null;
  };

  // Check if current time is within the event's active period
  const isWithinEventPeriod = () => {
    if (!isEventPage) return false;
    const dates = getEventDates();
    if (!dates) return false;
    const now = getCaliforniaTime();
    const { startDate, endDate } = dates;
    // Must be after start date (if set) and before end date (if set)
    const afterStart = !startDate || now >= startDate;
    const beforeEnd = !endDate || now <= endDate;
    return afterStart && beforeEnd;
  };

  // Store the timer end time in a ref to persist across renders
  const timerEndTimeRef = useRef<number | null>(null);
  
  // Initialize timer end time from cookie on mount (client-side only)
  const initializeTimerEndTime = () => {
    if (timerEndTimeRef.current !== null) return; // Already initialized
    
    const COOKIE_NAME = 'fluff_timer_end';
    const DEFAULT_HOURS = 1;
    const DEFAULT_MINUTES = 27;
    const DEFAULT_SECONDS = 14;
    
    const now = Date.now();
    
    // Try to get existing timer end time from cookie
    const cookieValue = document.cookie
      .split('; ')
      .find(row => row.startsWith(COOKIE_NAME + '='));
    
    if (cookieValue) {
      const endTimeStr = cookieValue.split('=')[1];
      const parsedTime = parseInt(endTimeStr);
      if (!isNaN(parsedTime) && parsedTime > now) {
        timerEndTimeRef.current = parsedTime;
        return;
      }
    }
    
    // No valid cookie - create new timer
    timerEndTimeRef.current = now + (DEFAULT_HOURS * 3600 + DEFAULT_MINUTES * 60 + DEFAULT_SECONDS) * 1000;
    
    // Set cookie to expire in 30 days
    const expires = new Date(now + 30 * 24 * 60 * 60 * 1000);
    document.cookie = `${COOKIE_NAME}=${timerEndTimeRef.current}; expires=${expires.toUTCString()}; path=/`;
  };
  
  const calculateTimeRemaining = () => {
    const eventEndDate = getEventEndDate();
    const withinPeriod = isWithinEventPeriod();
    
    // Use legacy cookie-based timer if:
    // 1. Not an event page, OR
    // 2. Event page but outside the configured date range
    if (!eventEndDate || !withinPeriod) {
      // Initialize timer end time if not already done
      initializeTimerEndTime();
      
      const now = Date.now();
      const timerEndTime = timerEndTimeRef.current || now;
      
      // Check if timer has expired and needs reset
      if (timerEndTime <= now) {
        // Reset the timer
        const COOKIE_NAME = 'fluff_timer_end';
        const DEFAULT_HOURS = 1;
        const DEFAULT_MINUTES = 27;
        const DEFAULT_SECONDS = 14;
        
        timerEndTimeRef.current = now + (DEFAULT_HOURS * 3600 + DEFAULT_MINUTES * 60 + DEFAULT_SECONDS) * 1000;
        const expires = new Date(now + 30 * 24 * 60 * 60 * 1000);
        document.cookie = `${COOKIE_NAME}=${timerEndTimeRef.current}; expires=${expires.toUTCString()}; path=/`;
      }
      
      // Calculate remaining time
      const diff = (timerEndTimeRef.current || now) - now;
      const totalSeconds = Math.max(0, Math.floor(diff / 1000));
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      
      return { hours, minutes, seconds, isExpired: false };
    }
    
    // Use California timezone for consistent timer display
    const now = getCaliforniaTime();
    const diff = eventEndDate.getTime() - now.getTime();
    
    if (diff <= 0) {
      return { hours: 0, minutes: 0, seconds: 0, isExpired: true };
    }
    
    const totalSeconds = Math.floor(diff / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    // If more than 24 hours, show days in hours
    const displayHours = days * 24 + hours;
    
    return { hours: displayHours, minutes, seconds, isExpired: false };
  };

  const [countdown, setCountdown] = useState(calculateTimeRemaining);
  const [timerExpired, setTimerExpired] = useState(false);

  useEffect(() => {
    // Check if event page timer is expired
    const initial = calculateTimeRemaining();
    if (initial.isExpired) {
      setTimerExpired(true);
      return;
    }

    const timer = setInterval(() => {
      const timeRemaining = calculateTimeRemaining();
      
      if (timeRemaining.isExpired) {
        setTimerExpired(true);
        clearInterval(timer);
        return;
      }
      
      setCountdown(timeRemaining);
    }, 1000);
    
    return () => clearInterval(timer);
  }, [config.id]);

  useEffect(() => {
    const interval = setInterval(() => {
      setViewerCount(prev => {
        const change = Math.floor(Math.random() * 7) - 3;
        const newCount = prev + change;
        return Math.max(38, Math.min(72, newCount));
      });
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Animate viewer count when it changes
  useEffect(() => {
    if (viewerCount !== displayedViewerCount) {
      setIsViewerCountAnimating(true);
      const timeout = setTimeout(() => {
        setDisplayedViewerCount(viewerCount);
        setIsViewerCountAnimating(false);
      }, 150);
      return () => clearTimeout(timeout);
    }
  }, [viewerCount, displayedViewerCount]);

  // Calculate purchased today based on NYC timezone with non-linear growth curve
  const calculatePurchasedToday = () => {
    // Get current time in NYC timezone
    const now = new Date();
    const nycTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const hours = nycTime.getHours();
    const minutes = nycTime.getMinutes();
    const totalMinutes = hours * 60 + minutes;
    
    // Total range: 20 at midnight to 720 at 11:59PM (700 growth over 24h)
    const minPurchases = 20;
    const maxPurchases = 720;
    const totalGrowth = maxPurchases - minPurchases; // 700
    
    // Define growth weights for each hour (higher during peak times)
    // Peak 1: 9-11AM, Peak 2: 5-8PM
    const hourlyWeights = [
      0.5, 0.5, 0.5, 0.5, 0.5, 0.5,  // 12AM-6AM: slow
      1.0, 1.5, 2.0,                  // 6AM-9AM: ramping up
      4.0, 4.0, 3.0,                  // 9AM-12PM: first peak (9-11AM)
      2.0, 2.0, 2.0, 2.0, 2.0,        // 12PM-5PM: moderate
      4.0, 4.5, 4.5, 4.0,             // 5PM-9PM: second peak (5-8PM)
      2.0, 1.5, 1.0                   // 9PM-12AM: winding down
    ];
    
    // Calculate cumulative weight up to current time
    const totalWeight = hourlyWeights.reduce((a, b) => a + b, 0);
    let cumulativeWeight = 0;
    
    for (let h = 0; h < hours; h++) {
      cumulativeWeight += hourlyWeights[h];
    }
    // Add partial hour weight
    cumulativeWeight += hourlyWeights[hours] * (minutes / 60);
    
    // Calculate purchases based on weighted progress
    const progress = cumulativeWeight / totalWeight;
    const purchases = Math.floor(minPurchases + (totalGrowth * progress));
    
    return purchases;
  };

  // Initialize and update purchased today
  useEffect(() => {
    const updatePurchased = () => {
      const newValue = calculatePurchasedToday();
      setPurchasedToday(newValue);
    };
    
    updatePurchased();
    // Update every 2 minutes (less frequent than viewing counter)
    const interval = setInterval(updatePurchased, 120000);
    return () => clearInterval(interval);
  }, []);

  // Animate purchased today when it changes
  useEffect(() => {
    if (purchasedToday !== displayedPurchasedToday) {
      setIsPurchasedAnimating(true);
      const timeout = setTimeout(() => {
        setDisplayedPurchasedToday(purchasedToday);
        setIsPurchasedAnimating(false);
      }, 150);
      return () => clearTimeout(timeout);
    }
  }, [purchasedToday, displayedPurchasedToday]);

  // Stock level decreases 1% per minute, minimum 7%
  useEffect(() => {
    const timer = setInterval(() => {
      setStockLevel(prev => Math.max(7, prev - 1));
    }, 60000); // Every 60 seconds
    return () => clearInterval(timer);
  }, []);

  // Update document title and meta tags from config
  useEffect(() => {
    // Check if this is a landing page (has a config slug that's not the default)
    const isLandingPage = config.slug && config.slug !== 'default';
    
    // Update title - use "{title} – FluffCo" format for landing pages
    const pageTitle = config.seo?.title || config.hero?.headline || 'Ryder One';
    document.title = `${pageTitle} – Ryder`;
    
    // Add noindex/nofollow for landing pages to prevent SEO indexing
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (isLandingPage) {
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta');
        robotsMeta.setAttribute('name', 'robots');
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.setAttribute('content', 'noindex, nofollow');
    } else if (robotsMeta) {
      // Remove noindex for main page
      robotsMeta.remove();
    }
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', config.seo?.description || config.hero?.subheadline || '');
    
    // Update Open Graph title
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', `${pageTitle} – Ryder`);
    
    // Update Open Graph description
    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
    }
    ogDescription.setAttribute('content', config.seo?.description || config.hero?.subheadline || '');
    
    // Update Open Graph image (first gallery image)
    const firstImage = config.gallery?.images?.[0]?.src;
    if (firstImage) {
      let ogImage = document.querySelector('meta[property="og:image"]');
      if (!ogImage) {
        ogImage = document.createElement('meta');
        ogImage.setAttribute('property', 'og:image');
        document.head.appendChild(ogImage);
      }
      // Convert relative path to absolute URL
      const absoluteImageUrl = firstImage.startsWith('http') 
        ? firstImage 
        : `${window.location.origin}${firstImage}`;
      ogImage.setAttribute('content', absoluteImageUrl);
    }
    
    // Update Twitter Card tags
    let twitterCard = document.querySelector('meta[name="twitter:card"]');
    if (!twitterCard) {
      twitterCard = document.createElement('meta');
      twitterCard.setAttribute('name', 'twitter:card');
      document.head.appendChild(twitterCard);
    }
    twitterCard.setAttribute('content', 'summary_large_image');
  }, [config.seo, config.hero, config.gallery]);

  // Analytics: Track page view on load
  useEffect(() => {
    // Facebook Pixel: Track ViewContent for Ryder One
    const productData = getProductDataForTracking(pricing.scenarioId);
    trackViewContent(productData);
    
    // GA4: Track view_item for Ryder One
    const ga4Item = createMainProductItem(
      'standard',
      1,
      pricing.salePrice,
      pricing.originalPrice,
      pricing.scenarioId
    );
    trackViewItem(ga4Item, pricing.salePrice);
  }, []);

  // Scroll detection for sticky bar and sticky header
  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          const ctaButton = document.getElementById('main-cta-button');
          const footer = document.querySelector('footer');
          const announcementBar = document.querySelector('[data-announcement-bar]');
          const announcementHeight = announcementBar?.getBoundingClientRect().height || 44;
          
          // Sticky bar logic
          if (ctaButton) {
            const rect = ctaButton.getBoundingClientRect();
            const footerRect = footer?.getBoundingClientRect();
            const ctaOutOfView = rect.bottom < 0;
            const footerVisible = footerRect ? footerRect.top < window.innerHeight + 100 : false;
            setShowStickyBar(ctaOutOfView && !footerVisible);
          }
          
          // Sticky header logic - show when scrolling up, hide when scrolling down
          // Only activate after scrolling past the announcement bar + header height
          // Also account for ProfileResultsBanner if present (on quiz results page)
          const profileBanner = document.querySelector('[data-profile-banner]');
          const profileBannerHeight = profileBanner ? profileBanner.getBoundingClientRect().height : 0;
          const scrollThreshold = announcementHeight + 56 + (profileBanner ? profileBannerHeight + 100 : 0); // announcement + header + banner if present
          
          if (currentScrollY > scrollThreshold) {
            // Scrolling up - show header
            if (currentScrollY < lastScrollY) {
              setShowStickyHeader(true);
            } 
            // Scrolling down - hide header
            else if (currentScrollY > lastScrollY) {
              setShowStickyHeader(false);
            }
          } else {
            // Near top of page or within banner area - hide sticky header
            setShowStickyHeader(false);
          }
          
          // FAQ micro-modal logic - show when FAQ section is in view
          const faqSection = document.getElementById('faq');
          if (faqSection) {
            const faqRect = faqSection.getBoundingClientRect();
            const faqInView = faqRect.top < window.innerHeight * 0.7 && faqRect.bottom > 0;
            // Show modal when FAQ is in view, hide when scrolling back above it
            // Only if not manually dismissed
            if (faqInView && !faqMicroModalDismissed) {
              setShowFaqMicroModal(true);
            } else if (faqRect.top > window.innerHeight) {
              // User scrolled back up above FAQ - hide modal and reset dismissed state
              setShowFaqMicroModal(false);
            }
          }
          
          setLastScrollY(currentScrollY);
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, faqMicroModalDismissed]);

  // Award carousel auto-rotation (every 5 seconds for smoother experience)
  const awardsList = [
    { publication: "CoinDesk", award: "Best Crypto Wallet" },
    { publication: "TechCrunch", award: "Editor's Choice" },
    { publication: "Decrypt", award: "Most User-Friendly" },
  ];

  useEffect(() => {
    const awardTimer = setInterval(() => {
      setAwardIndex(prev => (prev + 1) % awardsList.length);
    }, 5000);
    return () => clearInterval(awardTimer);
  }, [awardsList.length]);

  // Keyboard navigation for size and quantity selection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if focus is within the product section
      const activeElement = document.activeElement;
      const isInProductSection = activeElement?.closest('[data-product-selector]');
      
      if (!isInProductSection) return;
      
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        // Toggle size
        setSelectedSize(prev => prev === 'Standard' ? 'King' : 'Standard');
      }
      
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        const quantities = [1, 2, 4];
        const currentIdx = quantities.indexOf(selectedQuantity);
        if (e.key === 'ArrowUp' && currentIdx > 0) {
          setSelectedQuantity(quantities[currentIdx - 1]);
        } else if (e.key === 'ArrowDown' && currentIdx < quantities.length - 1) {
          setSelectedQuantity(quantities[currentIdx + 1]);
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedQuantity]);

  // Just purchased toast notifications - 20 diverse variants for Ryder One
  const purchaseToasts = [
    // US customers (80% - 16 entries)
    { name: "Sarah M.", location: "Austin, TX", flag: "🇺🇸", action: "bought a Ryder One" },
    { name: "James W.", location: "Denver, CO", flag: "🇺🇸", action: "bought a Ryder One" },
    { name: "Maria G.", location: "Phoenix, AZ", flag: "🇺🇸", action: "bought a Ryder One" },
    { name: "DeShawn J.", location: "Atlanta, GA", flag: "🇺🇸", action: "bought a Ryder One" },
    { name: "Yuki T.", location: "Seattle, WA", flag: "🇺🇸", action: "bought a Ryder One" },
    { name: "Priya P.", location: "San Jose, CA", flag: "🇺🇸", action: "bought a Ryder One" },
    { name: "Miguel R.", location: "Miami, FL", flag: "🇺🇸", action: "bought a Ryder One" },
    { name: "Jennifer L.", location: "Chicago, IL", flag: "🇺🇸", action: "bought a Ryder One" },
    { name: "Kwame A.", location: "Houston, TX", flag: "🇺🇸", action: "bought a Ryder One" },
    { name: "Aisha B.", location: "Brooklyn, NY", flag: "🇺🇸", action: "bought a Ryder One" },
    { name: "Chen W.", location: "Los Angeles, CA", flag: "🇺🇸", action: "bought a Ryder One" },
    { name: "Robert K.", location: "Portland, OR", flag: "🇺🇸", action: "bought a Ryder One" },
    { name: "Fatima H.", location: "Dallas, TX", flag: "🇺🇸", action: "bought a Ryder One" },
    { name: "David S.", location: "Boston, MA", flag: "🇺🇸", action: "bought a Ryder One" },
    { name: "Kenji M.", location: "San Francisco, CA", flag: "🇺🇸", action: "bought a Ryder One" },
    { name: "Olivia N.", location: "Nashville, TN", flag: "🇺🇸", action: "bought a Ryder One" },
    // Canada & UK (10% - 2 entries)
    { name: "Emma T.", location: "Toronto, ON", flag: "🇨🇦", action: "bought a Ryder One" },
    { name: "Oliver B.", location: "London", flag: "🇬🇧", action: "bought a Ryder One" },
    // Europe (10% - 2 entries)
    { name: "Sophie M.", location: "Paris", flag: "🇫🇷", action: "bought a Ryder One" },
    { name: "Lars E.", location: "Stockholm", flag: "🇸🇪", action: "bought a Ryder One" },
  ];

  // Toast notification rotation with randomized timing
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let hideTimeoutId: NodeJS.Timeout;
    
    const showNextToast = () => {
      // Random delay between 15-40 seconds for natural feel
      const delay = 15000 + Math.random() * 25000;
      timeoutId = setTimeout(() => {
        setCurrentToast(Math.floor(Math.random() * purchaseToasts.length));
        setShowToast(true);
        // Auto-hide after 4-6 seconds (randomized)
        hideTimeoutId = setTimeout(() => {
          setShowToast(false);
        }, 4000 + Math.random() * 2000);
        // Schedule next toast
        showNextToast();
      }, delay);
    };
    
    // Initial delay before first toast (5-12 seconds)
    const initialDelay = setTimeout(() => {
      setCurrentToast(Math.floor(Math.random() * purchaseToasts.length));
      setShowToast(true);
      // Auto-hide first toast
      hideTimeoutId = setTimeout(() => {
        setShowToast(false);
      }, 4000 + Math.random() * 2000);
      // Start the rotation
      showNextToast();
    }, 5000 + Math.random() * 7000);

    return () => {
      clearTimeout(initialDelay);
      clearTimeout(timeoutId);
      clearTimeout(hideTimeoutId);
    };
  }, []);

  const formatTime = (num: number) => num.toString().padStart(2, '0');
  
  // Calculate total seconds left for the pillowcase timer display
  const timeLeft = countdown.hours * 3600 + countdown.minutes * 60 + countdown.seconds;
  const formatTimeLeft = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${formatTime(h)}:${formatTime(m)}:${formatTime(s)}`;
  };

  const sizes = [
    { name: "Standard" as const, dimensions: "20\" × 28\"", image: "/images/optimized/sizes/size-standard.jpg", badge: null },
    { name: "King" as const, dimensions: "20\" × 36\"", image: "/images/optimized/sizes/size-king.jpg", badge: "Chosen by 81%", badgeColor: "green" }
  ];
  
  // Ryder One pricing - driven by PricingContext for A/B testing
  const actualDiscount = pricing.discountPercent;
  
  const standardPricing = [
    { quantity: 1, price: pricing.salePrice, originalPrice: pricing.originalPrice, savings: pricing.savingsAmount, discount: actualDiscount, badge: undefined, bonus: undefined },
  ];

  // King pricing (not used for Ryder, kept for compatibility)
  const kingPricing = [
    { quantity: 1, price: pricing.salePrice, originalPrice: pricing.originalPrice, savings: pricing.savingsAmount, discount: actualDiscount, badge: undefined, bonus: undefined },
  ];

  const getPricing = (size: "Standard" | "King") => size === "Standard" ? standardPricing : kingPricing;
  const currentPricing = getPricing(selectedSize);
  const pricingSectionPricing = getPricing(pricingSectionSize);
  
  // Animate prices when size changes
  useEffect(() => {
    if (prevPricingSectionSize !== pricingSectionSize) {
      setIsPricingSectionAnimating(true);
      const timeout = setTimeout(() => {
        setPrevPricingSectionSize(pricingSectionSize);
        setIsPricingSectionAnimating(false);
      }, 150);
      return () => clearTimeout(timeout);
    }
  }, [pricingSectionSize, prevPricingSectionSize]);

  // Track price direction for animation (up or down)
  const [priceDirection, setPriceDirection] = useState<'up' | 'down' | null>(null);
  
  // Animate price tag when size or quantity changes
  useEffect(() => {
    const currentPrice = currentPricing.find(p => p.quantity === selectedQuantity)?.price || 0;
    const perPillowPrice = currentPrice / selectedQuantity;
    const prevPerPillow = prevPrice !== null ? prevPrice : perPillowPrice;
    
    if (prevPrice !== null && prevPerPillow !== perPillowPrice) {
      // Determine direction: price going up or down
      setPriceDirection(perPillowPrice > prevPerPillow ? 'up' : 'down');
      setIsHeroPriceAnimating(true);
      const timeout = setTimeout(() => {
        setIsHeroPriceAnimating(false);
        setPriceDirection(null);
      }, 300);
      return () => clearTimeout(timeout);
    }
    setPrevPrice(perPillowPrice);
  }, [selectedSize, selectedQuantity, currentPricing, prevPrice]);

  // Helper function to get optimized thumbnail srcset for gallery images
  const getThumbnailSrcSet = (imagePath: string): string => {
    const thumbnailMap: Record<string, string> = {
      '/images/optimized/hero/hero-pillow.png': '/images/optimized/hero/responsive/thumbnails/hero-pillow-64.webp 64w, /images/optimized/hero/responsive/thumbnails/hero-pillow-128.webp 128w',
      '/images/optimized/hero/hero-pillow.webp': '/images/optimized/hero/responsive/thumbnails/hero-pillow-64.webp 64w, /images/optimized/hero/responsive/thumbnails/hero-pillow-128.webp 128w',
      '/images/optimized/lifestyle-sleep.png': '/images/optimized/hero/responsive/thumbnails/lifestyle-sleep-64.webp 64w, /images/optimized/hero/responsive/thumbnails/lifestyle-sleep-128.webp 128w',
      '/images/optimized/lifestyle-sleep.webp': '/images/optimized/hero/responsive/thumbnails/lifestyle-sleep-64.webp 64w, /images/optimized/hero/responsive/thumbnails/lifestyle-sleep-128.webp 128w',
      '/images/optimized/benefits/engineering-detail-new.png': '/images/optimized/hero/responsive/thumbnails/engineering-detail-new-64.webp 64w, /images/optimized/hero/responsive/thumbnails/engineering-detail-new-128.webp 128w',
      '/images/optimized/benefits/engineering-detail-new.webp': '/images/optimized/hero/responsive/thumbnails/engineering-detail-new-64.webp 64w, /images/optimized/hero/responsive/thumbnails/engineering-detail-new-128.webp 128w',
      '/images/optimized/benefits/washing-machine.png': '/images/optimized/hero/responsive/thumbnails/washing-machine-64.webp 64w, /images/optimized/hero/responsive/thumbnails/washing-machine-128.webp 128w',
      '/images/optimized/benefits/washing-machine.webp': '/images/optimized/hero/responsive/thumbnails/washing-machine-64.webp 64w, /images/optimized/hero/responsive/thumbnails/washing-machine-128.webp 128w',
      '/images/optimized/benefits/hotel-comfort-packaging.png': '/images/optimized/hero/responsive/thumbnails/hotel-comfort-packaging-64.webp 64w, /images/optimized/hero/responsive/thumbnails/hotel-comfort-packaging-128.webp 128w',
      '/images/optimized/benefits/hotel-comfort-packaging.webp': '/images/optimized/hero/responsive/thumbnails/hotel-comfort-packaging-64.webp 64w, /images/optimized/hero/responsive/thumbnails/hotel-comfort-packaging-128.webp 128w',
    };
    return thumbnailMap[imagePath] || '';
  };

  // Optimized gallery image map: original URL → desktop/mobile WebP versions
  const GALLERY_OPTIMIZED: Record<string, { desktop: string; mobile: string }> = {
    'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/NjPBThSaodRgKVfu.png': {
      desktop: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/HTmC6kmU7Xh3XwwcFSK4D7/NjPBThSaodRgKVfu-desktop_4e78099c.webp',
      mobile: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/HTmC6kmU7Xh3XwwcFSK4D7/NjPBThSaodRgKVfu-mobile_70065bad.webp',
    },
    'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/ExeMYjYyFNdfJBGh.png': {
      desktop: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/HTmC6kmU7Xh3XwwcFSK4D7/ExeMYjYyFNdfJBGh-desktop_3656d628.webp',
      mobile: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/HTmC6kmU7Xh3XwwcFSK4D7/ExeMYjYyFNdfJBGh-mobile_845d8ba9.webp',
    },
    'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/QdoLDlEHJWUkfZYK.png': {
      desktop: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/HTmC6kmU7Xh3XwwcFSK4D7/QdoLDlEHJWUkfZYK-desktop_090393fa.webp',
      mobile: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/HTmC6kmU7Xh3XwwcFSK4D7/QdoLDlEHJWUkfZYK-mobile_eb0cf448.webp',
    },
    'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/UZvynpDmRoRlFchQ.png': {
      desktop: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/HTmC6kmU7Xh3XwwcFSK4D7/UZvynpDmRoRlFchQ-desktop_4ed66156.webp',
      mobile: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/HTmC6kmU7Xh3XwwcFSK4D7/UZvynpDmRoRlFchQ-mobile_b2b76a42.webp',
    },
  };

  const getOptimizedGalleryUrl = (src: string, variant: 'desktop' | 'mobile'): string => {
    return GALLERY_OPTIMIZED[src]?.[variant] || src;
  };

  const getOptimizedGallerySrcSet = (src: string): string => {
    const opt = GALLERY_OPTIMIZED[src];
    if (!opt) return '';
    return `${opt.mobile} 400w, ${opt.desktop} 800w`;
  };

  // Gallery images - reads from config.gallery.images if available
  // This ensures all pages use the standard gallery system with customizations from their configs
  const getGalleryImages = () => {
    // If config has gallery images defined, use them
    if (config.gallery?.images && config.gallery.images.length > 0) {
      return config.gallery.images.map(img => {
        if (img.type === 'comparison') return 'comparison';
        if (img.type === 'hotel-comparison') return 'hotel-comparison';
        if (img.type === 'component') return `component:${img.src}`; // Interactive component slides
        if (img.type === 'video') return img.src; // Videos don't need WebP conversion
        // Don't convert external CDN URLs (manuscdn.com, ryder.id, etc.) - they're already optimized
        if (img.src.startsWith('http://') || img.src.startsWith('https://')) return img.src;
        return toWebP(img.src); // Apply WebP optimization only to local images
      });
    }
    // Default gallery for pages without gallery config
    // Order: 1-intro, 2-tips, 3-layers, 4-VS, 5-care, 6-hotel/summary, 7-pricing
    return [
      toWebP("/images/optimized/hero/hero-pillow.png"),
      toWebP("/images/optimized/hero/lifestyle-sleep.png"),
      toWebP("/images/optimized/benefits/engineering-detail-new.png"), // New image with overlay cards
      "comparison", // Custom comparison component
      toWebP("/images/optimized/benefits/washing-machine.png"), // With overlay badges
      toWebP("/images/optimized/benefits/hotel-comfort-packaging.png"), // 5-Star Hotel Comfort with overlay cards
      "hotel-comparison", // Hotel price comparison table (pricing)
    ];
  };
  const galleryImages = getGalleryImages();
  
  // Helper to get gallery image type
  const getGalleryImageType = (index: number): 'image' | 'video' | 'comparison' | 'hotel-comparison' | 'component' => {
    if (!config.gallery?.images) return 'image';
    const item = config.gallery.images[index];
    if (!item) return 'image';
    return item.type as any;
  };

  // Preload gallery images AFTER initial page load for better performance
  // This ensures the first image loads quickly, then remaining images load in background
  useEffect(() => {
    // Mark the first image as preloaded immediately (it's already being loaded)
    const firstImage = galleryImages[0];
    if (firstImage && firstImage !== 'comparison' && firstImage !== 'hotel-comparison' && !firstImage.startsWith('component:')) {
      setPreloadedImages(new Set([firstImage]));
    }

    // Delay preloading remaining images until after initial render and first image load
    const preloadRemainingImages = () => {
      const imagesToPreload = galleryImages.filter(
        (img, idx) => img !== 'comparison' && img !== 'hotel-comparison' && !img.startsWith('component:') && idx !== 0
      );
      
      // Preload images sequentially with small delays to avoid blocking
      imagesToPreload.forEach((src, index) => {
        setTimeout(() => {
          const img = new Image();
          img.onload = () => {
            setPreloadedImages(prev => {
              const newSet = new Set(Array.from(prev));
              newSet.add(src);
              return newSet;
            });
          };
          img.src = src;
        }, 100 * (index + 1)); // Stagger by 100ms each
      });
    };

    // Wait for page to be fully loaded before preloading remaining images
    if (document.readyState === 'complete') {
      // Page already loaded, start preloading after a short delay
      setTimeout(preloadRemainingImages, 500);
    } else {
      // Wait for page load event
      window.addEventListener('load', () => {
        setTimeout(preloadRemainingImages, 500);
      });
    }

    return () => {
      window.removeEventListener('load', preloadRemainingImages);
    };
  }, []);

  // Reset imageLoaded when switching gallery images
  useEffect(() => {
    const currentImage = galleryImages[activeImageIndex];
    if (currentImage === 'comparison' || currentImage === 'hotel-comparison' || currentImage.startsWith('component:')) {
      // These are React components, no image to load
      setImageLoaded(true);
    } else if (preloadedImages.has(currentImage)) {
      // Image already preloaded
      setImageLoaded(true);
    } else {
      // Wait for image to load
      setImageLoaded(false);
    }
  }, [activeImageIndex, preloadedImages]);

  const handleGalleryImageLoad = () => {
    setImageLoaded(true);
  };

  // Navigate gallery programmatically (for thumbnail clicks)
  const navigateGallery = (index: number) => {
    if (galleryScrollRef.current) {
      const slideWidth = galleryScrollRef.current.offsetWidth;
      galleryScrollRef.current.scrollTo({
        left: slideWidth * index,
        behavior: 'smooth'
      });
    }
  };

  const navSections = [
    { id: "how-it-works", label: "How It Works" },
    { id: "difference", label: "Compare" },
    { id: "security", label: "Tags" },
    { id: "testimonials", label: "Testimonials" },
    { id: "reviews-section", label: "Reviews" },
    { id: "faq", label: "FAQ" },
  ];

  // Emotional testimonials with human touches - use config testimonials or fallback to defaults
  // Note: Config testimonials are merged with default images and ratings
  // Testimonial images optimized to 96x96 WebP (2x retina for 48px display, ~1.5KB each)
  const defaultTestimonialImages = [
    'https://ryder.id/cdn/shop/files/tim-draper.jpg?v=1770282579', // Tim Draper (same as LovedByThousands)
    'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/KgvJKKoCWkzkDHlC.webp', // Sean Carey
    'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/HWBDlaeuxxQBwvtL.webp', // Robinson
    'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/ypQZVcPzdldtEPHT.webp', // Andres
    'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/AvtvknjnURJHYwfx.webp', // Joe Vezzani
    'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/bvOmmhiowfMlCtcf.webp', // Igor
    'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/sGPBKhvKgyeFozCv.webp', // Dayana
    'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/iWiLSXEupsbJgixe.webp', // Yohan Lee
    'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/YUozrZphmvEZcmtt.webp', // Marcus
    'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/hzerXlYarlkDJTGK.webp', // Elena
    'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/pcJKzRErboKDGIlq.webp', // Alex Chen
    'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/qCucHVowwYXjqFbM.webp', // Sarah K.
  ];
  
  const configTestimonials = config.testimonials.testimonials.map((t, idx) => ({
    name: t.name,
    location: t.location,
    text: t.content,
    rating: 5,
    image: defaultTestimonialImages[idx] || defaultTestimonialImages[0],
    verified: t.verified,
  }));
  
  const testimonials = configTestimonials.length > 0 ? configTestimonials : [
    { 
      name: "Mina Fayek", 
      location: "Verified Buyer", 
      text: "I have used other hardware wallets before and I always had worries about my seed phrase. The tap setup with Ryder is very simple and the Recovery Tag makes my backup feel so secure now.", 
      rating: 5, 
      image: defaultTestimonialImages[0] || '/images/optimized/testimonials/testimonial-1.png', 
      verified: true 
    },
    { 
      name: "Sean Carey", 
      location: "Verified Buyer", 
      text: "I am happy to say that Ryder is not vapor. Best hardware wallet experience I've ever had.", 
      rating: 5, 
      image: 'https://ryder.id/cdn/shop/files/Sean_Carey-modified.png?v=1737981370', 
      verified: true 
    },
    { 
      name: "Mehdi Izemmour", 
      location: "Verified Buyer", 
      text: "It's been a few weeks that I use my Ryder and I must say it is the easiest setup I've ever had with a hardware wallet. It literally took me less than 2 minutes.", 
      rating: 5, 
      image: defaultTestimonialImages[2] || '/images/optimized/testimonials/testimonial-3.png', 
      verified: true 
    },
    { 
      name: "Pierre", 
      location: "Verified Buyer", 
      text: "Setup was so quick, the app is clear and the device feels solid. I bought it with extra Recovery Tags and it feels so nice to have these extra backups just in case.", 
      rating: 5, 
      image: defaultTestimonialImages[3] || '/images/optimized/testimonials/testimonial-4.png', 
      verified: true 
    },
    { 
      name: "Joe Vezzani", 
      location: "Crypto Investor", 
      text: "Ryder is a paradigm shift in experience which will change the way you feel about hardware wallets forever.", 
      rating: 5, 
      image: 'https://ryder.id/cdn/shop/files/joe-vezzani-ryder-testimonial.png?v=1737980075', 
      verified: true 
    },
    { 
      name: "Ivo Verhaegh", 
      location: "Verified Buyer", 
      text: "I was honestly surprised by how simple Ryder is to use. The device looks great and feels well built. The setup was much easier than I expected for a hardware wallet.", 
      rating: 5, 
      image: defaultTestimonialImages[5] || '/images/optimized/testimonials/testimonial-6.png', 
      verified: true 
    },
    { 
      name: "Esabella Tan", 
      location: "Verified Buyer", 
      text: "The experience turned out to be smooth. Everything felt so well designed from the unboxing to receive my first crypto to my wallet. It basically just works.", 
      rating: 5, 
      image: defaultTestimonialImages[6] || defaultTestimonialImages[0], 
      verified: true 
    },
    { 
      name: "Samarth Shyam", 
      location: "Verified Buyer", 
      text: "Really satisfied. I used a few hardware wallets before and Ryder is very intuitive. There is no trouble during setup and it felt great to go through the entire process.", 
      rating: 5, 
      image: defaultTestimonialImages[7] || defaultTestimonialImages[1], 
      verified: true 
    },
  ];

  const visibleTestimonials = showAllTestimonials ? testimonials : testimonials.slice(0, 4);

  // FAQs from config or defaults
  const configFaqs = config.faq.items.map(item => ({ q: item.question, a: item.answer }));
  const defaultFaqs = [
    { q: "What are the key features of Ryder One?", a: "Quick and simple setup, tap to send/swap/recover, secure signing, TapSafe Recovery, Social Recovery, wireless charging, and support for Bitcoin, Ethereum, Solana, Stacks, plus top ERC-20 and SPL tokens." },
    { q: "How do I set up my Ryder One?", a: "Setting up your Ryder One involves only three steps: 1) Pair your Ryder One with your phone, 2) Create your wallet, 3) Back it up using your Recovery Tag and your phone. The entire process takes less than a minute." },
    { q: "What if I lose my Ryder One wallet?", a: "If you lose your Ryder One, you can recover it using your Recovery Tag or Recovery Contacts, depending on how you've set up your recovery options. Your crypto stays safe and accessible." },
    { q: "Can I still see my seed phrase?", a: "Yes. While TapSafe makes it unnecessary to write down or store your 24 words, Ryder One still lets you reveal your seed phrase in the device settings if you prefer traditional backup methods." },
    { q: "How does TapSafe Recovery work?", a: "TapSafe Recovery uses Shamir Secret Sharing, an established algorithm that splits your backup into shares. These shares are securely saved on your Recovery Tag, phone, or Recovery Contacts. No paper, no stress." },
    { q: "What comes with my purchase?", a: "Your purchase includes: 1 Ryder One hardware wallet, 1 Recovery Tag, 1 wireless charger, and 1 travel pouch. Everything you need to secure your crypto in one package." },
    { q: "Is Ryder One secure?", a: "Yes. Ryder One features an Infineon SLC38 security chip with EAL6+ certification, encrypted NFC communication, and offline signing. It's bank-level security without the complexity." },
  ];
  const faqs = configFaqs.length > 0 ? configFaqs : defaultFaqs;

  const scrollToSection = (id: string) => {
    // Map nav IDs to actual section IDs
    const sectionIdMap: Record<string, string> = {
      'reviews': 'reviews-section',
    };
    const targetId = sectionIdMap[id] || id;
    const element = document.getElementById(targetId);
    if (!element) return;

    const HEADER_OFFSET = 100;
    const doScroll = (smooth: boolean) => {
      const rect = element.getBoundingClientRect();
      const absoluteTop = rect.top + window.scrollY;
      window.scrollTo({ top: absoluteTop - HEADER_OFFSET, behavior: smooth ? 'smooth' : 'auto' });
    };

    doScroll(true);

    // Correct for lazy-loaded content shifting layout
    let settled = false;
    const resizeObserver = new ResizeObserver(() => {
      if (!settled) doScroll(false);
    });
    resizeObserver.observe(document.body);

    const correctionTimers = [200, 500, 1000, 1500, 2000].map(d =>
      setTimeout(() => { if (!settled) doScroll(false); }, d)
    );
    const settleTimer = setTimeout(() => {
      settled = true;
      resizeObserver.disconnect();
      correctionTimers.forEach(clearTimeout);
    }, 3000);
  };

  const scrollToPricing = () => {
    const element = document.getElementById('hero-pricing');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const selectOptionAndScroll = (quantity: number, size: "Standard" | "King") => {
    setSelectedSize(size);
    setSelectedQuantity(quantity);
    scrollToPricing();
  };

  const benefitCards = [
    { title: "Holds Shape for Years", desc: "Sanforized construction maintains loft and structure. No flattening, no sagging.", icon: Clock, image: "/images/optimized/hero/hero-pillow.png" },
    { title: "No Daily Fluffing", desc: "The structural core holds its position. Make your bed once and forget about it.", icon: Sparkles, image: "/images/optimized/benefits/benefit-fluffing.png" },
    { title: "Machine Washable", desc: "Reinforced seams handle regular cleaning. Proper hygiene without compromise.", icon: Droplets, image: "/images/optimized/benefits/benefit-washable.png" },
    { title: "Hypoallergenic", desc: "Down alternative fill provides luxury feel without allergens or ethical concerns.", icon: Shield, image: "/images/optimized/benefits/benefit-hypoallergenic.png" },
    { title: "Temperature Neutral", desc: "Breathable cotton cover stays comfortable. No overheating, no cold spots.", icon: Wind, image: "/images/optimized/benefits/benefit-temperature.png" },
    { title: "Consistent Support", desc: "Same feel on night 1,000 as night 1. Your neck knows what to expect.", icon: RefreshCw, image: "/images/optimized/hero/lifestyle-sleep.png" },
  ];

  // CSS variables for event colors
  const eventCssVars = isEventPage && eventColors ? {
    '--event-primary': eventColors.primary,
    '--event-secondary': eventColors.secondary,
    '--event-accent': eventColors.accent,
    '--event-accent-dark': eventColors.accentDark,
    '--event-background': eventColors.background,
  } as React.CSSProperties : {};

  // Check for quiz profile parameter and use-case source
  const urlParams = new URLSearchParams(window.location.search);
  const profileId = urlParams.get('profile');
  const userEmail = urlParams.get('email');
  const fromUseCase = urlParams.get('from'); // e.g., 'side-sleeper-pillow'
  const quizTags = urlParams.get('tags')?.split(',').filter(Boolean) || []; // Parse quiz tags for badges
  const quizLabels = urlParams.get('labels')?.split(',').filter(Boolean) || []; // Human-readable labels for results
  
  // Convert use-case slug to display name
  const getUseCaseName = (slug: string | null) => {
    // Don't show badge for generic 'quiz' source - use quizLabels instead
    if (!slug || slug === 'quiz') return null;
    const names: Record<string, string> = {
      'side-sleeper-pillow': 'Side Sleepers',
      'hot-sleeper': 'Hot Sleepers',
      'neck-pain': 'Neck Pain Relief',
    };
    return names[slug] || null; // Don't fallback to slug parsing, only show known use cases
  };
  
  const useCaseName = getUseCaseName(fromUseCase);

  return (
    <div className="min-h-screen bg-background" style={eventCssVars}>
      <main id="main-content">

      {/* Profile Results Banner - shown when user arrives from quiz */}
      {profileId && <ProfileResultsBanner profileId={profileId} email={userEmail || undefined} tags={quizTags} labels={quizLabels} onRestartQuiz={() => setIsQuizModalOpen(true)} onOpenCheckout={() => setShowOrderPopup(true)} pageId={config.id} />}
      {/* STICKY ANNOUNCEMENT BAR - clickable on mobile */}
      <div 
        data-announcement-bar
        className="sticky top-0 z-[60] text-white py-1.5 sm:py-2.5 px-3 sm:px-4 text-center text-sm sm:cursor-default cursor-pointer transition-colors pointer-events-auto"
        style={{ backgroundColor: isEventPage && eventColors ? eventColors.primary : '#1a1a2e' }}
        onClick={(e) => {
          // Only trigger on mobile (when Claim Now button is hidden)
          if (window.innerWidth < 640) {
            // Haptic feedback for mobile devices
            if (navigator.vibrate) {
              navigator.vibrate(10);
            }
            scrollToPricing();
          }
        }}
      >
        <div className="flex items-center justify-center gap-2 sm:gap-3 flex-wrap">
          <span>{config.announcementBar.text}</span>
          {!timerExpired && (
            <div className="flex items-center gap-1 font-mono text-xs bg-white/10 px-2 py-1 rounded">
              <span className="tabular-nums">{formatTime(countdown.hours)}</span>
              <span>:</span>
              <span className="tabular-nums">{formatTime(countdown.minutes)}</span>
              <span>:</span>
              <span className="tabular-nums">{formatTime(countdown.seconds)}</span>
            </div>
          )}
          <button 
            onClick={(e) => {
              e.stopPropagation();
              scrollToPricing();
            }}
            className="hidden sm:block text-white text-xs font-medium px-4 py-1.5 rounded-full transition-colors animate-cta-shimmer"
            style={isEventPage && eventColors ? {
              backgroundColor: eventColors.secondary,
            } : {
              backgroundColor: '#6a63f5'
            }}
            onMouseEnter={(e) => {
              if (isEventPage && eventColors) {
                // Darken the secondary color by 10% on hover
                const color = eventColors.secondary;
                const darkened = color.replace('#', '');
                const r = Math.max(0, parseInt(darkened.substr(0, 2), 16) - 25);
                const g = Math.max(0, parseInt(darkened.substr(2, 2), 16) - 25);
                const b = Math.max(0, parseInt(darkened.substr(4, 2), 16) - 25);
                e.currentTarget.style.backgroundColor = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
              } else {
                e.currentTarget.style.backgroundColor = '#5850d9';
              }
            }}
            onMouseLeave={(e) => {
              if (isEventPage && eventColors) {
                e.currentTarget.style.backgroundColor = eventColors.secondary;
              } else {
                e.currentTarget.style.backgroundColor = '#6a63f5';
              }
            }}
          >
            {config.announcementBar.ctaText}
          </button>
        </div>
      </div>

      {/* STICKY HEADER - Shows on scroll up, hides on scroll down (desktop only) */}
      {/* Positioned below the sticky announcement bar (top-[44px] accounts for announcement bar height) */}
      <header 
        className={`fixed top-[44px] left-0 right-0 z-50 border-b border-border hidden md:block transition-transform duration-300 ${isEventPage ? 'bg-white' : 'bg-background'} ${showStickyHeader ? 'translate-y-0' : '-translate-y-[calc(100%+44px)]'}`}
        style={{ boxShadow: showStickyHeader ? '0 2px 8px rgba(0,0,0,0.08)' : 'none' }}
      >
        <div className="container px-4 flex items-center justify-between h-14">
          <RyderLogo className="h-7 w-auto" variant="dark" />
          <nav className="flex items-center gap-6">
            {navSections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {section.label}
              </button>
            ))}
          </nav>
          <button 
            onClick={scrollToPricing}
            className="text-white text-sm font-medium px-5 py-2 rounded-full transition-all duration-300 animate-cta-shimmer hover:-translate-y-px active:translate-y-0"
            style={{ 
              backgroundColor: isEventPage && eventColors ? eventColors.primary : '#6a63f5',
              boxShadow: `0 2px 6px ${isEventPage && eventColors ? eventColors.primary : '#6a63f5'}40`
            }}
          >
            {isBtcOffer ? 'Get Ryder One →' : `${getPageCTAWithDiscount(actualDiscount)} →`}
          </button>
        </div>
      </header>

      {/* HEADER - Normal (not sticky) */}
      <header className={`border-b border-border ${isEventPage ? 'bg-white' : 'bg-background'}`}>
        <div className="container px-4 flex items-center justify-between h-14">
          <RyderLogo className="h-7 w-auto" variant="dark" />
          <nav className="hidden md:flex items-center gap-6">
            {navSections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {section.label}
              </button>
            ))}
          </nav>
          <button 
            onClick={scrollToPricing}
            className="text-white text-sm font-medium px-5 py-2 rounded-full transition-all duration-300 animate-cta-shimmer hover:-translate-y-px active:translate-y-0"
            style={{ 
              backgroundColor: isEventPage && eventColors ? eventColors.primary : '#6a63f5',
              boxShadow: `0 2px 6px ${isEventPage && eventColors ? eventColors.primary : '#6a63f5'}40`
            }}
            onMouseEnter={(e) => {
              const color = isEventPage && eventColors ? eventColors.primary : '#6a63f5';
              e.currentTarget.style.boxShadow = `0 2px 8px ${color}35`;
              e.currentTarget.style.filter = 'brightness(0.97)';
            }}
            onMouseLeave={(e) => {
              const color = isEventPage && eventColors ? eventColors.primary : '#6a63f5';
              e.currentTarget.style.boxShadow = `0 2px 6px ${color}40`;
              e.currentTarget.style.filter = 'brightness(1)';
            }}
          >
            {isBtcOffer ? 'Get Ryder One →' : `${getPageCTAWithDiscount(actualDiscount)} →`}
          </button>
        </div>
      </header>

      {/* BLACK FRIDAY URGENCY SECTION - Only shown on Black Friday page */}
      {config.id === 'black-friday' && <BlackFridayUrgencySection backgroundColor={eventColors?.accentDark || '#f8f8f6'} primaryColor={eventColors?.primary || '#FFD700'} textColor={eventColors?.textOnLight || eventColors?.primary || '#1a1a1a'} accentColor={eventColors?.accent || '#FF6B6B'} />}

      {/* HERO SECTION */}
      <section 
        data-hero-section
        className="pt-0 pb-8 md:py-12 md:pt-5 overflow-x-clip"
        style={isEventPage && eventColors ? {
          background: `linear-gradient(to bottom, ${eventColors.background}, white)`
        } : {}}
      >
        <div className="container px-0 md:px-4 lg:px-4 max-w-full overflow-x-hidden md:overflow-x-visible">
          <div className="grid md:grid-cols-2 gap-4 md:gap-6 lg:gap-12 max-w-full">
            {/* LEFT: Product Gallery - Sticky on desktop/tablet */}
            <div className="space-y-2 md:space-y-4 md:sticky md:top-[88px] md:self-start md:h-fit w-full min-w-0 relative md:pl-0 overflow-visible">
              {/* Badge - Desktop only here, Mobile is fixed positioned via JS */}
              {/* Desktop badge - positioned at top-right corner overlapping gallery */}
              <div className="hidden md:block absolute -top-5 -right-5 z-30 animate-[fadeSlideIn_0.4s_ease-out_0.15s_both]">
                <div className="relative">
                  {/* Scalloped badge shape - slightly larger for better readability */}
                  <svg viewBox="0 0 80 80" className="w-[80px] h-[80px] lg:w-[88px] lg:h-[88px] drop-shadow-md">
                    <path d="M40 2 L44 8 L52 6 L54 14 L62 14 L62 22 L70 24 L68 32 L76 38 L70 44 L74 52 L66 54 L66 62 L58 62 L54 70 L46 68 L40 76 L34 68 L26 70 L22 62 L14 62 L14 54 L6 52 L10 44 L4 38 L12 32 L10 24 L18 22 L18 14 L26 14 L28 6 L36 8 Z" fill={isEventPage ? eventColors?.accent : '#ffffff'} stroke={isEventPage ? eventColors?.primary : '#e0ddd5'} strokeWidth="1" strokeLinejoin="round" strokeLinecap="round"/>
                  </svg>
                  {/* Badge text content */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center" style={{paddingBottom: '2px'}}>
                    {(() => {
                      if (isBtcOffer) {
                        return (
                          <div className="flex flex-col items-center justify-center gap-0" style={{marginTop: '2px'}}>
                            <span className="text-lg lg:text-xl font-black leading-none text-[#6a63f5]" style={{ marginBottom: '-1px' }}>$200</span>
                            <span className="text-[7px] lg:text-[8px] font-bold uppercase tracking-wider leading-none text-[#6a63f5]/90" style={{ marginBottom: '1px' }}>CASH</span>
                            <div className="flex flex-col items-center" style={{marginTop: '1px'}}>
                              <span className="text-[6px] lg:text-[7px] font-semibold uppercase tracking-wide leading-none text-[#6a63f5]/80">BACK</span>
                            </div>
                          </div>
                        );
                      }
                      const primaryColor = isEventPage ? eventColors?.primary : '#171717';
                      return (
                        <div className="flex flex-col items-center justify-center gap-0" style={{marginTop: '2px'}}>
                          <span className="text-2xl lg:text-[28px] font-black leading-none" style={{ color: primaryColor, marginBottom: '-3px' }}>30</span>
                          <span className="text-[9px] lg:text-[10px] font-bold uppercase tracking-wider leading-none" style={{ color: primaryColor, fontSize: '11px', marginBottom: '1px' }}>DAY</span>
                          <div className="flex flex-col items-center" style={{marginTop: '1px'}}>
                            <span className="text-[6.5px] lg:text-[7.5px] font-semibold uppercase tracking-wide leading-none" style={{ color: primaryColor }}>MONEY</span>
                            <span className="text-[6.5px] lg:text-[7.5px] font-semibold uppercase tracking-wide leading-none" style={{ color: primaryColor }}>BACK</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
              <div 
                className="aspect-square bg-secondary rounded-none md:rounded-lg overflow-hidden relative border border-border/40"
              >
                {/* Badge - Mobile only, first slide only */}
                {activeImageIndex === 0 && (
                  <div className="md:hidden absolute top-2 right-2 z-30 animate-[fadeSlideIn_0.4s_ease-out_0.15s_both]">
                    <div className="relative">
                      {/* Scalloped badge shape - same size as desktop (80px) */}
                      <svg viewBox="0 0 80 80" className="w-[80px] h-[80px] drop-shadow-md">
                        <path d="M40 2 L44 8 L52 6 L54 14 L62 14 L62 22 L70 24 L68 32 L76 38 L70 44 L74 52 L66 54 L66 62 L58 62 L54 70 L46 68 L40 76 L34 68 L26 70 L22 62 L14 62 L14 54 L6 52 L10 44 L4 38 L12 32 L10 24 L18 22 L18 14 L26 14 L28 6 L36 8 Z" fill={isEventPage ? eventColors?.accent : '#ffffff'} stroke={isEventPage ? eventColors?.primary : '#e0ddd5'} strokeWidth="1" strokeLinejoin="round" strokeLinecap="round"/>
                      </svg>
                      {/* Badge text content */}
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        {(() => {
                          if (isBtcOffer) {
                            return (
                              <div className="flex flex-col items-center justify-center gap-0" style={{marginTop: '2px'}}>
                                <span className="text-lg font-black leading-none text-[#6a63f5]" style={{ marginBottom: '-1px' }}>$200</span>
                                <span className="text-[7px] font-bold uppercase tracking-wider leading-none text-[#6a63f5]/90" style={{ marginBottom: '1px' }}>CASH</span>
                                <div className="flex flex-col items-center" style={{marginTop: '1px'}}>
                                  <span className="text-[6px] font-semibold uppercase tracking-wide leading-none text-[#6a63f5]/80">BACK</span>
                                </div>
                              </div>
                            );
                          }
                          const primaryColor = isEventPage ? eventColors?.primary : '#171717';
                          return (
                            <div className="flex flex-col items-center justify-center gap-0" style={{marginTop: '2px'}}>
                              <span className="text-2xl font-black leading-none" style={{ color: primaryColor, marginBottom: '-3px' }}>30</span>
                              <span className="text-[9px] font-bold uppercase tracking-wider leading-none" style={{ color: primaryColor, fontSize: '11px', marginBottom: '1px' }}>DAY</span>
                              <div className="flex flex-col items-center" style={{marginTop: '1px'}}>
                                <span className="text-[6.5px] font-semibold uppercase tracking-wide leading-none" style={{ color: primaryColor }}>MONEY</span>
                                <span className="text-[6.5px] font-semibold uppercase tracking-wide leading-none" style={{ color: primaryColor }}>BACK</span>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}
                {/* Horizontal scroll carousel - all images laid out side-by-side */}
                <div 
                  className="flex w-full h-full overflow-x-scroll snap-x snap-mandatory scrollbar-hide"
                  style={{
                    scrollBehavior: 'smooth',
                    WebkitOverflowScrolling: 'touch',
                  }}
                  onScroll={(e) => {
                    const container = e.currentTarget;
                    const scrollLeft = container.scrollLeft;
                    const slideWidth = container.offsetWidth;
                    const newIndex = Math.round(scrollLeft / slideWidth);
                    if (newIndex !== activeImageIndex) {
                      setActiveImageIndex(newIndex);
                    }
                  }}
                  ref={(el) => {
                    if (el && galleryScrollRef.current !== el) {
                      galleryScrollRef.current = el;
                    }
                  }}
                >
                  {/* Render all gallery images side-by-side */}
                  {galleryImages.map((image, index) => {
                    // Extract src from image object or use string directly
                    const imageSrc = typeof image === 'string' ? image : ((image as any).src || image);
                    return (
                    <div key={index} className="flex-shrink-0 w-full h-full relative snap-start">
                      {imageSrc === 'comparison' ? (
                        <ComparisonSlide />
                      ) : imageSrc === 'hotel-comparison' ? (
                        <HotelComparisonSlide fluffPrice={`$${((currentPricing.find(p => p.quantity === selectedQuantity)?.price || 0) / selectedQuantity).toFixed(0)}`} />
                      ) : getGalleryImageType(index) === 'video' ? (
                        <div className="relative w-full h-full">
                          <LazyVideo
                            src={imageSrc}
                            className="w-full h-full object-cover"
                            autoPlay
                            loop
                            muted
                            playsInline
                            poster={config.gallery?.images?.[index]?.posterSrc}
                            onLoadedData={() => {
                              if (index === activeImageIndex) {
                                handleGalleryImageLoad();
                              }
                            }}
                          />
                          {/* CRO-friendly title + description overlay with bottom gradient */}
                          <div className="absolute bottom-0 left-0 right-0 pointer-events-none" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.35) 50%, transparent 100%)' }}>
                            <div className="px-5 pb-5 pt-12 md:px-6 md:pb-6 md:pt-16">
                              {index === 1 && (
                                <>
                                  <h3 className="text-white text-lg md:text-xl font-bold leading-tight mb-1">Set Up in 60 Seconds</h3>
                                  <p className="text-white/85 text-sm md:text-base leading-snug">Tap your phone. Create your wallet. Done. No seed phrases, no complexity.</p>
                                </>
                              )}
                              {index === 3 && (
                                <>
                                  <h3 className="text-white text-lg md:text-xl font-bold leading-tight mb-1">Send Crypto Instantly</h3>
                                  <p className="text-white/85 text-sm md:text-base leading-snug">One tap to confirm. Your transaction is signed offline and sent securely.</p>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        // Use regular img for external CDN URLs to avoid blur issues
                        imageSrc.startsWith('http://') || imageSrc.startsWith('https://') ? (
                          <img 
                            src={getOptimizedGalleryUrl(imageSrc, 'desktop')} 
                            srcSet={getOptimizedGallerySrcSet(imageSrc)}
                            sizes="(max-width: 640px) 400px, 800px"
                            alt="Product" 
                            className="w-full h-full object-cover"
                            width={800}
                            height={800}
                            loading={index === 0 ? 'eager' : 'lazy'}
                            decoding="async"
                            fetchPriority={index === 0 ? 'high' : undefined}
                            onLoad={() => {
                              if (index === activeImageIndex) {
                                handleGalleryImageLoad();
                              }
                            }}
                          />
                        ) : (
                          <BlurImage 
                            src={imageSrc} 
                            alt="Product" 
                            className="w-full h-full object-cover"
                            blurAmount={15}
                            transitionDuration={400}
                            loading={index === 0 ? 'eager' : 'lazy'}
                            fetchPriority={index === 0 ? 'high' : undefined}
                            onLoad={() => {
                              if (index === activeImageIndex) {
                                handleGalleryImageLoad();
                              }
                            }}
                          />
                        )
                      )}
                    </div>
                  );
                  })}
                </div>
                {/* First Image Badges - Base page only (exclude neck-pain-relief and restorative-alignment which have their own overlays) */}
                {activeImageIndex === 0 && imageLoaded && config.id !== 'neck-pain-relief' && config.id !== 'restorative-alignment' && (
                  <>
                    {/* Event Headline Overlay - Top Right for event pages */}
                    {isEventPage && config.hero.eventHeadline && (
                      <div className="absolute top-4 right-4 lg:top-6 lg:right-6 max-w-[280px] lg:max-w-[380px] animate-[fadeSlideIn_0.4s_ease-out_0.15s_both] z-20 text-right">
                        <p 
                          className="text-2xl md:text-3xl lg:text-4xl font-semibold leading-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]"
                          style={{ color: '#ffffff' }}
                        >
                          {/* Support line breaks in eventHeadline using <br> tag or newline */}
                          {config.hero.eventHeadline.split('\n').map((line, idx, arr) => (
                            <span key={idx}>
                              {line}
                              {idx < arr.length - 1 && <br />}
                            </span>
                          ))}
                        </p>
                      </div>
                    )}
                    {/* Profile-Based Contextual Badge - Top Right for quiz profiles (same design as event headline) */}
                    {!isEventPage && quizTags.length > 0 && (() => {
                      // Define playful sentences for each profile tag
                      const profileMessages: Record<string, string> = {
                        'neck-pain': 'Finally, mornings\nwithout the ouch',
                        'hot-sleeper': 'No more flipping\nfor the cool side',
                        'restless': 'Sleep through\nthe night, finally',
                        'stomach': 'Designed for\nface-down dreamers',
                        'back': 'Perfect support,\nzero pressure points',
                        'side': 'Made for\nside sleepers like you',
                      };
                      // Find the first matching tag
                      const matchedTag = quizTags.find(tag => profileMessages[tag]);
                      if (!matchedTag) return null;
                      const message = profileMessages[matchedTag];
                      return (
                        <div className="absolute top-4 right-4 lg:top-6 lg:right-6 max-w-[280px] lg:max-w-[380px] animate-[fadeSlideIn_0.4s_ease-out_0.15s_both] z-20 text-right">
                          <p 
                            className="text-2xl md:text-3xl lg:text-4xl font-semibold leading-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]"
                            style={{ color: '#ffffff' }}
                          >
                            {message.split('\n').map((line, idx, arr) => (
                              <span key={idx}>
                                {line}
                                {idx < arr.length - 1 && <br />}
                              </span>
                            ))}
                          </p>
                        </div>
                      );
                    })()}
                    {/* Dynamic Price Tag Overlay - Only for hotel-quality page */}
                    {config.id === 'hotel-quality' && (
                      <div className="absolute bottom-16 right-4 lg:bottom-20 lg:right-6 animate-[fadeSlideIn_0.4s_ease-out_0.3s_both] z-20">
                        <div className="relative">
                          {/* Price tag shape */}
                          <div className="bg-white shadow-xl rounded-lg px-4 py-3 border-2 border-amber-400 relative">
                            {/* Tag hole */}
                            <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-3 h-3 bg-amber-400 rounded-full border-2 border-white shadow-sm"></div>
                            {/* String */}
                            <div className="absolute -left-4 top-1/2 w-3 h-0.5 bg-amber-600/50"></div>
                            
                            {/* Price per unit with animation */}
                            <div className="text-center">
                              <div className="text-[10px] text-gray-500 uppercase tracking-wide font-medium mb-0.5">Per Unit</div>
                              <div className="flex items-baseline justify-center gap-1">
                                <span 
                                  className="text-2xl lg:text-3xl font-bold text-gray-900 inline-block"
                                  style={{
                                    transition: 'transform 0.25s ease-out, opacity 0.2s ease',
                                    transform: isHeroPriceAnimating ? 'scale(1.05)' : 'scale(1)',
                                    opacity: isHeroPriceAnimating ? 0.85 : 1
                                  }}
                                >
                                  ${((currentPricing.find(p => p.quantity === selectedQuantity)?.price || 0) / selectedQuantity).toFixed(0)}
                                </span>
                                <span className="text-xs text-gray-400 line-through">
                                  ${((currentPricing.find(p => p.quantity === selectedQuantity)?.originalPrice || 0) / selectedQuantity).toFixed(0)}
                                </span>
                              </div>
                              <div className="text-[10px] text-gray-500 mt-0.5">
                                Total: ${(currentPricing.find(p => p.quantity === selectedQuantity)?.price || 0).toFixed(0)} for {selectedQuantity}
                              </div>
                              <div className="mt-1 bg-green-100 text-green-700 text-[10px] font-semibold px-2 py-0.5 rounded-full inline-block">
                                Save ${(currentPricing.find(p => p.quantity === selectedQuantity)?.savings || 0).toFixed(0)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Oprah badge removed - not relevant for crypto wallet */}

                    {/* Award Carousel Badge - Bottom Center */}
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 animate-[fadeSlideIn_0.4s_ease-out_0.2s_both]">
                      <div className="bg-white/95 backdrop-blur-sm rounded-md px-2 py-1 lg:px-2.5 lg:py-1.5 shadow-md flex items-center gap-1.5">
                        <button 
                          onClick={(e) => { e.stopPropagation(); setAwardIndex(prev => prev === 0 ? awardsList.length - 1 : prev - 1); }}
                          className="p-0.5 hover:bg-gray-100 rounded transition-colors"
                          aria-label="Previous award"
                        >
                          <ChevronLeft className="w-3 h-3 text-gray-800" />
                        </button>
                        <div 
                          key={awardIndex}
                          className="flex items-center gap-1.5 whitespace-nowrap animate-[fadeInUp_0.4s_ease-out_both]"
                        >
                          <span className="text-gray-900 text-[11px] lg:text-xs font-semibold">
                            {awardsList[awardIndex].award}
                          </span>
                          <span className="text-muted-foreground text-[10px] lg:text-[11px]">
                            {awardsList[awardIndex].publication}
                          </span>
                        </div>
                        <button 
                          onClick={(e) => { e.stopPropagation(); setAwardIndex(prev => (prev + 1) % awardsList.length); }}
                          className="p-0.5 hover:bg-gray-100 rounded transition-colors"
                          aria-label="Next award"
                        >
                          <ChevronRight className="w-3 h-3 text-gray-800" />
                        </button>
                      </div>
                    </div>

                  </>
                )}
                {/* Neck Pain Relief - First Image Overlay */}
                {activeImageIndex === 0 && config.id === 'neck-pain-relief' && imageLoaded && (
                  <>
                    {/* Oprah badge removed - not relevant for crypto wallet */}
                    {/* Bottom Left Badge - Neutral Alignment */}
                    <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm text-[#171717] px-3 py-2 lg:px-4 lg:py-3 rounded-lg lg:rounded-xl shadow-lg max-w-[160px] lg:max-w-[200px] animate-[fadeSlideIn_0.4s_ease-out_0.2s_both]">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-5 h-5 lg:w-6 lg:h-6 bg-[#2563eb] text-white rounded-full text-[11px] lg:text-sm flex items-center justify-center font-semibold">1</span>
                        <span className="text-[11px] lg:text-sm font-bold">Neutral Alignment</span>
                      </div>
                      <p className="text-[10px] lg:text-xs text-gray-600 leading-snug">Maintains natural spine curvature</p>
                    </div>
                    {/* Bottom Right Badge - Proper Support */}
                    <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur-sm text-[#171717] px-3 py-2 lg:px-4 lg:py-3 rounded-lg lg:rounded-xl shadow-lg max-w-[160px] lg:max-w-[200px] animate-[fadeSlideIn_0.4s_ease-out_0.35s_both]">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-5 h-5 lg:w-6 lg:h-6 bg-[#2563eb] text-white rounded-full text-[11px] lg:text-sm flex items-center justify-center font-semibold">2</span>
                        <span className="text-[11px] lg:text-sm font-bold">Proper Support</span>
                      </div>
                      <p className="text-[10px] lg:text-xs text-gray-600 leading-snug">Consistent support night after night</p>
                    </div>
                    {/* Award Badge - Top Center (same style as base page) */}
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 animate-[fadeSlideIn_0.4s_ease-out_0.2s_both]">
                      <div className="bg-white/95 backdrop-blur-sm rounded-md px-2 py-1 lg:px-2.5 lg:py-1.5 shadow-md flex items-center gap-1.5">
                        <button 
                          onClick={(e) => { e.stopPropagation(); setAwardIndex(prev => prev === 0 ? awardsList.length - 1 : prev - 1); }}
                          className="p-0.5 hover:bg-gray-100 rounded transition-colors"
                          aria-label="Previous award"
                        >
                          <ChevronLeft className="w-3 h-3 text-gray-800" />
                        </button>
                        <div 
                          key={awardIndex}
                          className="flex items-center gap-1.5 whitespace-nowrap animate-[fadeInUp_0.4s_ease-out_both]"
                        >
                          <span className="text-gray-900 text-[11px] lg:text-xs font-semibold">
                            {awardsList[awardIndex].award}
                          </span>
                          <span className="text-muted-foreground text-[10px] lg:text-[11px]">
                            {awardsList[awardIndex].publication}
                          </span>
                        </div>
                        <button 
                          onClick={(e) => { e.stopPropagation(); setAwardIndex(prev => (prev + 1) % awardsList.length); }}
                          className="p-0.5 hover:bg-gray-100 rounded transition-colors"
                          aria-label="Next award"
                        >
                          <ChevronRight className="w-3 h-3 text-gray-800" />
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {/* Restorative Alignment - First Image Overlay */}
                {activeImageIndex === 0 && config.id === 'restorative-alignment' && imageLoaded && (
                  <>
                    {/* Oprah badge removed - not relevant for crypto wallet */}
                    {/* Bottom Left Badge - Neutral Alignment */}
                    <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm text-[#171717] px-3 py-2 lg:px-4 lg:py-3 rounded-lg lg:rounded-xl shadow-lg max-w-[160px] lg:max-w-[200px] animate-[fadeSlideIn_0.4s_ease-out_0.2s_both]">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-5 h-5 lg:w-6 lg:h-6 bg-[#c9a962] text-white rounded-full text-[11px] lg:text-sm flex items-center justify-center font-semibold">1</span>
                        <span className="text-[11px] lg:text-sm font-bold">Neutral Alignment</span>
                      </div>
                      <p className="text-[10px] lg:text-xs text-gray-600 leading-snug">Maintains natural spine curvature</p>
                    </div>
                    {/* Bottom Right Badge - Proper Support */}
                    <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur-sm text-[#171717] px-3 py-2 lg:px-4 lg:py-3 rounded-lg lg:rounded-xl shadow-lg max-w-[160px] lg:max-w-[200px] animate-[fadeSlideIn_0.4s_ease-out_0.35s_both]">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-5 h-5 lg:w-6 lg:h-6 bg-[#c9a962] text-white rounded-full text-[11px] lg:text-sm flex items-center justify-center font-semibold">2</span>
                        <span className="text-[11px] lg:text-sm font-bold">Proper Support</span>
                      </div>
                      <p className="text-[10px] lg:text-xs text-gray-600 leading-snug">Consistent support night after night</p>
                    </div>
                    {/* Award Badge - Top Center (same style as base page) */}
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 animate-[fadeSlideIn_0.4s_ease-out_0.2s_both]">
                      <div className="bg-white/95 backdrop-blur-sm rounded-md px-2 py-1 lg:px-2.5 lg:py-1.5 shadow-md flex items-center gap-1.5">
                        <button 
                          onClick={(e) => { e.stopPropagation(); setAwardIndex(prev => prev === 0 ? awardsList.length - 1 : prev - 1); }}
                          className="p-0.5 hover:bg-gray-100 rounded transition-colors"
                          aria-label="Previous award"
                        >
                          <ChevronLeft className="w-3 h-3 text-gray-800" />
                        </button>
                        <div 
                          key={awardIndex}
                          className="flex items-center gap-1.5 whitespace-nowrap animate-[fadeInUp_0.4s_ease-out_both]"
                        >
                          <span className="text-gray-900 text-[11px] lg:text-xs font-semibold">
                            {awardsList[awardIndex].award}
                          </span>
                          <span className="text-muted-foreground text-[10px] lg:text-[11px]">
                            {awardsList[awardIndex].publication}
                          </span>
                        </div>
                        <button 
                          onClick={(e) => { e.stopPropagation(); setAwardIndex(prev => (prev + 1) % awardsList.length); }}
                          className="p-0.5 hover:bg-gray-100 rounded transition-colors"
                          aria-label="Next award"
                        >
                          <ChevronRight className="w-3 h-3 text-gray-800" />
                        </button>
                      </div>
                    </div>
                  </>
                )}
                {/* Tips Overlay Badges - dynamically detects tips slide based on image path */}
                {galleryImages[activeImageIndex]?.includes?.('lifestyle-sleep') && galleryImages[activeImageIndex] !== 'comparison' && galleryImages[activeImageIndex] !== 'hotel-comparison' && imageLoaded && (
                  <>
                    {/* Top Left Badge - Responsive sizing */}
                    <div className="absolute top-3 left-3 lg:top-6 lg:left-6 bg-white/95 backdrop-blur-sm text-[#171717] px-2.5 py-2 lg:px-4 lg:py-3 rounded-lg lg:rounded-xl shadow-lg max-w-[140px] lg:max-w-[180px] animate-[fadeSlideIn_0.4s_ease-out_0.1s_both]">
                      <div className="flex items-center gap-1.5 lg:gap-2 mb-1 lg:mb-1.5">
                        <span className="w-5 h-5 lg:w-6 lg:h-6 bg-[#171717] text-white rounded-full text-[11px] lg:text-sm flex items-center justify-center font-semibold">1</span>
                        <span className="text-[11px] lg:text-sm font-bold">{config.gallery?.slotOverrides?.tips?.pills?.[0]?.title || 'Secure Your Crypto'}</span>
                      </div>
                      <p className="text-[10px] lg:text-xs text-gray-600 leading-snug">{config.gallery?.slotOverrides?.tips?.pills?.[0]?.description || 'EAL6+ certified security chip'}</p>
                    </div>
                    {/* Right Badge - Responsive sizing - Positioned at bottom third to avoid covering face */}
                    <div className="absolute bottom-1/3 right-3 lg:right-6 bg-white/95 backdrop-blur-sm text-[#171717] px-2.5 py-2 lg:px-4 lg:py-3 rounded-lg lg:rounded-xl shadow-lg max-w-[140px] lg:max-w-[180px] animate-[fadeSlideIn_0.4s_ease-out_0.25s_both]">
                      <div className="flex items-center gap-1.5 lg:gap-2 mb-1 lg:mb-1.5">
                        <span className="w-5 h-5 lg:w-6 lg:h-6 bg-[#171717] text-white rounded-full text-[11px] lg:text-sm flex items-center justify-center font-semibold">2</span>
                        <span className="text-[11px] lg:text-sm font-bold">{config.gallery?.slotOverrides?.tips?.pills?.[1]?.title || 'Multi-Chain'}</span>
                      </div>
                      <p className="text-[10px] lg:text-xs text-gray-600 leading-snug">{config.gallery?.slotOverrides?.tips?.pills?.[1]?.description || 'Multi-chain support for all your assets'}</p>
                    </div>
                    {/* Bottom Left Badge - Responsive sizing */}
                    <div className="absolute bottom-3 left-3 lg:bottom-6 lg:left-6 bg-white/95 backdrop-blur-sm text-[#171717] px-2.5 py-2 lg:px-4 lg:py-3 rounded-lg lg:rounded-xl shadow-lg max-w-[140px] lg:max-w-[180px] animate-[fadeSlideIn_0.4s_ease-out_0.4s_both]">
                      <div className="flex items-center gap-1.5 lg:gap-2 mb-1 lg:mb-1.5">
                        <span className="w-5 h-5 lg:w-6 lg:h-6 bg-[#171717] text-white rounded-full text-[11px] lg:text-sm flex items-center justify-center font-semibold">3</span>
                        <span className="text-[11px] lg:text-sm font-bold">{config.gallery?.slotOverrides?.tips?.pills?.[2]?.title || 'Wake Refreshed'}</span>
                      </div>
                      <p className="text-[10px] lg:text-xs text-gray-600 leading-snug">{config.gallery?.slotOverrides?.tips?.pills?.[2]?.description || 'Clear-eyed, pain free, ready for the day'}</p>
                    </div>
                  </>
                )}
                {/* TapSafe Recovery Pills - Gallery Item 3 */}
                {activeImageIndex === 2 && imageLoaded && (
                  <>
                    {/* Top Left Badge - TapSafe Recovery */}
                    <div className="absolute top-3 left-3 lg:top-6 lg:left-6 bg-white/95 backdrop-blur-sm text-[#171717] px-2.5 py-2 lg:px-4 lg:py-3 rounded-lg lg:rounded-xl shadow-lg max-w-[140px] lg:max-w-[180px] animate-[fadeSlideIn_0.4s_ease-out_0.1s_both]">
                      <div className="flex items-center gap-1.5 lg:gap-2 mb-1 lg:mb-1.5">
                        <span className="w-5 h-5 lg:w-6 lg:h-6 aspect-square bg-[#6a63f5] text-white rounded-full text-[11px] lg:text-sm flex items-center justify-center font-semibold flex-shrink-0">1</span>
                        <span className="text-[11px] lg:text-sm font-bold">TapSafe Recovery</span>
                      </div>
                      <p className="text-[10px] lg:text-xs text-gray-600 leading-snug">Tap your Recovery Tag to restore your wallet instantly</p>
                    </div>
                    {/* Right Badge - Passcode Lock */}
                    <div className="absolute bottom-1/3 right-3 lg:right-6 bg-white/95 backdrop-blur-sm text-[#171717] px-2.5 py-2 lg:px-4 lg:py-3 rounded-lg lg:rounded-xl shadow-lg max-w-[140px] lg:max-w-[180px] animate-[fadeSlideIn_0.4s_ease-out_0.25s_both]">
                      <div className="flex items-center gap-1.5 lg:gap-2 mb-1 lg:mb-1.5">
                        <span className="w-5 h-5 lg:w-6 lg:h-6 aspect-square bg-[#6a63f5] text-white rounded-full text-[11px] lg:text-sm flex items-center justify-center font-semibold flex-shrink-0">2</span>
                        <span className="text-[11px] lg:text-sm font-bold">Passcode Lock</span>
                      </div>
                      <p className="text-[10px] lg:text-xs text-gray-600 leading-snug">PIN protection prevents unauthorized access</p>
                    </div>
                    {/* Bottom Left Badge - Multiple Backups */}
                    <div className="absolute bottom-3 left-3 lg:bottom-6 lg:left-6 bg-white/95 backdrop-blur-sm text-[#171717] px-2.5 py-2 lg:px-4 lg:py-3 rounded-lg lg:rounded-xl shadow-lg max-w-[140px] lg:max-w-[180px] animate-[fadeSlideIn_0.4s_ease-out_0.4s_both]">
                      <div className="flex items-center gap-1.5 lg:gap-2 mb-1 lg:mb-1.5">
                        <span className="w-5 h-5 lg:w-6 lg:h-6 aspect-square bg-[#6a63f5] text-white rounded-full text-[11px] lg:text-sm flex items-center justify-center font-semibold flex-shrink-0">3</span>
                        <span className="text-[11px] lg:text-sm font-bold">Multiple Backups</span>
                      </div>
                      <p className="text-[10px] lg:text-xs text-gray-600 leading-snug">Add extra Recovery Tags for additional security</p>
                    </div>
                  </>
                )}
                {/* Bundle Overlay - Gallery Item 5 (index 4) - Mini bundle summary */}
                {activeImageIndex === 4 && imageLoaded && (
                  <>
                    {/* Bottom overlay - mini bundle card */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent pt-16 pb-4 px-4 lg:px-6 animate-[fadeSlideIn_0.3s_ease-out_0.1s_both]">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-bold text-sm lg:text-base">Complete Bundle</span>
                        <span className="bg-green-500 text-white text-[10px] lg:text-xs font-bold px-2 py-0.5 rounded-full">SAVE {pricing.discountPercent}%</span>
                      </div>
                      <div className="flex items-center gap-3 text-white/90 text-[10px] lg:text-xs">
                        <span className="flex items-center gap-1"><span className="text-green-400">✓</span> Recovery Tag <span className="text-green-400 font-semibold">FREE</span></span>
                        <span className="flex items-center gap-1"><span className="text-green-400">✓</span> Charger <span className="text-green-400 font-semibold">FREE</span></span>
                        <span className="flex items-center gap-1"><span className="text-green-400">✓</span> Pouch <span className="text-green-400 font-semibold">FREE</span></span>
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-white/50 line-through text-xs">${pricing.originalPrice}</span>
                        <span className="text-white font-bold text-base lg:text-lg">${pricing.salePrice}</span>
                        <span className="text-green-400 text-[10px] lg:text-xs font-medium">Save ${pricing.savingsAmount} + free kit</span>
                      </div>
                    </div>
                  </>
                )}
                {/* App Function Overlay - Gallery Item 6 (index 5) - Mini app showcase */}
                {activeImageIndex === 5 && imageLoaded && (
                  <>
                    {/* Bottom overlay - app features card */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent pt-16 pb-4 px-4 lg:px-6 animate-[fadeSlideIn_0.3s_ease-out_0.1s_both]">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-bold text-sm lg:text-base">Ryder App</span>
                        <span className="bg-[#6a63f5] text-white text-[10px] lg:text-xs font-bold px-2 py-0.5 rounded-full">FREE</span>
                      </div>
                      <div className="flex items-center gap-3 text-white/90 text-[10px] lg:text-xs">
                        <span className="flex items-center gap-1"><span className="text-[#a78bfa]">●</span> Portfolio</span>
                        <span className="flex items-center gap-1"><span className="text-[#a78bfa]">●</span> Multi-Chain</span>
                        <span className="flex items-center gap-1"><span className="text-[#a78bfa]">●</span> Send & Swap</span>
                      </div>
                      <p className="mt-1.5 text-white/60 text-[10px] lg:text-xs">All your coins in one place. Secured by Ryder One.</p>
                    </div>
                  </>
                )}
                {/* Engineering Detail Overlay Badges for engineering-detail image - ALTERNATE POSITIONS with animations */}
                {(galleryImages[activeImageIndex] === '/images/optimized/benefits/engineering-detail-new.png' || galleryImages[activeImageIndex] === '/images/optimized/benefits/engineering-detail-new.webp') && imageLoaded && (
                  <>
                    {/* Top Right Badge - Cotton Shell - Responsive */}
                    <div className="absolute top-3 right-3 lg:top-6 lg:right-6 bg-white/95 backdrop-blur-sm text-[#171717] px-2.5 py-2 lg:px-4 lg:py-3 rounded-lg lg:rounded-xl shadow-lg max-w-[140px] lg:max-w-[180px] animate-[fadeSlideIn_0.4s_ease-out_0.1s_both]">
                      <div className="flex items-center gap-1.5 lg:gap-2 mb-1 lg:mb-1.5">
                        <span className="w-5 h-5 lg:w-6 lg:h-6 bg-[#171717] text-white rounded-full text-[11px] lg:text-sm flex items-center justify-center font-semibold">1</span>
                        <span className="text-[11px] lg:text-sm font-bold">Cotton Shell</span>
                      </div>
                      <p className="text-[10px] lg:text-xs text-gray-600 leading-snug">100% breathable cotton exterior</p>
                    </div>
                    {/* Middle Left Badge - Microfiber Fill - Responsive */}
                    <div className="absolute top-1/2 -translate-y-1/2 left-3 lg:left-6 bg-white/95 backdrop-blur-sm text-[#171717] px-2.5 py-2 lg:px-4 lg:py-3 rounded-lg lg:rounded-xl shadow-lg max-w-[140px] lg:max-w-[180px] animate-[fadeSlideIn_0.4s_ease-out_0.25s_both]">
                      <div className="flex items-center gap-1.5 lg:gap-2 mb-1 lg:mb-1.5">
                        <span className="w-5 h-5 lg:w-6 lg:h-6 bg-[#171717] text-white rounded-full text-[11px] lg:text-sm flex items-center justify-center font-semibold">2</span>
                        <span className="text-[11px] lg:text-sm font-bold">Microfiber Fill</span>
                      </div>
                      <p className="text-[10px] lg:text-xs text-gray-600 leading-snug">5x thinner than human hair for plush feel</p>
                    </div>
                    {/* Bottom Right Badge - Structural Core - Responsive */}
                    <div className="absolute bottom-3 right-3 lg:bottom-6 lg:right-6 bg-white/95 backdrop-blur-sm text-[#171717] px-2.5 py-2 lg:px-4 lg:py-3 rounded-lg lg:rounded-xl shadow-lg max-w-[140px] lg:max-w-[180px] animate-[fadeSlideIn_0.4s_ease-out_0.4s_both]">
                      <div className="flex items-center gap-1.5 lg:gap-2 mb-1 lg:mb-1.5">
                        <span className="w-5 h-5 lg:w-6 lg:h-6 bg-[#171717] text-white rounded-full text-[11px] lg:text-sm flex items-center justify-center font-semibold">3</span>
                        <span className="text-[11px] lg:text-sm font-bold">Structural Core</span>
                      </div>
                      <p className="text-[10px] lg:text-xs text-gray-600 leading-snug">Sanforized for lasting shape retention</p>
                    </div>
                  </>
                )}
                {/* Built for Real Life Overlay Badges for washing machine image with animations */}
                {(galleryImages[activeImageIndex] === '/images/optimized/benefits/washing-machine.png' || galleryImages[activeImageIndex] === '/images/optimized/benefits/washing-machine.webp') && imageLoaded && (
                  <>
                    {/* Top Left Badge - Responsive */}
                    <div className="absolute top-3 left-3 lg:top-6 lg:left-6 bg-white/95 backdrop-blur-sm text-[#171717] px-2.5 py-2 lg:px-4 lg:py-3 rounded-lg lg:rounded-xl shadow-lg max-w-[140px] lg:max-w-[180px] animate-[fadeSlideIn_0.4s_ease-out_0.1s_both]">
                      <div className="flex items-center gap-1.5 lg:gap-2 mb-1 lg:mb-1.5">
                        <span className="w-5 h-5 lg:w-6 lg:h-6 bg-[#171717] text-white rounded-full text-[11px] lg:text-sm flex items-center justify-center font-semibold">1</span>
                        <span className="text-[11px] lg:text-sm font-bold">Machine Washable</span>
                      </div>
                      <p className="text-[10px] lg:text-xs text-gray-600 leading-snug">Toss it in, comes out perfect</p>
                    </div>
                    {/* Right Badge - Responsive */}
                    <div className="absolute top-1/3 right-3 lg:right-6 bg-white/95 backdrop-blur-sm text-[#171717] px-2.5 py-2 lg:px-4 lg:py-3 rounded-lg lg:rounded-xl shadow-lg max-w-[140px] lg:max-w-[180px] animate-[fadeSlideIn_0.4s_ease-out_0.25s_both]">
                      <div className="flex items-center gap-1.5 lg:gap-2 mb-1 lg:mb-1.5">
                        <span className="w-5 h-5 lg:w-6 lg:h-6 bg-[#171717] text-white rounded-full text-[11px] lg:text-sm flex items-center justify-center font-semibold">2</span>
                        <span className="text-[11px] lg:text-sm font-bold">Shape Retention</span>
                      </div>
                      <p className="text-[10px] lg:text-xs text-gray-600 leading-snug">Maintains loft after every wash</p>
                    </div>
                    {/* Bottom Left Badge - Responsive */}
                    <div className="absolute bottom-3 left-3 lg:bottom-6 lg:left-6 bg-white/95 backdrop-blur-sm text-[#171717] px-2.5 py-2 lg:px-4 lg:py-3 rounded-lg lg:rounded-xl shadow-lg max-w-[140px] lg:max-w-[180px] animate-[fadeSlideIn_0.4s_ease-out_0.4s_both]">
                      <div className="flex items-center gap-1.5 lg:gap-2 mb-1 lg:mb-1.5">
                        <span className="w-5 h-5 lg:w-6 lg:h-6 bg-[#171717] text-white rounded-full text-[11px] lg:text-sm flex items-center justify-center font-semibold">3</span>
                        <span className="text-[11px] lg:text-sm font-bold">Open Source</span>
                      </div>
                      <p className="text-[10px] lg:text-xs text-gray-600 leading-snug">Open-source security without compromise</p>
                    </div>
                  </>
                )}
                {/* 5-Star Hotel Comfort Overlay - Three icon cards at bottom */}
                {(galleryImages[activeImageIndex] === '/images/optimized/benefits/hotel-comfort-packaging.png' || galleryImages[activeImageIndex] === '/images/optimized/benefits/hotel-comfort-packaging.webp' || galleryImages[activeImageIndex] === '/images/optimized/events/black-friday-packaging.png' || galleryImages[activeImageIndex] === '/images/optimized/events/black-friday-packaging.webp') && imageLoaded && (
                  <>
                    {/* Top Banner - Main Headline with The Block Badge */}
                    <div className="absolute top-4 left-0 right-0 text-center animate-[fadeSlideIn_0.3s_ease-out_0.1s_both]">
                      <h3 className={`text-lg lg:text-2xl font-bold tracking-tight ${
                        (galleryImages[activeImageIndex] === '/images/optimized/events/black-friday-packaging.png' || galleryImages[activeImageIndex] === '/images/optimized/events/black-friday-packaging.webp')
                          ? 'text-white' 
                          : 'text-[#171717]'
                      }`}>Military-Grade Security at Home</h3>

                    </div>
                    
                    {/* Three Icon Cards Row - Bottom area */}
                    <div className="absolute bottom-4 left-4 right-4 flex justify-between gap-2 lg:gap-4">
                      {/* Open Source Security */}
                      <div className="flex flex-col items-center text-center flex-1 animate-[fadeSlideIn_0.4s_ease-out_0.15s_both]">
                        <div className="w-8 h-8 lg:w-10 lg:h-10 mb-1 text-[#d4a5a5]">
                          <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full"><path d="M8.5 5A1.5 1.5 0 0 0 7 6.5c0 .5.2.9.5 1.2L9 9l-1.5 1.3c-.3.3-.5.7-.5 1.2a1.5 1.5 0 0 0 3 0c0-.5-.2-.9-.5-1.2L8 9l1.5-1.3c.3-.3.5-.7.5-1.2A1.5 1.5 0 0 0 8.5 5zM12 12c-1.1 0-2 .9-2 2v6c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2v-6c0-1.1-.9-2-2-2h-8z"/></svg>
                        </div>
                        <span className={`text-[10px] lg:text-xs font-semibold leading-tight ${
                          (galleryImages[activeImageIndex] === '/images/optimized/events/black-friday-packaging.png' || galleryImages[activeImageIndex] === '/images/optimized/events/black-friday-packaging.webp')
                            ? 'text-white' 
                            : 'text-[#171717]'
                        }`}>Ethical Down</span>
                        <span className={`text-[10px] lg:text-xs font-semibold leading-tight ${
                          (galleryImages[activeImageIndex] === '/images/optimized/events/black-friday-packaging.png' || galleryImages[activeImageIndex] === '/images/optimized/events/black-friday-packaging.webp')
                            ? 'text-white' 
                            : 'text-[#171717]'
                        }`}>Alternative</span>
                      </div>
                      {/* 300 Thread Count */}
                      <div className="flex flex-col items-center text-center flex-1 animate-[fadeSlideIn_0.4s_ease-out_0.25s_both]">
                        <div className="w-8 h-8 lg:w-10 lg:h-10 mb-1 text-[#d4a5a5]">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-full h-full"><rect x="4" y="6" width="16" height="12" rx="2"/><line x1="4" y1="10" x2="20" y2="10"/></svg>
                        </div>
                        <span className={`text-[10px] lg:text-xs font-semibold leading-tight ${
                          (galleryImages[activeImageIndex] === '/images/optimized/events/black-friday-packaging.png' || galleryImages[activeImageIndex] === '/images/optimized/events/black-friday-packaging.webp')
                            ? 'text-white' 
                            : 'text-[#171717]'
                        }`}>300 Thread</span>
                        <span className={`text-[10px] lg:text-xs font-semibold leading-tight ${
                          (galleryImages[activeImageIndex] === '/images/optimized/events/black-friday-packaging.png' || galleryImages[activeImageIndex] === '/images/optimized/events/black-friday-packaging.webp')
                            ? 'text-white' 
                            : 'text-[#171717]'
                        }`}>Count Cotton</span>
                      </div>
                      {/* 5-Star Quality */}
                      <div className="flex flex-col items-center text-center flex-1 animate-[fadeSlideIn_0.4s_ease-out_0.35s_both]">
                        <div className="w-8 h-8 lg:w-10 lg:h-10 mb-1 text-[#d4a5a5]">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-full h-full"><path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6M9 9h.01M15 9h.01M9 13h.01M15 13h.01"/></svg>
                        </div>
                        <span className={`text-[10px] lg:text-xs font-semibold leading-tight ${
                          (galleryImages[activeImageIndex] === '/images/optimized/events/black-friday-packaging.png' || galleryImages[activeImageIndex] === '/images/optimized/events/black-friday-packaging.webp')
                            ? 'text-white' 
                            : 'text-[#171717]'
                        }`}>5-Star Luxury</span>
                        <span className={`text-[10px] lg:text-xs font-semibold leading-tight ${
                          (galleryImages[activeImageIndex] === '/images/optimized/events/black-friday-packaging.png' || galleryImages[activeImageIndex] === '/images/optimized/events/black-friday-packaging.webp')
                            ? 'text-white' 
                            : 'text-[#171717]'
                        }`}>Quality</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
              {/* Thumbnail container with hidden scrollbar and gradient indicators */}
              <div className="relative -mx-0 lg:mx-0">
                {/* Left gradient indicator - adapts to event color scheme */}
                <div 
                  className="absolute left-0 top-0 bottom-2 w-12 pointer-events-none z-10 opacity-0 transition-opacity duration-200" 
                  id="thumb-gradient-left"
                  style={{ background: `linear-gradient(to right, ${isEventPage && eventColors ? eventColors.background : 'var(--background)'}, transparent)` }}
                />
                {/* Right gradient indicator - adapts to event color scheme */}
                <div 
                  className="absolute right-0 top-0 bottom-2 w-12 pointer-events-none z-10" 
                  id="thumb-gradient-right"
                  style={{ background: `linear-gradient(to left, ${isEventPage && eventColors ? eventColors.background : 'var(--background)'}, transparent)` }}
                />
                <div 
                  className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide px-4 md:px-0"
                  onScroll={(e) => {
                    const el = e.currentTarget;
                    const leftGradient = document.getElementById('thumb-gradient-left');
                    const rightGradient = document.getElementById('thumb-gradient-right');
                    
                    // Only show gradients if content actually overflows
                    const hasOverflow = el.scrollWidth > el.clientWidth;
                    
                    if (!hasOverflow) {
                      // No overflow - hide both gradients
                      if (leftGradient) leftGradient.style.opacity = '0';
                      if (rightGradient) rightGradient.style.opacity = '0';
                    } else {
                      // Has overflow - show gradients based on scroll position
                      if (leftGradient) leftGradient.style.opacity = el.scrollLeft > 10 ? '1' : '0';
                      if (rightGradient) rightGradient.style.opacity = el.scrollLeft < el.scrollWidth - el.clientWidth - 10 ? '1' : '0';
                    }
                  }}
                >
                  {galleryImages.map((img, idx) => {
                    // Extract src from image object or use string directly
                    const imgSrc = typeof galleryImages[idx] === 'string' ? galleryImages[idx] : ((galleryImages[idx] as any).src || galleryImages[idx]);
                    return (
                    <button
                      key={idx}
                      onClick={() => navigateGallery(idx)}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                        activeImageIndex === idx ? 'border-foreground' : 'border-border/50 hover:border-muted-foreground'
                      }`}
                    >
                      {/* Thumbnail with dynamic image and tag indicator - detect comparison slides by content */}
                      {imgSrc === 'comparison' ? (
                        <ComparisonThumbnail />
                      ) : imgSrc === 'hotel-comparison' ? (
                        <HotelComparisonThumbnail />
                      ) : imgSrc.startsWith('component:') ? (
                        <div className="w-full h-full relative bg-gradient-to-br from-[#2d1b69] to-[#1a1a2e] flex items-center justify-center">
                          <svg className="w-7 h-7 text-white/90" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M7 7h10v10H7z" />
                            <path d="M12 3v4M12 17v4M3 12h4M17 12h4" />
                          </svg>
                          <div className="absolute bottom-0 right-0 bg-white/90 rounded-tl-lg px-1.5 py-1">
                            <span className="text-[7px] font-semibold text-[#171717]">tags</span>
                          </div>
                        </div>
                      ) : (imgSrc.endsWith('.mp4') || imgSrc.includes('.mp4')) ? (
                        <div className="w-full h-full relative bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f]">
                          {/* Video thumbnail - use posterSrc if available, otherwise gradient + play icon */}
                          {config.gallery?.images?.[idx]?.posterSrc ? (
                            <img
                              src={config.gallery.images[idx].posterSrc}
                              alt={config.gallery.images[idx].alt || ''}
                              className="w-full h-full object-cover"
                            />
                          ) : null}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-8 h-8 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center">
                              <svg className="w-4 h-4 text-white ml-0.5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                              </svg>
                            </div>
                          </div>
                          {/* Contextual label for video thumbnails */}
                          <div className="absolute bottom-0 right-0 bg-white/90 rounded-tl-lg px-1.5 py-1">
                            <span className="text-[7px] font-semibold text-[#171717]">{idx === 1 ? 'setup' : 'send'}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="w-full h-full relative">
                          <img 
                            src={getOptimizedGalleryUrl(imgSrc, 'mobile')} 
                            srcSet={getThumbnailSrcSet(imgSrc) || getOptimizedGallerySrcSet(imgSrc)}
                            sizes="64px"
                            alt="" 
                            className="w-full h-full object-cover"
                            width={64}
                            height={64}
                            loading="lazy"
                            decoding="async"
                          />
                          {/* Contextual pills for Ryder gallery thumbnails */}
                          {idx === 0 && (
                            <div className="absolute bottom-0 right-0 bg-white/90 rounded-tl-lg px-1.5 py-1">
                              <span className="text-[7px] font-semibold text-[#171717]">wallet</span>
                            </div>
                          )}
                          {idx === 2 && (
                            <div className="absolute bottom-0 right-0 bg-white/90 rounded-tl-lg px-1.5 py-1">
                              <span className="text-[7px] font-semibold text-[#171717]">recovery</span>
                            </div>
                          )}
                          {idx === 4 && (
                            <div className="absolute bottom-0 right-0 bg-white/90 rounded-tl-lg px-1.5 py-1">
                              <span className="text-[7px] font-semibold text-[#171717]">bundle</span>
                            </div>
                          )}
                          {idx === 5 && (
                            <div className="absolute bottom-0 right-0 bg-white/90 rounded-tl-lg px-1.5 py-1">
                              <span className="text-[7px] font-semibold text-[#171717]">app</span>
                            </div>
                          )}
                        </div>
                      )}
                    </button>
                  );
                  })}
                </div>
              </div>
            </div>

            {/* RIGHT: Product Info */}
            <div className="space-y-4 px-4 md:px-0 overflow-hidden md:overflow-visible max-w-full" id="hero-pricing" data-hero-section>
              <div className="flex flex-wrap items-center gap-2 md:gap-3" style={{marginBottom: '12px'}}>
                {/* Hotel Quality: Show 5-Star Secret badge instead of stars */}
                {config.id === 'hotel-quality' ? (
                  <div className="flex items-center gap-2 relative">
                    <div className="flex items-center gap-1.5 px-2 py-1 bg-[#171717]/5 rounded-full">
                      <svg className="w-3.5 h-3.5 text-[#c9a962]" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                      <span className="text-xs font-semibold text-[#171717]">The 5-Star Secret</span>
                    </div>
                    <span className="text-xs text-muted-foreground">(4.7/5) <CustomerCount responsive /> Happy Users</span>

                  </div>
                ) : (
                  <button 
                    onClick={() => scrollToSection('reviews')}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-[#f59e0b] text-[#f59e0b]" />
                      ))}
                    </div>
                    <span className="text-sm text-muted-foreground">(4.7/5) <CustomerCount responsive /> Happy Users</span>
                  </button>
                )}

              </div>

              <div>
                <h1
                  className="text-3xl md:text-4xl font-semibold tracking-tight leading-tight"
                  style={isEventPage ? { color: eventColors?.primary } : {}}
                >
                  {isBtcOffer ? config.hero.headline : 'Ryder One – Your Crypto, Your Control'}
                </h1>
                {/* Product Tags - Inline under title (desktop only) with hover cards */}
                <div className="mt-2 hidden md:flex flex-wrap items-center gap-2">
                  {[
                    { label: 'Self-Custody', title: 'True Self-Custody', description: 'You hold the keys. No third-party custodians, no intermediaries. Your crypto stays under your complete control, just as Satoshi intended.' },
                    { label: 'Multi-Chain', title: 'Multi-Chain Native', description: 'Bitcoin, Ethereum, Stacks, and Solana supported natively, plus top ERC-20 and SPL tokens. One wallet for your entire portfolio.' },
                    { label: 'Secure', title: 'Bank-Level Security', description: 'EAL6+ certified secure element with passcode lock and offline signing. Military-grade protection without the complexity.' },
                    { label: 'Easy to Use', title: 'Actually Easy to Use', description: 'Physical confirmation button, intuitive interface, and seamless mobile app. No command lines, no seed phrase memorization, just simple, secure transactions.' }
                  ].map((tag) => (
                    <div key={tag.label} className="relative group">
                      <span 
                        className="text-xs px-2.5 py-1 rounded-full cursor-pointer transition-all"
                        style={{
                          backgroundColor: isEventPage && eventColors ? eventColors.secondaryLight : '#6a63f515',
                          color: isEventPage && eventColors ? (isDarkScheme ? eventColors.secondary : eventColors.primary) : '#6a63f5'
                        }}
                      >
                        {tag.label}
                      </span>
                      {/* Hover card - desktop */}
                      <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-64 p-3 bg-white rounded-lg shadow-xl border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 pointer-events-none">
                        <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white border-l border-t border-gray-200 rotate-45"></div>
                        <h4 className="font-semibold text-sm text-foreground mb-1">{tag.title}</h4>
                        <p className="text-xs text-muted-foreground leading-relaxed">{tag.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Made for Use Case Badge - Only for specific use-case pages, not quiz */}
                {useCaseName && !quizLabels.length && (
                  <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 bg-[#1d9bf0]/10 rounded-full">
                    <span className="w-3 h-3 rounded-full bg-[#1d9bf0] flex items-center justify-center flex-shrink-0">
                      <Check className="w-2 h-2 text-white stroke-[3]" />
                    </span>
                    <span className="text-xs font-semibold text-[#1d9bf0]">
                      Made for {useCaseName}
                    </span>
                  </div>
                )}
                <p className="mt-3 text-muted-foreground leading-relaxed">
                  {config.hero.subheadline}
                </p>
                {/* Angle-based emphasis for event pages */}
                {isEventPage && currentAngleModifier.heroEmphasis && (
                  <p className="mt-2 text-sm font-medium" style={{ color: eventColors?.primary }}>
                    {currentAngleModifier.heroEmphasis}
                  </p>
                )}
                {/* Hotel Quality additional reinforcement */}
                {config.id === 'hotel-quality' && (
                  <p className="mt-2 text-sm font-medium text-green-700">
                    Same suppliers as Four Seasons & Ritz-Carlton, 50%+ less.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                {config.hero.usps
                  .map(usp => {
                    // Replace "Made in USA" / "Made in the USA" with alternative for non-US visitors
                    if (!isUS && (usp.title.toLowerCase().includes('made in usa') || usp.title.toLowerCase().includes('made in the usa'))) {
                      return { ...usp, title: nonUsAlternativeUsp.title, description: nonUsAlternativeUsp.description };
                    }
                    return usp;
                  })
                  .map((usp, idx) => {
                  const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
                    'support': Moon,
                    'temperature': Thermometer,
                    'hypoallergenic': Shield,
                    'location': MapPin,
                    'shield': Shield,
                    'clock': Clock,
                    'key': Key,
                  };
                  const IconComponent = iconMap[usp.icon] || Moon;
                  return (
                    <div key={idx} className="flex items-start gap-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                        style={isEventPage ? { backgroundColor: `${eventColors?.primary}1a` } : { backgroundColor: '#6a63f515' }}
                      >
                        <span style={isEventPage ? { color: eventColors?.primary } : { color: '#6a63f5' }}>
                          <IconComponent className="w-5 h-5" />
                        </span>
                      </div>
                      <div>
                        <div className="font-medium text-sm">{usp.title}</div>
                        <div className="text-xs text-muted-foreground">{usp.description}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Contextual Carousel - Only shown when user has quiz tags */}
              {(() => {
                // Build contextual cards based on quiz tags
                const contextualCards: { id: string; title: string; description: string; image: string; icon: React.ReactNode }[] = [];
                
                // Sleep position card - only show user's specific position
                if (quizTags.includes('side-sleeper')) {
                  contextualCards.push({
                    id: 'side-sleeper',
                    title: 'Made for Side Sleepers',
                    description: 'Higher loft fills the shoulder gap, keeping your spine perfectly aligned all night.',
                    image: '/images/optimized/cards/sleep-position-side.png',
                    icon: <Bed className="w-4 h-4" />
                  });
                } else if (quizTags.includes('back-sleeper')) {
                  contextualCards.push({
                    id: 'back-sleeper',
                    title: 'Perfect for Back Sleepers',
                    description: 'Cradles your neck curve with just the right support for natural spinal alignment.',
                    image: '/images/optimized/cards/sleep-position-back.png',
                    icon: <Bed className="w-4 h-4" />
                  });
                } else if (quizTags.includes('stomach-sleeper')) {
                  contextualCards.push({
                    id: 'stomach-sleeper',
                    title: 'Designed for Stomach Sleepers',
                    description: 'Low profile support prevents neck strain while keeping you comfortable face-down.',
                    image: '/images/optimized/cards/sleep-position-stomach.png',
                    icon: <Bed className="w-4 h-4" />
                  });
                } else if (quizTags.includes('mixed-sleeper')) {
                  contextualCards.push({
                    id: 'mixed-sleeper',
                    title: 'Adapts to Any Position',
                    description: 'Responsive fill adjusts as you move, providing perfect support in every position.',
                    image: '/images/optimized/cards/sleep-alignment.png',
                    icon: <RefreshCw className="w-4 h-4" />
                  });
                }
                
                // Note: Pillow replacement messaging is now shown in a separate section above the CTA button
                
                // Neck pain card
                if (quizTags.includes('neck-pain') || quizTags.includes('alignment-issues')) {
                  contextualCards.push({
                    id: 'neck-support',
                    title: 'Neck Pain Relief',
                    description: 'Proper cervical alignment eliminates morning stiffness. Wake up without that familiar ache.',
                    image: '/images/optimized/cards/card-neck-relief.png',
                    icon: <Shield className="w-4 h-4" />
                  });
                }
                
                // Shoulder pain card
                if (quizTags.includes('shoulder-pain') || quizTags.includes('circulation-issues')) {
                  contextualCards.push({
                    id: 'shoulder-relief',
                    title: 'Shoulder Pressure Relief',
                    description: 'Contoured support reduces pressure points, preventing that 3am wake-up to switch sides.',
                    image: '/images/optimized/cards/card-shoulder-relief.png',
                    icon: <Heart className="w-4 h-4" />
                  });
                }
                
                // Temperature card
                if (quizTags.includes('hot-sleeper') || quizTags.includes('temperature') || quizTags.includes('warm-sleeper')) {
                  contextualCards.push({
                    id: 'cooling',
                    title: 'Cooling Technology',
                    description: 'Breathable materials and airflow design keep you cool all night. No more flipping to the cool side.',
                    image: '/images/optimized/cards/card-cooling-closeup.png',
                    icon: <Thermometer className="w-4 h-4" />
                  });
                }
                
                // Allergy card
                if (quizTags.includes('allergies') || quizTags.includes('dust-mites') || quizTags.includes('congested')) {
                  contextualCards.push({
                    id: 'hypoallergenic',
                    title: 'Hypoallergenic Protection',
                    description: 'Dust mite resistant materials reduce allergens by up to 80%. Breathe easier, sleep better.',
                    image: '/images/optimized/cards/card-hypoallergenic.png',
                    icon: <Shield className="w-4 h-4" />
                  });
                }
                
                // Support issues card
                if (quizTags.includes('poor-support') || quizTags.includes('compression') || quizTags.includes('severe-support-issues')) {
                  contextualCards.push({
                    id: 'lasting-support',
                    title: 'Lasting Support',
                    description: 'Premium fill maintains shape night after night. No more constant fluffing or adjusting.',
                    image: '/images/optimized/cards/card-lasting-support.png',
                    icon: <Layers className="w-4 h-4" />
                  });
                }
                
                // Hotel quality card
                if (quizTags.includes('hotel-quality') || quizTags.includes('pillow-issue')) {
                  contextualCards.push({
                    id: 'hotel-quality',
                    title: 'Hotel-Grade Quality',
                    description: 'Same suppliers as Four Seasons and Ritz-Carlton. Luxury hotel sleep at a fraction of the price.',
                    image: '/images/optimized/cards/card-hotel-quality.png',
                    icon: <Star className="w-4 h-4" />
                  });
                }
                
                // Only render if we have cards
                if (contextualCards.length === 0) return null;
                
                // Only enable auto-switch if more than 3 cards
                const shouldAnimate = contextualCards.length > 3;
                
                // Update cards count for auto-switch (only if animating)
                const totalSlides = contextualCards.length;
                if (shouldAnimate && totalSlides !== contextualCardsCount) {
                  setTimeout(() => setContextualCardsCount(totalSlides), 0);
                }
                
                // Calculate visible cards based on screen size
                // Desktop: 3 cards, Mobile: 2 cards
                // If 3 or fewer cards, show all without animation
                const visibleStartIndex = shouldAnimate ? (contextualCarouselIndex % contextualCards.length) : 0;
                
                // Get cards to display
                const visibleCards = shouldAnimate 
                  ? [
                      contextualCards[visibleStartIndex],
                      contextualCards[(visibleStartIndex + 1) % contextualCards.length],
                      contextualCards[(visibleStartIndex + 2) % contextualCards.length]
                    ].filter(Boolean)
                  : contextualCards.slice(0, 3);
                
                // Swipe/drag handlers for carousel
                const handleDragStart = (clientX: number) => {
                  if (!shouldAnimate) return;
                  setIsDragging(true);
                  setDragStartX(clientX);
                  setDragCurrentX(clientX);
                };
                
                const handleDragMove = (clientX: number) => {
                  if (!isDragging) return;
                  setDragCurrentX(clientX);
                };
                
                const handleDragEnd = () => {
                  if (!isDragging) return;
                  
                  // Skip if in cooldown period
                  if (swipeCooldownRef.current) {
                    setIsDragging(false);
                    setDragStartX(0);
                    setDragCurrentX(0);
                    return;
                  }
                  
                  const diff = dragStartX - dragCurrentX;
                  const threshold = 50; // Minimum drag distance to trigger slide change
                  
                  if (Math.abs(diff) > threshold) {
                    // Set cooldown to prevent rapid triggers
                    swipeCooldownRef.current = true;
                    
                    if (diff > 0) {
                      // Swiped left - go to next
                      setSlideDirection('left');
                      setContextualCarouselIndex((prev) => (prev + 1) % contextualCards.length);
                    } else {
                      // Swiped right - go to previous
                      setSlideDirection('right');
                      setContextualCarouselIndex((prev) => (prev - 1 + contextualCards.length) % contextualCards.length);
                    }
                    
                    // Reset direction after animation and clear cooldown
                    setTimeout(() => {
                      setSlideDirection(null);
                      swipeCooldownRef.current = false;
                    }, 500);
                  }
                  
                  setIsDragging(false);
                  setDragStartX(0);
                  setDragCurrentX(0);
                };
                
                // Wheel handler for Apple Magic Mouse / trackpad horizontal swipe
                const handleWheel = (e: React.WheelEvent) => {
                  if (!shouldAnimate) return;
                  
                  // Skip if in cooldown period (prevents rapid multiple triggers)
                  if (swipeCooldownRef.current) return;
                  
                  // Check if this is a horizontal scroll (Magic Mouse/trackpad)
                  const isHorizontalScroll = Math.abs(e.deltaX) > Math.abs(e.deltaY);
                  if (!isHorizontalScroll) return;
                  
                  // Prevent page scroll when swiping carousel
                  e.preventDefault();
                  
                  const now = Date.now();
                  const timeSinceLastWheel = now - lastWheelTimeRef.current;
                  
                  // Reset accumulator if too much time has passed (gesture ended)
                  if (timeSinceLastWheel > 200) {
                    wheelAccumulatorRef.current = 0;
                  }
                  
                  lastWheelTimeRef.current = now;
                  wheelAccumulatorRef.current += e.deltaX;
                  
                  const threshold = 100; // Accumulated scroll needed to trigger slide change
                  
                  if (Math.abs(wheelAccumulatorRef.current) > threshold) {
                    // Set cooldown to prevent rapid triggers
                    swipeCooldownRef.current = true;
                    
                    if (wheelAccumulatorRef.current > 0) {
                      // Scrolled right - go to next
                      setSlideDirection('left');
                      setContextualCarouselIndex((prev) => (prev + 1) % contextualCards.length);
                    } else {
                      // Scrolled left - go to previous
                      setSlideDirection('right');
                      setContextualCarouselIndex((prev) => (prev - 1 + contextualCards.length) % contextualCards.length);
                    }
                    
                    // Reset direction after animation and clear cooldown
                    setTimeout(() => {
                      setSlideDirection(null);
                      swipeCooldownRef.current = false;
                    }, 500);
                    wheelAccumulatorRef.current = 0;
                  }
                };
                
                return (
                  <div className="mt-5">
                    {/* Carousel Container - Refined design matching page aesthetic */}
                    <div 
                      className="relative overflow-hidden rounded-xl bg-secondary/30 border border-border select-none"
                      style={{ cursor: shouldAnimate ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
                      onMouseDown={(e) => handleDragStart(e.clientX)}
                      onMouseMove={(e) => handleDragMove(e.clientX)}
                      onMouseUp={handleDragEnd}
                      onMouseLeave={handleDragEnd}
                      onTouchStart={(e) => handleDragStart(e.touches[0].clientX)}
                      onTouchMove={(e) => handleDragMove(e.touches[0].clientX)}
                      onTouchEnd={handleDragEnd}
                      onWheel={handleWheel}
                    >
                      {/* Title Header - Clean, minimal */}
                      <div className="px-4 pt-4 pb-3 border-b border-border/50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-1 h-4 bg-[#1d9bf0] rounded-full"></div>
                            <h4 className="text-sm font-semibold text-foreground">Why Ryder One is Right for You</h4>
                          </div>
                          <span className="text-xs text-muted-foreground bg-background/50 px-2 py-1 rounded-full">Based on your profile</span>
                        </div>
                      </div>
                      
                      {/* Cards Grid - Responsive based on card count */}
                      <div className="p-4">
                        <div className={`grid gap-3 ${
                          visibleCards.length === 1 
                            ? 'grid-cols-1' 
                            : visibleCards.length === 2 
                              ? 'grid-cols-2' 
                              : 'grid-cols-2 md:grid-cols-3'
                        }`}>
                          {visibleCards.map((card, idx) => (
                            <div 
                              key={`${card.id}-${shouldAnimate ? contextualCarouselIndex : 'static'}-${idx}`} 
                              className={`group flex flex-col rounded-lg border border-border bg-background overflow-hidden hover:border-[#1d9bf0]/30 transition-all duration-200 ${
                                visibleCards.length >= 3 && idx === 2 ? 'hidden md:flex' : ''
                              }`}
                              style={shouldAnimate ? { 
                                animation: `${slideDirection === 'right' ? 'slideInFromLeft' : 'slideInFromRight'} 0.4s ease-out forwards`,
                                animationDelay: `${idx * 0.05}s`,
                                opacity: 0
                              } : {}}
                            >
                              {/* Image - Compact with subtle overlay */}
                              <div className="relative w-full h-24 md:h-28 overflow-hidden bg-secondary">
                                <img 
                                  src={card.image} 
                                  alt={card.title}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                />
                                {/* Subtle gradient overlay */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
                              </div>
                              {/* Content */}
                              <div className="p-3">
                                <h4 className="text-xs font-semibold text-foreground leading-tight mb-1">{card.title}</h4>
                                <p className="text-[11px] text-muted-foreground leading-relaxed line-clamp-4">{card.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Dots Navigation - Refined style */}
                      {shouldAnimate && (
                        <div 
                          className="flex justify-center gap-1.5 pb-4"
                          onMouseDown={(e) => e.stopPropagation()}
                          onTouchStart={(e) => e.stopPropagation()}
                        >
                          {contextualCards.map((_, idx) => (
                            <button
                              key={idx}
                              onClick={(e) => {
                                e.stopPropagation();
                                setContextualCarouselIndex(idx);
                              }}
                              className={`h-1.5 rounded-full transition-all duration-300 ${
                                idx === contextualCarouselIndex 
                                  ? 'bg-[#1d9bf0] w-4' 
                                  : 'bg-border hover:bg-muted-foreground/50 w-1.5'
                              }`}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}



              {/* Bundle Showcase - What's Included */}
              <div data-product-selector>
                {isBtcOffer ? <BtcBundleShowcase /> : <BundleShowcase />}
              </div>

              {/* Why You Need a Hardware Wallet + CTA - Unified element for quiz users with old pillow */}
              {(() => {
                const urlParams = new URLSearchParams(window.location.search);
                const quizTagsForReplace = urlParams.get('tags')?.split(',').filter(Boolean) || [];
                const needsReplacement = quizTagsForReplace.includes('old-pillow') || quizTagsForReplace.includes('very-old-pillow') || quizTagsForReplace.includes('danger');
                
                if (!needsReplacement) return null;
                
                return (
                  <div className="rounded-xl overflow-hidden bg-secondary/30 border border-border">
                    {/* Why You Need a Hardware Wallet - Top part */}
                    <div className="px-4 pt-4 pb-3 border-b border-border/50">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-1 h-4 bg-[#1d9bf0] rounded-full"></div>
                          <h4 className="text-sm font-semibold text-foreground">Why You Need a Hardware Wallet</h4>
                        </div>
                        {/* Alarm-style pillow age badge - light background with black text */}
                        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                          quizTagsForReplace.includes('very-old-pillow') 
                            ? 'bg-[#6a63f5]/10 text-foreground border border-[#6a63f5]/20' 
                            : 'bg-[#f5c542]/10 text-foreground border border-[#f5c542]/20'
                        }`}>
                          <span className={`w-2 h-2 rounded-full animate-pulse ${
                            quizTagsForReplace.includes('very-old-pillow') ? 'bg-[#6a63f5]' : 'bg-[#f5c542]'
                          }`}></span>
                          {quizTagsForReplace.includes('very-old-pillow') ? '5+ years old' : '2-5 years old'}
                        </div>
                      </div>
                    </div>
                    {/* Content section - Self-custody focused */}
                    <div className="p-4">
                      <div className="space-y-2.5">
                        <div className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-[#1d9bf0] flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-foreground/80"><span className="font-semibold">Not your keys, not your crypto</span> – Exchange hacks have lost billions in user funds</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-[#1d9bf0] flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-foreground/80"><span className="font-semibold">Software wallets are vulnerable</span> – Malware and phishing target hot wallets daily</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-[#1d9bf0] flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-foreground/80"><span className="font-semibold">True ownership requires offline keys</span> – Hardware wallets keep your keys safe from online threats</p>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border/50">Self-custody means you hold your own private keys. Ryder One makes this simple with TapSafe Recovery, so you never have to choose between security and convenience.</p>
                    </div>
                    {/* CTA Button - Bottom part, visually attached */}
                    <div className="px-4 pb-4">
                      <button 
                        id="main-cta-button" 
                        onClick={() => {
                          const productData = getProductDataForTracking(pricing.scenarioId);
                          trackAddToCart(productData);
                          
                          // GA4: Track add_to_cart
                          const ga4Item = createMainProductItem('standard', 1, pricing.salePrice, pricing.originalPrice);
                          trackGA4AddToCart([ga4Item], pricing.salePrice);
                          
                          // Redirect to checkout popup
                          setShowOrderPopup(true);
                        }}
                        className="w-full text-white font-semibold py-4 rounded-full text-lg transition-all duration-300 animate-cta-shimmer hover:-translate-y-px active:translate-y-0"
                        style={{ 
                          backgroundColor: isEventPage && eventColors ? eventColors.primary : '#6a63f5',
                          boxShadow: `0 2px 8px ${isEventPage && eventColors ? eventColors.primary : '#6a63f5'}40`,
                          ['--event-primary' as string]: isEventPage && eventColors ? eventColors.primary : '#6a63f5'
                        }}
                        onMouseEnter={(e) => {
                          const color = isEventPage && eventColors ? eventColors.primary : '#6a63f5';
                          e.currentTarget.style.boxShadow = `0 4px 20px ${color}70`;
                          e.currentTarget.style.filter = 'brightness(0.95)';
                        }}
                        onMouseLeave={(e) => {
                          const color = isEventPage && eventColors ? eventColors.primary : '#6a63f5';
                          e.currentTarget.style.boxShadow = `0 2px 8px ${color}40`;
                          e.currentTarget.style.filter = 'brightness(1)';
                        }}
                      >
                        {getPageCTAWithDiscount(actualDiscount)}
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* Regular CTA button - Only show when pillow replacement section is not shown */}
              {(() => {
                const urlParams = new URLSearchParams(window.location.search);
                const quizTagsForReplace = urlParams.get('tags')?.split(',').filter(Boolean) || [];
                const needsReplacement = quizTagsForReplace.includes('old-pillow') || quizTagsForReplace.includes('very-old-pillow') || quizTagsForReplace.includes('danger');
                
                if (needsReplacement) return null;
                
                return (
                  <button 
                    id="main-cta-button" 
                    onClick={() => {
                      const productData = getProductDataForTracking(pricing.scenarioId);
                      trackAddToCart(productData);
                      
                      // GA4: Track add_to_cart
                      const ga4Item = createMainProductItem('standard', 1, pricing.salePrice, pricing.originalPrice);
                      trackGA4AddToCart([ga4Item], pricing.salePrice);
                      
                      // Redirect to checkout popup
                      setShowOrderPopup(true);
                    }}
                    className="w-full text-white font-semibold py-4 rounded-full text-lg transition-all duration-300 animate-cta-shimmer hover:-translate-y-px active:translate-y-0"
                    style={{ 
                  backgroundColor: isEventPage && eventColors ? eventColors.primary : '#6a63f5',
                  boxShadow: `0 2px 8px ${isEventPage && eventColors ? eventColors.primary : '#6a63f5'}40`,
                  ['--event-primary' as string]: isEventPage && eventColors ? eventColors.primary : '#6a63f5'
                }}
                onMouseEnter={(e) => {
                  const color = isEventPage && eventColors ? eventColors.primary : '#6a63f5';
                  e.currentTarget.style.boxShadow = `0 4px 20px ${color}70`;
                  e.currentTarget.style.filter = 'brightness(0.95)';
                }}
                onMouseLeave={(e) => {
                  const color = isEventPage && eventColors ? eventColors.primary : '#6a63f5';
                  e.currentTarget.style.boxShadow = `0 2px 8px ${color}40`;
                  e.currentTarget.style.filter = 'brightness(1)';
                }}
              >
                {getPageCTAWithDiscount(actualDiscount)}
              </button>
              );
            })()}

              {/* Compact urgency module - visually attached to CTA */}
              <div className="-mt-1">
                {/* Dynamic location-based shipping message - aligned left, above stock bar */}
                <div className="mb-2">
                  <ShippingMessage variant="inline" />
                </div>
                <StockInventoryBar stockLevel={stockLevel} countdown={countdown} timerExpired={timerExpired} />
                {/* FOMO counter - BTC offer only */}
                {isBtcOffer && (
                  <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-[#6a63f5]/5 border border-[#6a63f5]/15">
                    <span className="flex h-2 w-2 flex-shrink-0">
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-[#6a63f5]"></span>
                    </span>
                    <span className="text-xs text-foreground/70">
                      <span className="font-medium text-[#6a63f5]">Cashback program</span> — Available through February 2026
                    </span>
                  </div>
                )}
              </div>

              {/* Checkmark badges - scrollable on mobile with gradient fade */}
              <div className="relative -mx-4 md:mx-0">
                {/* Left gradient fade for scroll indication */}
                <div 
                  className={`absolute left-0 top-0 bottom-1 w-8 z-10 pointer-events-none transition-opacity duration-200 ${showLeftGradient ? 'opacity-100' : 'opacity-0'}`}
                  style={{ 
                    background: isEventPage && eventColors 
                      ? `linear-gradient(to right, ${eventColors.background || 'hsl(var(--background))'}, transparent)`
                      : 'linear-gradient(to right, hsl(var(--background)), transparent)'
                  }}
                />
                <div 
                  ref={trustBadgesRef}
                  className="flex items-center justify-start gap-2 text-xs text-muted-foreground overflow-x-auto scrollbar-hide pb-1 px-4 md:px-0"
                >
                  <span 
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border whitespace-nowrap flex-shrink-0 ${isEventPage && eventColors ? 'bg-white/80 dark:bg-white/10' : 'bg-secondary/30'}`}
                    style={{ 
                      borderColor: isEventPage && eventColors ? `${eventColors.primary}25` : undefined
                    }}
                  >
                    <Check className="w-3 h-3" style={{ color: isEventPage && eventColors ? eventColors.primary : '#16a34a' }} /> {siteSettings.guarantee_text}
                  </span>
                  <span 
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border whitespace-nowrap flex-shrink-0 ${isEventPage && eventColors ? 'bg-white/80 dark:bg-white/10' : 'bg-secondary/30'}`}
                    style={{ 
                      borderColor: isEventPage && eventColors ? `${eventColors.primary}25` : undefined
                    }}
                  >
                    <Check className="w-3 h-3" style={{ color: isEventPage && eventColors ? eventColors.primary : '#16a34a' }} /> {siteSettings.shipping_text}
                  </span>
                  <span 
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border whitespace-nowrap flex-shrink-0 ${isEventPage && eventColors ? 'bg-white/80 dark:bg-white/10' : 'bg-secondary/30'}`}
                    style={{ 
                      borderColor: isEventPage && eventColors ? `${eventColors.primary}25` : undefined
                    }}
                  >
                    <Check className="w-3 h-3" style={{ color: isEventPage && eventColors ? eventColors.primary : '#16a34a' }} /> {siteSettings.origin_text}
                  </span>
                </div>
                {/* Right gradient fade for scroll indication */}
                <div 
                  className={`absolute right-0 top-0 bottom-1 w-8 pointer-events-none transition-opacity duration-200 ${showRightGradient ? 'opacity-100' : 'opacity-0'}`}
                  style={{ 
                    background: isEventPage && eventColors 
                      ? `linear-gradient(to left, ${eventColors.background || 'hsl(var(--background))'}, transparent)`
                      : 'linear-gradient(to left, hsl(var(--background)), transparent)'
                  }}
                />
              </div>

              <div className="border-t border-border pt-4 space-y-0" style={{paddingTop: '0px'}}>
                {/* What's Included - First accordion item */}
                <div className="border-b border-border">
                  <button
                    onClick={() => setExpandedAccordion(expandedAccordion === 'whats-included' ? null : 'whats-included')}
                    className="w-full flex items-center justify-between py-3 text-sm font-medium"
                  >
                    What's Included
                    {expandedAccordion === 'whats-included' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {expandedAccordion === 'whats-included' && (
                    <div className="pb-3 space-y-2">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 bg-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                          <Layers className="w-3.5 h-3.5 text-muted-foreground" />
                        </div>
                        <div>
                          <span className="text-sm">Ryder One Hardware Wallet</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 bg-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                          <Gift className="w-3.5 h-3.5 text-muted-foreground" />
                        </div>
                        <div>
                          <span className="text-sm">Recovery Tag</span>
                          <span className="text-xs text-green-600 ml-1">{`(FREE - $${pricing.accessories.recoveryTag} value)`}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 bg-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                          <Gift className="w-3.5 h-3.5 text-muted-foreground" />
                        </div>
                        <div>
                          <span className="text-sm">Wireless Charger</span>
                          <span className="text-xs text-green-600 ml-1">{`(FREE - $${pricing.accessories.wirelessCharger} value)`}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 bg-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                          <Gift className="w-3.5 h-3.5 text-muted-foreground" />
                        </div>
                        <div>
                          <span className="text-sm">Travel Pouch</span>
                          <span className="text-xs text-green-600 ml-1">{`(FREE - $${pricing.accessories.travelPouch} value)`}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 bg-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                          <Shield className="w-3.5 h-3.5 text-muted-foreground" />
                        </div>
                        <span className="text-sm">{isBtcOffer ? '$200 Bitcoin Reward Included' : '30-Day Money Back Guarantee'}</span>
                      </div>
                    </div>
                  )}
                </div>
                {[
                  { id: 'care', title: 'Device Care & Maintenance', content: null },
                  { id: 'materials', title: 'Technical Specifications', content: null },
                  { id: 'returns', title: '30-Day Returns', content: null },
                  ...(isBtcOffer ? [{ id: 'btc-reward', title: 'Bitcoin Cashback Program', content: null }] : []),
                ].map((item) => (
                  <div key={item.id} className="border-b border-border">
                    <button
                      onClick={() => setExpandedAccordion(expandedAccordion === item.id ? null : item.id)}
                      className="w-full flex items-center justify-between py-3 text-sm font-medium"
                    >
                      {item.title}
                      {expandedAccordion === item.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    {expandedAccordion === item.id && (
                      <div className="pb-3 text-sm text-muted-foreground">
                        {item.id === 'care' && (
                          <div className="space-y-2">
                            <p>Keep your Ryder One in optimal condition:</p>
                            <ul className="space-y-1.5 text-sm">
                              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /> Clean with soft, dry cloth</li>
                              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /> Avoid exposure to extreme temperatures</li>
                              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /> Store in provided travel pouch when not in use</li>
                              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /> Charge regularly to maintain battery health</li>
                            </ul>
                          </div>
                        )}
                        {item.id === 'materials' && (
                          <div className="space-y-3">
                            <p className="mb-2">Enterprise-grade hardware wallet with military-level security.</p>
                            <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                              <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-600 flex-shrink-0" /> EAL6+ Certified Chip</li>
                              <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-600 flex-shrink-0" /> Multi-Chain Support</li>
                              <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-600 flex-shrink-0" /> NFC Communication</li>
                              <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-600 flex-shrink-0" /> Qi Wireless Charging</li>
                              <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-600 flex-shrink-0" /> 1.6" OLED Display</li>
                              <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-600 flex-shrink-0" /> Secure Firmware</li>
                            </ul>
                            <div className="flex flex-wrap gap-2 mt-3">
                              <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-secondary text-sm font-medium w-fit"><span className="font-semibold mr-1.5">Dimensions:</span> 41 × 55 × 14.5mm</span>
                              <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-secondary text-sm font-medium w-fit"><span className="font-semibold mr-1.5">Weight:</span> 42g</span>
                            </div>
                          </div>
                        )}
                        {item.id === 'returns' && (
                          <div className="space-y-2">
                            <p>Not satisfied? Return within 30 days for a full refund. No questions asked.</p>
                            <p className="text-xs">Simply contact our support team to initiate a return. Your Ryder One must be in original condition with all accessories included.</p>
                          </div>
                        )}
                        {item.id === 'btc-reward' && (
                          <div className="space-y-2">
                            <p>Receive $200 in Bitcoin directly to your new Ryder One wallet within 30 days of purchase.</p>
                            <ul className="space-y-1.5 text-sm">
                              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#6a63f5] flex-shrink-0 mt-0.5" /> Set up your Ryder One and create your wallet</li>
                              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#6a63f5] flex-shrink-0 mt-0.5" /> Activate your wallet through the Ryder app</li>
                              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#6a63f5] flex-shrink-0 mt-0.5" /> $200 in Bitcoin deposited to your wallet within 30 days</li>
                            </ul>
                            <p className="text-xs text-muted-foreground">Program available through February 2026. One cashback per customer. Bitcoin value calculated at market price at time of distribution.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Quiz Link - Hide when user already came from quiz */}
              {quizTags.length === 0 && (
                <div className="mt-4">
                  {/* Desktop: Single line */}
                  <button
                    onClick={() => setIsQuizModalOpen(true)}
                    className="hidden sm:inline text-sm text-muted-foreground hover:text-foreground underline underline-offset-4 transition-colors"
                  >
                    Not sure which wallet is right for you? Find out in 60 seconds →
                  </button>
                  {/* Mobile: Two lines with emphasis */}
                  <div className="sm:hidden flex flex-col items-center gap-1">
                    <span className="text-sm text-muted-foreground">Not sure which wallet is right for you?</span>
                    <button
                      onClick={() => setIsQuizModalOpen(true)}
                      className="text-sm font-medium text-foreground underline underline-offset-4 transition-colors"
                    >
                      Find out in 60 seconds →
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* PRESS BAR */}
      <section 
        className="py-8 border-y"
        style={{
          borderColor: 'var(--border)',
          backgroundColor: '#f8f7ff'
        }}
      >
        <div className="container px-4">
          {/* Desktop: single line */}
          <div className="hidden sm:flex items-center justify-center gap-2 mb-6">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-[#f59e0b] text-[#f59e0b]" />
              ))}
            </div>
            <span className="text-sm font-medium"><CustomerCount responsive /> Users Taking Control</span>
            <span className="text-muted-foreground text-sm">• As Seen In</span>
          </div>
          {/* Mobile: stacked layout */}
          <div className="sm:hidden flex flex-col items-center gap-1.5 mb-5">
            <div className="flex items-center gap-1.5">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-[#f59e0b] text-[#f59e0b]" />
                ))}
              </div>
              <span className="text-sm font-medium"><CustomerCount format="short" /> Users Taking Control</span>
            </div>
            <span className="text-muted-foreground text-xs">As Seen In</span>
          </div>
          <PressLogos backgroundColor="#f8f7ff" />
        </div>
      </section>

      {/* EVENT GIFT SECTION - Dynamic content based on event type */}
      {isEventPage && eventContent[config.id] && (() => {
        const content = eventContent[config.id];
        const colors = eventColors || eventColorSchemes['valentine-gift'];
        const FeatureIcon = ({ icon }: { icon: 'gift' | 'check' | 'heart' }) => {
          switch (icon) {
            case 'gift': return <Gift className="w-4 h-4" />;
            case 'check': return <Check className="w-4 h-4" />;
            case 'heart': return <Heart className="w-4 h-4" />;
          }
        };
        const CardIcon = ({ icon }: { icon: 'moon' | 'gift' | 'sparkles' }) => {
          switch (icon) {
            case 'moon': return <Moon className="w-8 h-8" style={{ color: colors.primary }} />;
            case 'gift': return <Gift className="w-8 h-8" style={{ color: colors.primary }} />;
            case 'sparkles': return <Sparkles className="w-8 h-8" style={{ color: colors.primary }} />;
          }
        };
        
        return (
          <section className="py-12 bg-white">
            <div className="container px-4">
              {/* Event Banner */}
              <div 
                className="relative overflow-hidden rounded-2xl p-8 md:p-12 text-white"
                style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.secondary})` }}
              >
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
                
                <div className="relative z-10 flex flex-col lg:flex-row items-center gap-8">
                  {/* Left: Gift Image */}
                  <div className="w-full lg:w-1/3 flex-shrink-0">
                    <div className="relative">
                      <img 
                        src={content.image} 
                        alt="Gift-Ready Packaging" 
                        className="w-full rounded-xl shadow-2xl"
                      />
                      <div 
                        className="absolute -bottom-3 -right-3 bg-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg"
                        style={{ color: colors.primary }}
                      >
                        Gift-Ready
                      </div>
                    </div>
                  </div>
                  
                  {/* Right: Content */}
                  <div className="flex-1 text-center lg:text-left">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm mb-4" style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}>
                      <Heart className="w-4 h-4 fill-current" />
                      <span>A Thoughtful {eventNames[config.id] || 'Holiday'} Gift</span>
                    </div>
                    
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">
                      <span className="block md:inline">{content.headline}</span>
                      <br className="hidden md:block" />
                      <span className="md:hidden"> </span>
                      <span className="block md:inline">{content.subheadline}</span>
                    </h2>
                    
                    <p className="text-white/90 text-lg mb-6 max-w-xl">
                      {content.description}
                    </p>
                    
                    <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm">
                      {content.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
                          <FeatureIcon icon={feature.icon} />
                          <span>{feature.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Why It's the Perfect Gift - Swipeable on mobile, grid on desktop */}
              {(() => {
                const [activeCardIndex, setActiveCardIndex] = useState(0);
                const carouselRef = useRef<HTMLDivElement>(null);
                const [isGiftDragging, setIsGiftDragging] = useState(false);
                const [giftDragStartX, setGiftDragStartX] = useState(0);
                const [giftScrollStart, setGiftScrollStart] = useState(0);
                
                const handleScroll = () => {
                  if (carouselRef.current && !isGiftDragging) {
                    const scrollLeft = carouselRef.current.scrollLeft;
                    const cardWidth = carouselRef.current.offsetWidth;
                    const newIndex = Math.round(scrollLeft / cardWidth);
                    setActiveCardIndex(newIndex);
                  }
                };
                
                const scrollToCard = (index: number) => {
                  if (carouselRef.current) {
                    const cardWidth = carouselRef.current.offsetWidth;
                    carouselRef.current.scrollTo({ left: cardWidth * index, behavior: 'smooth' });
                  }
                };
                
                // Mouse drag handlers for desktop
                const handleGiftDragStart = (e: React.MouseEvent) => {
                  if (!carouselRef.current) return;
                  setIsGiftDragging(true);
                  setGiftDragStartX(e.clientX);
                  setGiftScrollStart(carouselRef.current.scrollLeft);
                };
                
                const handleGiftDragMove = (e: React.MouseEvent) => {
                  if (!isGiftDragging || !carouselRef.current) return;
                  e.preventDefault();
                  const diff = giftDragStartX - e.clientX;
                  carouselRef.current.scrollLeft = giftScrollStart + diff;
                };
                
                const handleGiftDragEnd = () => {
                  if (!isGiftDragging || !carouselRef.current) return;
                  setIsGiftDragging(false);
                  // Snap to nearest card
                  const cardWidth = carouselRef.current.offsetWidth;
                  const newIndex = Math.round(carouselRef.current.scrollLeft / cardWidth);
                  scrollToCard(newIndex);
                  setActiveCardIndex(newIndex);
                };
                
                return (
                  <div className="mt-12">
                    {/* Mobile: Swipeable carousel */}
                    <div className="md:hidden">
                      <div 
                        ref={carouselRef}
                        onScroll={handleScroll}
                        onMouseDown={handleGiftDragStart}
                        onMouseMove={handleGiftDragMove}
                        onMouseUp={handleGiftDragEnd}
                        onMouseLeave={handleGiftDragEnd}
                        className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-4 px-4 select-none"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', cursor: isGiftDragging ? 'grabbing' : 'grab' }}
                      >
                        {content.cards.map((card, idx) => (
                          <div key={idx} className="flex-shrink-0 w-full snap-center px-2">
                            <div className="text-center">
                              {card.image ? (
                                <div className="w-full aspect-video mx-auto mb-4 rounded-xl overflow-hidden shadow-lg">
                                  <img 
                                    src={card.image} 
                                    alt={card.title}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                  />
                                </div>
                              ) : (
                                <div 
                                  className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                                  style={{ backgroundColor: colors.accent }}
                                >
                                  <CardIcon icon={card.icon} />
                                </div>
                              )}
                              <h3 className="font-semibold text-lg mb-2">{card.title}</h3>
                              <p className="text-muted-foreground text-sm px-4">
                                {card.description}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                      {/* Dot indicators */}
                      <div className="flex justify-center gap-2 mt-6">
                        {content.cards.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => scrollToCard(idx)}
                            className={`h-2.5 rounded-full transition-all duration-300 ${activeCardIndex === idx ? 'w-6' : 'w-2.5 opacity-50'}`}
                            style={{ backgroundColor: colors.primary }}
                            aria-label={`Go to card ${idx + 1}`}
                          />
                        ))}
                      </div>
                    </div>
                    
                    {/* Desktop: Grid layout */}
                    <div className="hidden md:grid md:grid-cols-3 gap-8">
                      {content.cards.map((card, idx) => (
                        <div key={idx} className="text-center">
                          {card.image ? (
                            <div className="w-full aspect-video mx-auto mb-4 rounded-xl overflow-hidden shadow-lg">
                              <img 
                                src={card.image} 
                                alt={card.title}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            </div>
                          ) : (
                            <div 
                              className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: colors.accent }}
                            >
                              <CardIcon icon={card.icon} />
                            </div>
                          )}
                          <h3 className="font-semibold text-lg mb-2">{card.title}</h3>
                          <p className="text-muted-foreground text-sm">
                            {card.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          </section>
        );
      })()}

      {/* INTRO VIDEO SECTION */}
      <IntroVideoSection />

      {/* HOW IT WORKS - 3-Step Visual Walkthrough */}
      <section id="how-it-works" className="py-20" style={{ backgroundColor: '#0a0a0f' }}>
        <div className="container px-4">
          <div className="text-center mb-4">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-sm text-gray-300 mb-4">
              <Clock className="w-4 h-4" /> 60-Second Setup
            </span>
            <h2 className="text-4xl lg:text-5xl ryder-title text-white mb-4">How Ryder One works</h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-lg">Three taps. That's all it takes to set up, transact, and recover.</p>
          </div>
          
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 max-w-5xl mx-auto mt-12">
            {[
              {
                step: '01',
                title: 'Tap to Setup',
                desc: 'Download the Ryder app. Tap your phone to Ryder One. Your wallet is created in under 60 seconds. No seed phrases to write down.',
                video: 'https://ryder.id/cdn/shop/videos/c/vp/2b3a4b4b5f1b4ca9828a44c06ebd0e46/2b3a4b4b5f1b4ca9828a44c06ebd0e46.HD-1080p-7.2Mbps-41642011.mp4?v=0',
              },
              {
                step: '02',
                title: 'Tap to Send',
                desc: 'Confirm every transaction with a single tap. Your keys never leave the device. Physical confirmation means zero remote exploits.',
                video: 'https://ryder.id/cdn/shop/videos/c/vp/0b8c3e4100ed465995115de9d7029fbb/0b8c3e4100ed465995115de9d7029fbb.HD-1080p-7.2Mbps-41642137.mp4?v=0',
              },
              {
                step: '03',
                title: 'Tap to Recover',
                desc: 'Lost your wallet? Tap the Recovery Tag to restore everything instantly. No memorizing, no writing, no stress. Your backup is always ready.',
                video: 'https://ryder.id/cdn/shop/videos/c/vp/63c2e598a6b74e7a9eda1733f6eafcb3/63c2e598a6b74e7a9eda1733f6eafcb3.HD-1080p-7.2Mbps-41642141.mp4?v=0',
              },
            ].map((item, idx) => (
              <div key={idx} className="relative group">
                {/* Step number */}
                <div className="text-6xl font-black text-white/5 absolute -top-4 -left-2 select-none">{item.step}</div>
                
                {/* Video container */}
                <div className="relative aspect-square max-w-[260px] sm:max-w-[280px] md:max-w-[280px] mx-auto mb-6 rounded-2xl overflow-hidden bg-[#141419] border border-white/10 shadow-2xl">
                  <video
                    src={item.video}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                  />
                  {/* Bottom gradient overlay with step label */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3 sm:p-4 pt-10 sm:pt-12">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-[#6a63f5] flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-white">{idx + 1}</span>
                      </div>
                      <span className="text-sm font-semibold text-white">{item.title}</span>
                    </div>
                  </div>
                </div>
                
                {/* Text content - contextual description (no repeated title) */}
                <div className="text-center">
                  <p className="text-sm text-gray-400 leading-relaxed max-w-xs mx-auto">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          
          {/* Bottom CTA */}
          <div className="text-center mt-12">
            <button
              onClick={() => document.getElementById('hero-pricing')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#6a63f5] text-white font-semibold hover:bg-[#5b54e6] transition-colors"
            >
              Get Your Ryder One
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* BENEFITS SECTION - Placed under How It Works */}
      <section id="benefits" className="py-16" style={isEventPage ? { backgroundColor: eventColors?.accent } : {}}>
        <div className="container px-4">
          <div className="text-center mb-12">
            {isEventPage && currentAngleModifier.trustBadge && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4" style={{ backgroundColor: `${eventColors?.primary}15` }}>
                <Check className="w-4 h-4" style={{ color: eventColors?.primary }} />
                <span className="text-sm font-medium" style={{ color: eventColors?.primary }}>
                  {currentAngleModifier.trustBadge}
                </span>
              </div>
            )}
            <h2 className="text-4xl lg:text-5xl ryder-title">{config.benefits.sectionTitle}</h2>
            <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
              {config.benefits.sectionSubtitle}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {config.benefits.benefits.map((benefit, idx) => {
              const icons = [<Zap key="z" className="w-6 h-6" />, <Shield key="s" className="w-6 h-6" />, <Layers key="l" className="w-6 h-6" />];
              return (
                <div key={idx} className="bg-card rounded-2xl border border-border overflow-hidden">
                  {benefit.image ? (
                    <div className="aspect-video bg-muted overflow-hidden">
                      <img
                        src={benefit.image}
                        alt={benefit.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  ) : (
                    <div className="flex justify-center pt-8">
                      <div className="w-14 h-14 rounded-xl bg-[#6a63f5]/10 flex items-center justify-center text-[#6a63f5]">
                        {icons[idx] || icons[0]}
                      </div>
                    </div>
                  )}
                  <div className="p-8">
                    <h3 className="font-semibold text-xl mb-3">{benefit.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{benefit.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TRUST BAR - Key Stats */}
      <section 
        className={`py-8 border-y ${isEventPage ? '' : 'border-border bg-secondary/30'}`}
        style={isEventPage ? { borderColor: `${eventColors?.primary}33`, backgroundColor: eventColors?.accentDark } : {}}
      >
        <div className="container px-4">
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10 md:gap-16">
            {[
              { stat: '60s', label: 'Setup time' },
              { stat: 'EAL6+', label: 'Security certified' },
              { stat: '50+', label: 'Chains supported' },
              { stat: '0', label: 'Seed phrases needed' },
            ].map((item) => (
              <div key={item.label} className="text-center">
                <div className="text-2xl sm:text-3xl font-bold">{item.stat}</div>
                <div className="text-xs text-muted-foreground mt-1">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LOVED BY THOUSANDS - Video testimonials + tweet masonry grid */}
      <LovedByThousands />

      {/* BTC OFFER SECTION - Only on btc-offer page, placed before comparison */}
      {isBtcOffer && <BtcOfferSection onCtaClick={() => setShowOrderPopup(true)} />}

      {/* COMPARISON TABLE - Ryder One vs Ledger vs Trezor */}
      {isBtcOffer ? (
        <BtcComparisonTable />
      ) : (
        <ComparisonTable 
          backgroundColor='#0a0a0f'
          accentColor={isEventPage && eventColors ? eventColors.accent : '#6a63f5'}
        />
      )}

      {/* THE DIFFERENCE SECTION */}
      <section id="difference" className="py-16" style={isEventPage ? { backgroundColor: eventColors?.accentDark } : { backgroundColor: '#f5f5f7' }}>
        <div className="container px-4">
          <h2 className="text-3xl font-semibold tracking-tight text-center mb-12">The difference is clear</h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl xl:max-w-4xl mx-auto">
            <div className="bg-card rounded-2xl overflow-hidden border border-border">
              {/* Video for Traditional Wallets */}
              <div className="aspect-square overflow-hidden bg-black">
                <video 
                  src="https://files.manuscdn.com/user_upload_by_module/session_file/310519663213769878/gjAvZzXIcqZdDZOH.mov" 
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8">
              <h3 className="font-semibold text-lg mb-4 text-muted-foreground">{config.difference.standardPillowTitle}</h3>
              <ul className="space-y-3">
                {config.difference.standardPillowPoints.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-muted-foreground">
                    <span className="text-[#6a63f5]">✕</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              </div>
            </div>
            <div className="bg-card rounded-2xl overflow-hidden border-2 border-foreground relative">
              {/* Video for Ryder One */}
              <div className="aspect-square overflow-hidden bg-black">
                <video 
                  src="https://files.manuscdn.com/user_upload_by_module/session_file/310519663213769878/thYcAncRNvNIHRRH.mov" 
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8">
              <h3 className="font-semibold text-lg mb-4">{config.difference.fluffcoPillowTitle}</h3>
              <ul className="space-y-3">
                {config.difference.fluffcoPillowPoints.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="text-green-600">✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TAPSAFE PROCESS SECTION */}
      <section className="py-16" style={isEventPage ? { backgroundColor: eventColors?.accent } : { backgroundColor: 'white' }}>
        <div className="container px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl ryder-title mb-4">Security without the anxiety.</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">TapSafe replaces the stressful 24-word seed phrase with simple NFC taps. No paper. No writing. No memorizing.</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              { step: '01', title: 'Tap to set up', desc: 'Create your wallet in under a minute. Just tap your Ryder One to your phone and you\'re ready.', icon: '\u26A1' },
              { step: '02', title: 'Tap to back up', desc: 'Backup offline with a Recovery Tag. No paper. No writing. Just a quick tap.', icon: '🔒' },
              { step: '03', title: 'Tap to transact', desc: 'Every transaction starts and ends with a single tap from you. Nothing moves without your approval.', icon: '💱' },
              { step: '04', title: 'Tap to recover', desc: 'Lose your wallet? Recover instantly with your Recovery Tag. No stress, no waiting.', icon: '\u2728' },
            ].map((item) => (
              <div key={item.step} className="relative bg-card rounded-2xl border border-border p-6 text-center hover:border-foreground/20 transition-colors">
                <div className="text-3xl mb-4">{item.icon}</div>
                <div className="text-xs font-mono text-muted-foreground mb-2">STEP {item.step}</div>
                <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TAPSAFE EXPLAINER PANEL - Removed (redundant with Recovery Scenarios) */}

      {/* RECOVERY SCENARIOS SECTION - expandable TapSafe options */}
      <TapSafeSection />

      {/* BUILT FOR SECURITY - Consolidated section (merged Built To Keep + Built Different + Zero Points of Failure) */}
      <BuiltForSecuritySection isEventPage={isEventPage} eventColors={eventColors} />

      {/* WHAT'S IN THE BOX - Bundle Value Section */}
      <section className="py-16" style={isEventPage ? { backgroundColor: eventColors?.accent } : {}}>
        <div className="container px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl ryder-title mb-4">{isBtcOffer ? 'Everything you need. One box.' : 'Everything you need. One box.'}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">{isBtcOffer ? 'Your Ryder One bundle includes everything to get started, plus $200 in Bitcoin through our cashback program.' : `Get Ryder One for $${pricing.salePrice} (${pricing.discountPercent}% off) plus a free welcome kit.`}</p>
          </div>
          
          <div className={`grid sm:grid-cols-2 ${isBtcOffer ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-6 max-w-6xl mx-auto`}>
            {[
              { name: 'Ryder One', desc: 'Hardware wallet with EAL6+ security certification', price: `$${pricing.salePrice}`, originalPrice: `$${pricing.originalPrice}`, highlight: true, isBtc: false, image: 'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/bfMrjDxrEMyxijvL.jpg' },
              ...(isBtcOffer ? [{ name: '$200 BTC Cashback', desc: 'Deposited to your Ryder wallet within 30 days', price: 'Cashback', originalPrice: '$200', highlight: true, isBtc: true, image: '' }] : []),
              { name: 'Recovery Tag', desc: 'Battery-free, ultra-resistant backup solution', price: 'FREE', originalPrice: '', highlight: false, isBtc: false, image: 'https://ryder.id/cdn/shop/files/recovery-tag.png?v=1770649962' },
              { name: 'Wireless Charger', desc: 'Universal charger for Ryder One and smartphones', price: 'FREE', originalPrice: '', highlight: false, isBtc: false, image: 'https://ryder.id/cdn/shop/files/wireless_charging_1.png?v=1769270696' },
              { name: 'Travel Pouch', desc: 'Premium protection for your wallet on the go', price: 'FREE', originalPrice: '', highlight: false, isBtc: false, image: 'https://private-us-east-1.manuscdn.com/sessionFile/zt0R2JLN6ZCkpO8tpr5PKy/sandbox/0k5yXezDwWrqUz1YRi290F-img-1_1771378546000_na1fn_dHJhdmVsLXBvdWNo.png?x-oss-process=image/resize,w_1920,h_1920/format,webp/quality,q_80&Expires=1798761600&Policy=eyJTdGF0ZW1lbnQiOlt7IlJlc291cmNlIjoiaHR0cHM6Ly9wcml2YXRlLXVzLWVhc3QtMS5tYW51c2Nkbi5jb20vc2Vzc2lvbkZpbGUvenQwUjJKTE42WkNrcE84dHByNVBLeS9zYW5kYm94LzBrNXlYZXpEd1dycVV6MVlSaTI5MEYtaW1nLTFfMTc3MTM3ODU0NjAwMF9uYTFmbl9kSEpoZG1Wc0xYQnZkV05vLnBuZz94LW9zcy1wcm9jZXNzPWltYWdlL3Jlc2l6ZSx3XzE5MjAsaF8xOTIwL2Zvcm1hdCx3ZWJwL3F1YWxpdHkscV84MCIsIkNvbmRpdGlvbiI6eyJEYXRlTGVzc1RoYW4iOnsiQVdTOkVwb2NoVGltZSI6MTc5ODc2MTYwMH19fV19&Key-Pair-Id=K2HSFNDJXOU9YS&Signature=Jij~inRxwiP1DodsfzQgXXTg8aveICIdKnYB0qZgnz4RLHazbHaUshDIqgWeON-hxMN-K4vfYDJBwPJylLurGBu96EOQyAItO6VYOzcJ3MBmQCakdFsgzMtOtvlXs4FQfm7FVHIMIkuZnk9BjU8HXPnioRG20npX1toICRQZ0iAzLdAcbU84T-Eu1B9keKlMdFoqNgqlFQ7BuTf2B8z-xnD36fH11x5C3rzJ0UcsiVoL3q~EMnh7LMcliwm0nl9CrsmTrYVWrBnQDbC-sUNtFv7njXh11JwrG9hQUI2-hrXKHnoqAXlwfW3J15NyLKs6mrGO398~yJXrDzhV7PH-jw__' },
            ].map((item) => (
              <div key={item.name} className={`rounded-2xl border p-6 text-center ${item.isBtc ? 'border-[#6a63f5]/20 bg-[#6a63f5]/[0.03] ring-1 ring-[#6a63f5]/10' : item.highlight ? 'border-foreground/30 bg-card ring-1 ring-foreground/10' : 'border-border bg-card'}`}>
                {item.isBtc ? (
                  <div className="w-20 h-20 mx-auto mb-4 rounded-xl bg-[#6a63f5]/10 flex items-center justify-center">
                    <span className="text-[#6a63f5] text-3xl font-bold">₿</span>
                  </div>
                ) : (
                  <div className="w-20 h-20 mx-auto mb-4 rounded-xl overflow-hidden bg-secondary">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" loading="lazy" decoding="async" width={80} height={80} />
                  </div>
                )}
                <h3 className={`font-semibold text-lg mb-1 ${item.isBtc ? 'text-[#6a63f5]' : ''}`}>{item.name}</h3>
                <p className="text-xs text-muted-foreground mb-3 leading-relaxed">{item.desc}</p>
                <div className="flex items-center justify-center gap-2">
                  <span className={`font-bold text-lg ${item.isBtc ? 'text-[#6a63f5]' : item.price === 'FREE' ? 'text-green-500' : ''}`}>{item.price}</span>
                  {item.originalPrice && <span className="text-sm text-muted-foreground line-through">{item.originalPrice}</span>}
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-8 text-center">
            <div className="inline-flex items-center gap-3 bg-card border border-border rounded-full px-6 py-3">
              <span className="text-sm text-muted-foreground">Regular: <span className="line-through">${pricing.originalPrice}</span></span>
              <span className="w-px h-4 bg-border" />
              <span className="text-sm font-semibold">You pay: ${pricing.salePrice}</span>
              <span className="w-px h-4 bg-border" />
              {isBtcOffer ? (
                <span className="text-sm font-bold text-[#6a63f5]">+ $200 BTC cashback</span>
              ) : (
                <span className="text-sm font-bold text-green-500">Save ${pricing.savingsAmount} ({pricing.discountPercent}% off) + free welcome kit</span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* MULTI-CHAIN SUPPORT SECTION */}
      <section className={`py-16 ${isEventPage ? '' : 'bg-secondary/30'}`} style={isEventPage ? { backgroundColor: eventColors?.accentDark } : {}}>
        <div className="container px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl ryder-title mb-4">One wallet. Every chain.</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">Bitcoin, Ethereum, Solana, Stacks — and your favourite tokens. One device for everything.</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 max-w-4xl mx-auto">
            {[
              { chain: 'Bitcoin', ticker: 'BTC', color: '#f7931a', desc: 'The original cryptocurrency' },
              { chain: 'Ethereum', ticker: 'ETH', color: '#627eea', desc: 'Smart contracts & ERC-20' },
              { chain: 'Stacks', ticker: 'STX', color: '#5546ff', desc: 'Smart contracts on Bitcoin' },
              { chain: 'Solana', ticker: 'SOL', color: '#9945ff', desc: 'High-speed transactions' },
            ].map((item) => (
              <div key={item.ticker} className="bg-card rounded-2xl border border-border p-6 text-center hover:shadow-md transition-shadow">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: `${item.color}15` }}>
                  <span className="font-mono font-bold text-sm" style={{ color: item.color }}>{item.ticker}</span>
                </div>
                <h3 className="font-semibold text-base mb-1">{item.chain}</h3>
                <p className="text-xs text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
          
          {/* Expandable token list */}
          <div className="mt-10 max-w-4xl mx-auto">
            <p className="text-center text-sm text-muted-foreground mb-6">Plus top ERC-20 and SPL tokens supported natively.</p>
            
            <button 
              onClick={() => setShowTokenList(!showTokenList)}
              className="mx-auto flex items-center gap-2 px-5 py-2.5 rounded-full border border-border bg-card text-sm font-medium hover:bg-secondary transition-colors"
            >
              {showTokenList ? 'Hide' : 'See'} full list of supported tokens
              {showTokenList ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            
            {showTokenList && (
              <div className="mt-6 grid md:grid-cols-2 gap-6 animate-in fade-in duration-300">
                {/* ERC-20 Tokens */}
                <div className="bg-card rounded-xl border border-border p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: '#627eea20' }}>
                      <span className="font-mono font-bold text-[10px]" style={{ color: '#627eea' }}>ETH</span>
                    </div>
                    <h4 className="font-semibold text-sm">ERC-20 (Ethereum)</h4>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      'USDT', 'USDC', 'WETH', 'LINK', 'cbBTC', 'SHIB', 'UNI', 'DAI', 'USDe', 'CRO',
                      'WLFI', 'MNT', 'AAVE', 'NEAR', 'BGB', 'OKB', 'ENA', 'PEPE', 'ONDO', 'KCS',
                      'WLD', 'POL', 'ATOM', 'ARB', 'RENDER', 'STRK', 'QNT', 'COMP', 'SAND', 'DOT'
                    ].map((token) => (
                      <span key={token} className="px-2 py-1 rounded-md bg-secondary text-[11px] font-mono text-muted-foreground border border-border">{token}</span>
                    ))}
                  </div>
                </div>
                
                {/* SPL Tokens */}
                <div className="bg-card rounded-xl border border-border p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: '#9945ff20' }}>
                      <span className="font-mono font-bold text-[10px]" style={{ color: '#9945ff' }}>SOL</span>
                    </div>
                    <h4 className="font-semibold text-sm">SPL (Solana)</h4>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      'USDC', 'USDT', 'cbBTC', 'RENDER', 'TRUMP', 'PUMP', 'JUP', 'HNT', 'PNUT', 'ORCA',
                      'PENGU', 'PYUSD', 'BONK', 'PYTH', 'JTO', 'TNSR', 'RAY', 'USDG', 'KMNO', 'W',
                      'PAI', 'WIF', 'ME', 'WBTC', 'GMT', 'DRIFT', 'ZBCN', 'GRASS', 'SONIC', 'IO', 'Fartcoin'
                    ].map((token) => (
                      <span key={token} className="px-2 py-1 rounded-md bg-secondary text-[11px] font-mono text-muted-foreground border border-border">{token}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          

        </div>
      </section>

      {/* YOU'RE NEVER LOCKED IN - Seed phrase import/export */}
      <NeverLockedInSection />



      {/* TESTIMONIALS - Emotional with human touches */}
      <section id="reviews-section" className="py-16" style={{ scrollMarginTop: '100px', backgroundColor: isEventPage && eventColors ? eventColors.accent : '#f5f5f7' }}>
        <div className="container px-4">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-[#f59e0b] text-[#f59e0b]" />
              ))}
            </div>
            <span className="font-extrabold text-xl ryder-title text-gray-900">{config.testimonials.sectionTitle}</span>
          </div>
          <h2 className="text-4xl lg:text-5xl ryder-title text-center mb-12 text-gray-900">{config.testimonials.sectionSubtitle}</h2>
          
          {/* Desktop: Show 4 testimonials in grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {visibleTestimonials.map((t, idx) => (
              <div key={idx} className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-100">
                    <img src={t.image} alt={t.name} className="w-full h-full object-cover" loading="lazy" decoding="async" width={48} height={48} />
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium text-gray-900">{t.name}</span>
                      {t.verified && (
                        <span className="flex items-center gap-1">
                          <span className="w-3.5 h-3.5 rounded-full bg-[#1d9bf0] flex items-center justify-center flex-shrink-0">
                            <Check className="w-2 h-2 text-white stroke-[3]" />
                          </span>
                          <span className="text-[10px] text-[#1d9bf0] font-medium">Verified</span>
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">{t.location}</div>
                  </div>
                </div>
                <div className="flex mb-2">
                  {[...Array(t.rating)].map((_, i) => (
                    <Star key={i} className="w-3.5 h-3.5 fill-[#f59e0b] text-[#f59e0b]" />
                  ))}
                </div>
                <p className="text-sm leading-relaxed text-gray-600">"{t.text}"</p>
              </div>
            ))}
          </div>
          
          {/* Desktop: Show More/Less button */}
          <div className="hidden md:block text-center mt-8">
            <button 
              onClick={() => setShowAllTestimonials(!showAllTestimonials)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gray-300 text-sm font-medium text-gray-700 hover:bg-white transition-colors"
            >
              {showAllTestimonials ? 'Show Less' : 'See More Testimonials'}
              {showAllTestimonials ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
          
          {/* Mobile: Show 3 testimonials + 4th with overlay button */}
          <div className="md:hidden relative">
            <div className="flex flex-col gap-2.5">
              {testimonials.map((t, idx) => {
                // Show first 3 fully, 4th partially (for overlay effect), hide rest when collapsed
                const isHidden = !showAllTestimonials && idx >= 4;
                const isPartial = !showAllTestimonials && idx === 3;
                
                if (isHidden) return null;
                
                return (
                  <div 
                    key={idx} 
                    className={`bg-white p-4 rounded-lg border border-gray-200 shadow-sm transition-all duration-300 relative ${
                      isPartial ? 'opacity-60' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                        <img src={t.image} alt={t.name} className="w-full h-full object-cover" loading="lazy" decoding="async" width={40} height={40} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium text-gray-900">{t.name}</span>
                          {t.verified && (
                            <span className="w-3.5 h-3.5 rounded-full bg-[#1d9bf0] flex items-center justify-center flex-shrink-0">
                              <Check className="w-2 h-2 text-white stroke-[3]" />
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex">
                            {[...Array(t.rating)].map((_, i) => (
                              <Star key={i} className="w-3 h-3 fill-[#f59e0b] text-[#f59e0b]" />
                            ))}
                          </div>
                          <span className="text-[10px] text-gray-500">{t.location}</span>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs leading-relaxed line-clamp-3 text-gray-600">"{t.text}"</p>
                    
                    {/* Overlay button on 4th card with gradient blur */}
                    {isPartial && (
                      <div className="absolute inset-0 flex items-center justify-center backdrop-blur-[2px] rounded-lg">
                        {/* Gradient overlay for smooth fade effect */}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#f5f5f7] via-[#f5f5f7]/80 to-[#f5f5f7]/50 rounded-lg" />
                        <button 
                          onClick={() => setShowAllTestimonials(true)}
                          className="relative z-10 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gray-300 text-sm font-medium bg-white text-gray-700 shadow-lg hover:bg-gray-50 transition-colors"
                        >
                          See All {testimonials.length} Reviews
                          <ChevronDown className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* PRICING SECTION - REMOVED (Choose Your Setup deleted per user request) */}
      {isEventPage && (<section 
        className="py-16"
        style={isEventPage && eventColors ? {
          background: `linear-gradient(to bottom, ${eventColors.accent}, ${eventColors.accentDark})`
        } : {
          backgroundColor: 'var(--secondary)'
        }}
      >
        <div className="container px-4 xl:max-w-4xl">
          <div className="text-center mb-8">
            {isEventPage && eventColors && (
              <span 
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium mb-4"
                style={{ backgroundColor: `${eventColors.primary}15`, color: eventColors.primary }}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
                A Thoughtful {eventNames[config.id] || 'Holiday'} Gift
              </span>
            )}
            <h2 className="text-3xl font-semibold tracking-tight">{isEventPage ? `${eventNames[config.id] || 'Holiday'} pricing` : 'Choose your setup'}</h2>
            <p className="mt-3 text-muted-foreground">{isEventPage ? 'Give the gift of crypto security at exclusive seasonal pricing.' : (isUS ? 'Online-only pricing. Free worldwide shipping.' : `Online-only pricing. ${nonUsBadgeText}.`)}</p>
          </div>

          {/* Size Toggle - horizontal on both mobile and desktop */}
          <div className="flex justify-center mb-8">
            <div 
              className="inline-flex rounded-full p-1"
              style={isEventPage && eventColors ? {
                backgroundColor: eventColors.secondaryLight,
                border: `1px solid ${eventColors.primary}40`
              } : {
                backgroundColor: 'var(--card)',
                border: '1px solid var(--border)'
              }}
            >
              <button
                onClick={() => {
                  setPricingSectionSize("Standard");
                  triggerHapticFeedback();
                }}
                className="px-4 sm:px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 flex flex-col sm:flex-row items-center gap-0 sm:gap-1 relative overflow-hidden active:scale-95"
                style={pricingSectionSize === "Standard" 
                  ? (isEventPage && eventColors ? {
                      backgroundColor: eventColors.primary,
                      color: 'white',
                      transform: 'scale(1.05)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    } : {
                      backgroundColor: 'var(--foreground)',
                      color: 'var(--background)',
                      transform: 'scale(1.05)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    })
                  : (isEventPage && eventColors ? {
                      color: eventColors.primary
                    } : {
                      color: 'var(--muted-foreground)'
                    })
                }
              >
                <span>Standard</span>
                <span className="text-[10px] sm:text-sm opacity-70">(20"×28")</span>
              </button>
              <button
                onClick={() => {
                  setPricingSectionSize("King");
                  triggerHapticFeedback();
                }}
                className="px-4 sm:px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 flex flex-col sm:flex-row items-center gap-0 sm:gap-1 relative overflow-hidden active:scale-95"
                style={pricingSectionSize === "King" 
                  ? (isEventPage && eventColors ? {
                      backgroundColor: eventColors.primary,
                      color: 'white',
                      transform: 'scale(1.05)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    } : {
                      backgroundColor: 'var(--foreground)',
                      color: 'var(--background)',
                      transform: 'scale(1.05)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    })
                  : (isEventPage && eventColors ? {
                      color: eventColors.primary
                    } : {
                      color: 'var(--muted-foreground)'
                    })
                }
              >
                <span>King</span>
                <span className="text-[10px] sm:text-sm opacity-70">(20"×36")</span>
              </button>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 relative">
            {pricingSectionPricing.map((option) => {
              const setupImage = option.quantity === 1 
                ? '/images/optimized/setup/setup-1-pillow.png' 
                : option.quantity === 2 
                  ? '/images/optimized/setup/setup-2-pillows.png' 
                  : '/images/optimized/setup/setup-4-pillows.png';
              return (
                <div
                  key={option.quantity}
                  className={`rounded-2xl border-2 relative overflow-visible flex flex-col ${
                    option.quantity === 4 ? 'border-foreground bg-card' : 'border-border bg-card'
                  }`}
                  style={option.quantity === 4 && isEventPage && eventColors ? {
                    borderColor: eventColors.primary
                  } : {}}
                >
                  {/* Badges container - flex layout for consistent spacing */}
                  <div className="absolute -top-3 right-4 z-20 flex items-center gap-1.5">
                    {option.badge && (
                      <span 
                        className="text-xs font-medium px-3 py-1 rounded-full shadow-md"
                        style={isEventPage && eventColors ? {
                          backgroundColor: eventColors.primary,
                          color: 'white'
                        } : {
                          backgroundColor: option.badge === 'Best Value' ? '#6a63f5' : 'var(--foreground)',
                          color: option.badge === 'Best Value' ? 'white' : 'var(--background)'
                        }}
                      >
                        {option.badge}
                      </span>
                    )}
                    {/* Discount badge - always shown */}
                    <span 
                      className="text-xs font-medium px-3 py-1 rounded-full shadow-md"
                      style={{
                        backgroundColor: '#22c55e',
                        color: 'white'
                      }}
                    >
                      -{option.discount}%
                    </span>
                  </div>
                  {/* 16:9 Image at top, touching edges - zoomed to focus on pillows */}
                  <div className="relative aspect-video w-full bg-secondary rounded-t-[14px] overflow-hidden">
                    <img 
                      src={setupImage} 
                      alt={`${option.quantity} Ryder One bundle`} 
                      className="w-full h-full object-cover object-top scale-[1.4] origin-top"
                      loading="lazy"
                    />
                  </div>
                  
                  {/* Content area with flex-grow to push button to bottom */}
                  <div className="p-6 flex flex-col flex-grow text-left">
                    <div className="text-xl font-semibold mb-2">{option.quantity} Unit{option.quantity > 1 ? 's' : ''}</div>
                    
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="text-2xl font-bold tabular-nums overflow-hidden">
                        <span 
                          key={`${pricingSectionSize}-${option.price}`}
                          className="inline-block animate-[slideIn_0.4s_cubic-bezier(0.34,1.56,0.64,1)_both]"
                          style={{ animationDelay: '0ms' }}
                        >
                          ${option.price.toFixed(2)}
                        </span>
                      </span>
                      <span className="text-sm text-muted-foreground tabular-nums overflow-hidden">
                        <span 
                          key={`${pricingSectionSize}-${option.originalPrice}`}
                          className="inline-block line-through animate-[slideIn_0.4s_cubic-bezier(0.34,1.56,0.64,1)_both]"
                          style={{ animationDelay: '50ms' }}
                        >
                          ${option.originalPrice.toFixed(2)}
                        </span>
                      </span>
                    </div>
                    <div className="text-sm text-green-600 font-medium mb-4 tabular-nums overflow-hidden">
                      <span 
                        key={`${pricingSectionSize}-${option.savings}`}
                        className="inline-block animate-[slideIn_0.4s_cubic-bezier(0.34,1.56,0.64,1)_both]"
                        style={{ animationDelay: '100ms' }}
                      >
                        Save ${option.savings.toFixed(2)}
                      </span>
                    </div>
                    
                    {/* Pillowcase bonus - horizontal bar style matching hero */}
                    {option.bonus && (
                      <div className="border-t border-b border-border py-2 my-2 flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <Gift className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">+ Free Recovery Tag</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-muted-foreground line-through">$116</span>
                          <span className="text-xs font-medium text-green-600">FREE</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Spacer to push button to bottom */}
                    <div className="flex-grow"></div>
                    
                    <button 
                      onClick={() => {
                        setSelectedQuantity(option.quantity);
                        setSelectedSize(pricingSectionSize);
                        // Track AddToCart event (Meta)
                        const productData = getProductDataForTracking(pricing.scenarioId);
                        trackAddToCart(productData);
                        
                        // GA4: Track add_to_cart
                        const ga4Item = createMainProductItem('standard', 1, pricing.salePrice, pricing.originalPrice);
                        trackGA4AddToCart([ga4Item], pricing.salePrice);
                        
                        setShowOrderPopup(true);
                      }}
                      className={`w-full py-3 rounded-full font-medium transition-all duration-300 hover:-translate-y-px active:translate-y-0 ${
                        option.quantity === 4 
                          ? 'text-white' 
                          : 'bg-foreground text-background'
                      }`}
                      style={option.quantity === 4 ? { 
                        backgroundColor: isEventPage && eventColors ? eventColors.primary : '#6a63f5',
                        boxShadow: `0 2px 8px ${isEventPage && eventColors ? eventColors.primary : '#6a63f5'}40`
                      } : (isEventPage && eventColors ? {
                        backgroundColor: 'transparent',
                        border: `2px solid ${eventColors.primary}`,
                        color: eventColors.primary
                      } : {})}
                      onMouseEnter={(e) => {
                        if (option.quantity === 4) {
                          const color = isEventPage && eventColors ? eventColors.primary : '#6a63f5';
                          e.currentTarget.style.boxShadow = `0 4px 20px ${color}70`;
                          e.currentTarget.style.filter = 'brightness(0.95)';
                        } else if (isEventPage && eventColors) {
                          e.currentTarget.style.backgroundColor = eventColors.primary;
                          e.currentTarget.style.color = 'white';
                          e.currentTarget.style.boxShadow = `0 2px 8px ${eventColors.primary}30`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (option.quantity === 4) {
                          const color = isEventPage && eventColors ? eventColors.primary : '#6a63f5';
                          e.currentTarget.style.boxShadow = `0 2px 8px ${color}40`;
                          e.currentTarget.style.filter = 'brightness(1)';
                        } else if (isEventPage && eventColors) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = eventColors.primary;
                          e.currentTarget.style.boxShadow = 'none';
                        }
                      }}
                    >
                      {getPageCTAWithDiscount(option.discount)}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="flex items-center justify-center gap-6 mt-8 text-sm text-muted-foreground">
            <span className="flex items-center gap-1"><Check className="w-4 h-4" /> 100-Night Guarantee</span>
            <span className="flex items-center gap-1"><Check className="w-4 h-4" /> {isUS ? 'Free Shipping' : nonUsBadgeText}</span>
            <span className="flex items-center gap-1"><Check className="w-4 h-4" /> {isUS ? 'Made in USA' : nonUsAlternativeUsp.title}</span>
          </div>
          
          {/* Award Badges - Mentions prominent, publications below */}
          {/* Desktop: horizontal with separators, Mobile: vertical list aligned left */}
          <div className="hidden sm:flex flex-wrap justify-center items-center gap-6 mt-8">
            <div className="flex flex-col items-center text-center">
              <span className="text-sm font-semibold text-foreground">"Best Hardware Wallet"</span>
              <span className="text-[10px] text-muted-foreground">CoinDesk</span>
            </div>
            <span className="text-muted-foreground/30">|</span>
            <div className="flex flex-col items-center text-center">
              <span className="text-sm font-semibold text-foreground">"Most User-Friendly"</span>
              <span className="text-[10px] text-muted-foreground">Bitcoin Magazine</span>
            </div>
            <span className="text-muted-foreground/30">|</span>
            <div className="flex flex-col items-center text-center">
              <span className="text-sm font-semibold text-foreground">"Top Security Pick"</span>
              <span className="text-[10px] text-muted-foreground">Decrypt</span>
            </div>
            <span className="text-muted-foreground/30">|</span>
            <div className="flex flex-col items-center text-center">
              <span className="text-sm font-semibold text-foreground">"Editor's Pick"</span>
              <span className="text-[10px] text-muted-foreground">Healthline</span>
            </div>
            <span className="text-muted-foreground/30">|</span>
            <div className="flex flex-col items-center text-center">
              <span className="text-sm font-semibold text-foreground">"95/100"</span>
              <span className="text-[10px] text-muted-foreground">The Block</span>
            </div>
          </div>
          {/* Mobile: clean 2x2 grid layout */}
          <div className="grid sm:hidden grid-cols-2 gap-3 mt-6 px-4 max-w-sm mx-auto">
            <div className="bg-card border border-border rounded-lg px-3 py-2 text-center">
              <span className="text-xs font-semibold text-foreground block">"Best Hardware Wallet"</span>
              <span className="text-[10px] text-muted-foreground">CoinDesk</span>
            </div>
            <div className="bg-card border border-border rounded-lg px-3 py-2 text-center">
              <span className="text-xs font-semibold text-foreground block">"Best Overall"</span>
              <span className="text-[10px] text-muted-foreground">Bitcoin Magazine</span>
            </div>
            <div className="bg-card border border-border rounded-lg px-3 py-2 text-center">
              <span className="text-xs font-semibold text-foreground block">"Top Security Pick"</span>
              <span className="text-[10px] text-muted-foreground">Decrypt</span>
            </div>
            <div className="bg-card border border-border rounded-lg px-3 py-2 text-center">
              <span className="text-xs font-semibold text-foreground block">"95/100 Rating"</span>
              <span className="text-[10px] text-muted-foreground">The Block</span>
            </div>
          </div>
        </div>
      </section>)}

      {/* FAQ SECTION - Closed by default, openable/closable */}
      <section id="faq" className="py-16" style={isEventPage ? { backgroundColor: eventColors?.accent } : {}}>
        <div className="container px-4 xl:max-w-3xl">
          <h2 className="text-4xl lg:text-5xl ryder-title text-center mb-12">{config.faq.sectionTitle}</h2>
          
          <div 
            className="rounded-2xl border overflow-hidden"
            style={isEventPage && eventColors ? {
              backgroundColor: 'white',
              borderColor: `${eventColors.primary}33`
            } : {
              backgroundColor: 'var(--card)',
              borderColor: 'var(--border)'
            }}
          >
            {faqs.map((faq, idx) => (
              <div 
                key={idx} 
                className="last:border-b-0 transition-colors border-b"
                style={isEventPage && eventColors ? {
                  borderColor: `${eventColors.primary}1a`,
                  backgroundColor: expandedFaq === idx ? eventColors.background : undefined
                } : {
                  borderColor: 'var(--border)'
                }}
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-4 sm:p-6 text-left"
                >
                  <h3 className="font-medium pr-4 text-sm sm:text-base">{faq.q}</h3>
                  {expandedFaq === idx ? (
                    <ChevronUp className="w-5 h-5 flex-shrink-0 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-5 h-5 flex-shrink-0 text-muted-foreground" />
                  )}
                </button>
                {expandedFaq === idx && (
                  <div className="px-4 pb-4 sm:px-6 sm:pb-6 -mt-2">
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* FOOTER */}
      <footer 
        className="py-12 border-t"
        style={isEventPage && eventColors ? {
          borderColor: `${eventColors.primary}33`,
          backgroundColor: 'white'
        } : {
          borderColor: 'var(--border)'
        }}
      >
        <div className="container px-4 text-center">
          <RyderLogo className="h-7 w-auto mx-auto mb-4" variant="dark" />
          <p className="text-sm text-muted-foreground mb-4">Your crypto. Your control.</p>
          <div className="flex flex-wrap justify-center gap-4 mb-4">
<a href="https://ryder.id/policies/terms-of-service" target="_blank" rel="noopener" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Terms of Service</a>
             <a href="https://ryder.id/policies/refund-policy" target="_blank" rel="noopener" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Refund Policy</a>
             <a href="https://ryder.id/policies/privacy-policy" target="_blank" rel="noopener" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Privacy Policy</a>
          </div>
          <div className="text-xs text-muted-foreground">
            © 2026 Ryder. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Size Guide Modal */}
      {showSizeGuide && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowSizeGuide(false)}
          />
          {/* Modal */}
          <div className="relative bg-card rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-auto animate-[fadeSlideIn_0.3s_ease-out]">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-lg font-semibold">Product Details</h3>
              <button 
                onClick={() => setShowSizeGuide(false)}
                className="p-1 hover:bg-secondary rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Content */}
            <div className="p-4 space-y-4">
              <p className="text-sm text-muted-foreground">Ryder One hardware wallet specifications:</p>
              
              {/* Standard Recommendation */}
              <div 
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  selectedSize === 'Standard' ? 'border-foreground bg-secondary/50' : 'border-border hover:border-muted-foreground'
                }`}
                onClick={() => { setSelectedSize('Standard'); setShowSizeGuide(false); }}
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6 text-muted-foreground">
                      <rect x="2" y="6" width="20" height="12" rx="2"/>
                      <line x1="8" y1="6" x2="8" y2="18"/>
                      <line x1="16" y1="6" x2="16" y2="18"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold">Standard</span>
                      <span className="text-xs text-muted-foreground">20" × 28"</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Best for Twin, Twin XL, Full, and Queen beds</p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <span className="text-[10px] bg-secondary px-2 py-0.5 rounded-full">Twin</span>
                      <span className="text-[10px] bg-secondary px-2 py-0.5 rounded-full">Twin XL</span>
                      <span className="text-[10px] bg-secondary px-2 py-0.5 rounded-full">Full</span>
                      <span className="text-[10px] bg-secondary px-2 py-0.5 rounded-full">Queen</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* King Recommendation */}
              <div 
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  selectedSize === 'King' ? 'border-foreground bg-secondary/50' : 'border-border hover:border-muted-foreground'
                }`}
                onClick={() => { setSelectedSize('King'); setShowSizeGuide(false); }}
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-7 h-7 text-muted-foreground">
                      <rect x="1" y="6" width="22" height="12" rx="2"/>
                      <line x1="6" y1="6" x2="6" y2="18"/>
                      <line x1="12" y1="6" x2="12" y2="18"/>
                      <line x1="18" y1="6" x2="18" y2="18"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold">King</span>
                      <span className="text-xs text-muted-foreground">20" × 36"</span>
                      <span className="text-[10px] bg-foreground text-background px-2 py-0.5 rounded-full">Chosen by 81%</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Best for King and California King beds</p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <span className="text-[10px] bg-secondary px-2 py-0.5 rounded-full">King</span>
                      <span className="text-[10px] bg-secondary px-2 py-0.5 rounded-full">Cal King</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Pro Tip - Hidden as per requirements */}
            </div>
          </div>
        </div>
      )}

      {/* Sticky Add to Cart Bar */}
      <div 
        className={`fixed bottom-0 left-0 right-0 z-40 bg-card border-t shadow-lg transform transition-all duration-300 ${
          showStickyBar ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={isEventPage && eventColors ? {
          borderColor: `${eventColors.primary}33`
        } : {
          borderColor: 'var(--border)'
        }}
      >
          {/* Desktop Layout */}
          <div className="hidden sm:block container px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              {/* Product info - Fixed width to prevent layout shift */}
              <div className="flex items-center gap-3" style={{ minWidth: '220px' }}>
                <img 
                  src="https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/AWwuJtuLTrztkpjg.jpg"
                  alt="Ryder One" 
                  className="w-12 h-12 object-cover flex-shrink-0 rounded-lg"
                />
                <div style={{ minWidth: '160px' }}>
                  {isBtcOffer ? (
                    <>
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-semibold">${pricing.salePrice}</p>
                        <span className="text-xs bg-[#6a63f5]/10 text-[#6a63f5] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap">+ $200 cashback</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">Ryder One + $200 BTC cashback</p>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-semibold">${currentPricing.find(p => p.quantity === selectedQuantity)?.price.toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground line-through">${pricing.originalPrice}.00</p>
                        <span className="text-xs bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400 px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap">-{currentPricing.find(p => p.quantity === selectedQuantity)?.discount}%</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-muted-foreground" style={{ minWidth: '80px' }}>Ryder One + free welcome kit</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              {/* Spacer - removed selectors as per requirements */}
              
              {/* CTA button */}
              <button 
                onClick={() => {
                  // Track AddToCart event (Meta)
                  const productData = getProductDataForTracking(pricing.scenarioId);
                  trackAddToCart(productData);
                  
                  // GA4: Track add_to_cart
                  const ga4Item = createMainProductItem('standard', 1, pricing.salePrice, pricing.originalPrice);
                  trackGA4AddToCart([ga4Item], pricing.salePrice);
                  
                  // Redirect to checkout popup
                  setShowOrderPopup(true);
                }}
                className="text-white font-semibold px-6 py-2.5 rounded-full text-sm transition-all duration-300 whitespace-nowrap animate-cta-shimmer hover:-translate-y-px active:translate-y-0"
                style={{ 
                  backgroundColor: isEventPage && eventColors ? eventColors.primary : '#6a63f5',
                  boxShadow: `0 2px 8px ${isEventPage && eventColors ? eventColors.primary : '#6a63f5'}40`
                }}
                onMouseEnter={(e) => {
                  const color = isEventPage && eventColors ? eventColors.primary : '#6a63f5';
                  e.currentTarget.style.boxShadow = `0 4px 20px ${color}70`;
                  e.currentTarget.style.filter = 'brightness(0.95)';
                }}
                onMouseLeave={(e) => {
                  const color = isEventPage && eventColors ? eventColors.primary : '#6a63f5';
                  e.currentTarget.style.boxShadow = `0 2px 8px ${color}40`;
                  e.currentTarget.style.filter = 'brightness(1)';
                }}
              >
                {isBtcOffer ? 'Get Ryder One' : getPageCTA()}
              </button>
            </div>
          </div>
          
          {/* Mobile Layout */}
          <div className="sm:hidden">
            {/* Removed expanded selection interface as per requirements */}
            <div className="px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                {/* Product info - no expand/collapse since there are no options */}
                <div className="flex items-center gap-2 flex-1">
                  <div className="flex-1">
                    {isBtcOffer ? (
                      <>
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-semibold">${pricing.salePrice}</p>
                          <span className="text-[10px] bg-[#6a63f5]/10 text-[#6a63f5] px-1.5 py-0.5 rounded-full font-medium">+ $200 cashback</span>
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-0.5">$200 BTC cashback included</p>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold">${currentPricing.find(p => p.quantity === selectedQuantity)?.price.toFixed(2)}</p>
                          <p className="text-xs text-muted-foreground line-through">${pricing.originalPrice}</p>
                          <span className="text-[10px] bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400 px-1.5 py-0.5 rounded-full font-medium">-{currentPricing.find(p => p.quantity === selectedQuantity)?.discount}%</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">Ryder One + free welcome kit</p>
                      </>
                    )}
                  </div>
                </div>
                
                {/* CTA button */}
                <button 
                  onClick={() => {
                    // Track AddToCart event (Meta)
                    const productData = getProductDataForTracking(pricing.scenarioId);
                    trackAddToCart(productData);
                    
                    // GA4: Track add_to_cart
                    const ga4Item = createMainProductItem('standard', 1, pricing.salePrice, pricing.originalPrice);
                    trackGA4AddToCart([ga4Item], pricing.salePrice);
                    
                    // Redirect to checkout popup
                    setShowOrderPopup(true);
                  }}
                  className="text-white font-semibold px-5 py-2.5 rounded-full text-sm transition-all duration-300 whitespace-nowrap animate-cta-shimmer hover:-translate-y-px active:translate-y-0"
                  style={{ 
                    backgroundColor: isEventPage && eventColors ? eventColors.primary : '#6a63f5',
                    boxShadow: `0 2px 8px ${isEventPage && eventColors ? eventColors.primary : '#6a63f5'}40`
                  }}
                  onMouseEnter={(e) => {
                    const color = isEventPage && eventColors ? eventColors.primary : '#6a63f5';
                    e.currentTarget.style.boxShadow = `0 4px 20px ${color}70`;
                    e.currentTarget.style.filter = 'brightness(0.95)';
                  }}
                  onMouseLeave={(e) => {
                    const color = isEventPage && eventColors ? eventColors.primary : '#6a63f5';
                    e.currentTarget.style.boxShadow = `0 2px 8px ${color}40`;
                    e.currentTarget.style.filter = 'brightness(1)';
                  }}
                >
                  {isBtcOffer ? 'Get Ryder One' : getPageCTA(true)}
                </button>
              </div>
            </div>
          </div>
        </div>

      {/* Just Purchased Toast Notification */}
      {showToast && currentToast !== null && (
        <div className={`fixed left-4 z-50 animate-toast-in-out transition-all duration-300 ${showStickyBar ? (stickyBarExpanded ? 'hidden sm:block sm:bottom-20' : 'bottom-20') : 'bottom-4'}`}>
          <div className="bg-card border border-border rounded-lg shadow-lg px-3 py-2">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                <Check className="w-3 h-3 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <span className="text-xs font-medium">{purchaseToasts[currentToast].name}</span>
                  <span className="text-[10px]">{purchaseToasts[currentToast].flag}</span>
                  <span className="text-[10px] text-muted-foreground">{purchaseToasts[currentToast].location}</span>
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-[9px] text-muted-foreground">Just</span>
                  <span className="text-[9px] text-muted-foreground">bought a</span>
                  <span className="text-[9px] bg-[#6a63f5]/10 text-[#6a63f5] px-1.5 py-0.5 rounded font-semibold">
                    Ryder One
                  </span>
                </div>
              </div>
              <button 
                onClick={() => setShowToast(false)}
                className="text-muted-foreground hover:text-foreground transition-colors p-0.5"
                aria-label="Close notification"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Popup */}
      <OrderPopup
        isOpen={showOrderPopup}
        onClose={() => setShowOrderPopup(false)}
        // Exit recovery disabled - no longer showing unlock 10% popup on close
        selectedSize={selectedSize}
        selectedQuantity={selectedQuantity}
        stockLevel={stockLevel}
        countdown={countdown}
        isBtcOffer={isBtcOffer}
      />

      {/* Quiz Modal */}
      <QuizModal
        isOpen={isQuizModalOpen}
        onClose={() => setIsQuizModalOpen(false)}
      />

      {/* FAQ Micro-Modal - Appears when FAQ section is in view, hidden for quiz users */}
      {/* Positioned directly below announcement bar - mobile: ~38px, desktop: ~48px */}
      {showFaqMicroModal && quizTags.length === 0 && (
        <div 
          className="fixed left-0 right-0 z-[55] animate-[slideDown_0.3s_ease-out]"
          style={{ 
            top: typeof window !== 'undefined' 
              ? `${document.querySelector('[data-announcement-bar]')?.getBoundingClientRect().height || 44}px`
              : '44px'
          }}
        >
          <div className="bg-background border-b border-border shadow-sm">
            <div className="container px-4 py-3 flex items-center justify-between">
              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
                <span className="text-sm text-muted-foreground">Not sure if you need a hardware wallet?</span>
                <button
                  onClick={() => {
                    setIsQuizModalOpen(true);
                    setShowFaqMicroModal(false);
                    setFaqMicroModalDismissed(true);
                  }}
                  className="text-sm font-medium text-foreground underline underline-offset-4 hover:text-primary transition-colors"
                >
                  Find out in 60 seconds →
                </button>
              </div>
              <button
                onClick={() => {
                  setShowFaqMicroModal(false);
                  setFaqMicroModalDismissed(true);
                }}
                className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Checkout Exit Recovery Modal */}
      {showExitRecoveryModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowExitRecoveryModal(false)}
          />
          {/* Modal */}
          <div className="relative bg-background rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 animate-[fadeSlideIn_0.3s_ease-out]">
            <button
              onClick={() => setShowExitRecoveryModal(false)}
              className="absolute top-4 right-4 p-1 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            
            {!exitRecoverySubmitted ? (
              <>
                <h3 className="text-xl font-semibold mb-2">Not sure yet?</h3>
                <p className="text-muted-foreground mb-4">
                  Get an extra <span className="font-semibold text-foreground">10% off</span> only available right now, in exchange for your email.
                </p>
                <div className="space-y-3">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={exitRecoveryEmail}
                    onChange={(e) => setExitRecoveryEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                  />
                  <button
                    onClick={() => {
                      if (exitRecoveryEmail) {
                        // TODO: Save email to backend
                        setExitRecoverySubmitted(true);
                      }
                    }}
                    disabled={!exitRecoveryEmail}
                    className="w-full text-white font-semibold py-3 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ 
                      backgroundColor: isEventPage && eventColors ? eventColors.primary : '#6a63f5'
                    }}
                  >
                    Unlock 10% Off
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-3 text-center">
                  No spam, just your exclusive discount code.
                </p>
              </>
            ) : (
              <div className="text-center py-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Check your inbox!</h3>
                <p className="text-muted-foreground">
                  Your exclusive 10% discount code is on its way.
                </p>
                <button
                  onClick={() => setShowExitRecoveryModal(false)}
                  className="mt-4 text-sm text-primary hover:underline"
                >
                  Continue shopping
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      </main>
    </div>
  );
}
